import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatStrategyContext } from './StrategyCanvas';
import Header from '../components/Header';
import ConsultantModal from '../components/ConsultantModal';
import FeedbackPanel from '../components/FeedbackPanel';
import { PurposeIcon, FlowIcon, OutcomeIcon, TipsIcon } from '../components/HowToIcons';
import { CANVAS_API, UPLOAD_API } from '../config';
import { track } from '../utils/analytics';
import { getRoleLabel, DEFAULT_ROLE_LABEL } from '../utils/roleLabel';

const API = CANVAS_API;

// ── Helpers for canvas-completion-aware launch ──────────────────
const STRATEGY_KEYS = ['founder_purpose','values','mission','vision','value_proposition','customers','competitors','differentiators','go_to_market','revenue_model','cost_model','current_challenges'];

function readUserScopedCanvas(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const all = JSON.parse(raw);
    if (!all || typeof all !== 'object') return {};
    let user = null;
    try { user = JSON.parse(localStorage.getItem('guruone_user')); } catch { /* ignore */ }
    const userKey = user?.id ? `user_${user.id}` : null;
    if (userKey && all[userKey]) return all[userKey];
    const entries = Object.values(all).filter(v => v && typeof v === 'object');
    return entries.reduce((best, curr) => Object.keys(curr).length > Object.keys(best).length ? curr : best, {});
  } catch { return {}; }
}

// Mirrors the "any non-empty field" definition used elsewhere.
function isFieldValueFilled(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return !!v;
}

function calcStrategyPctRaw(strategyData) {
  if (!strategyData || typeof strategyData !== 'object') return 0;
  const filled = STRATEGY_KEYS.filter(k => {
    const s = strategyData[k];
    if (!s) return false;
    if (s.entries) return s.entries.some(e => Object.values(e).some(isFieldValueFilled));
    return Object.values(s).some(isFieldValueFilled);
  }).length;
  return Math.round((filled / STRATEGY_KEYS.length) * 100);
}

function calcBlueprintPctRaw(omData) {
  if (!omData || typeof omData !== 'object') return 0;
  const sections = Object.values(omData).filter(v => typeof v === 'object' && v !== null);
  if (sections.length === 0) return 0;
  const filled = sections.filter(s => s.current && Object.values(s.current).some(isFieldValueFilled)).length;
  return Math.round((filled / 10) * 100);
}

// ── Lens display map — labels, icons, lens-intro blurbs ────────────
// Used wherever lens text is rendered. Legacy keys (team / market / profit)
// alias to their canonical equivalents so persisted localStorage state
// continues to render correctly.
const LENS_DISPLAY = {
  team_capacity:       { label: 'Team & Capacity Planning',       icon: '👥', blurb: "Your challenge points to a people and capacity question. These questions help me diagnose where the real constraint is — before recommending who to hire, what to change, or what to stop." },
  market_focus:        { label: 'Market Choice & Focus',          icon: '🎯', blurb: "Your challenge points to a market and positioning question. These questions help me understand where you are already winning, where you're spreading too thin, and where the real growth opportunity lies." },
  profitability:       { label: 'Profitability & Cash Health',    icon: '💰', blurb: "Your challenge points to a profitability or cash question. These questions help me understand your cost structure, where margin is being lost, and what the most important financial levers are." },
  positioning:         { label: 'Positioning & Differentiation',  icon: '⚔️', blurb: "Your challenge points to a positioning question — how you compete, how clearly you can articulate it, and how defensible that position is." },
  growth_path:         { label: 'Growth Path & Strategic Options', icon: '🚀', blurb: "Your challenge is about how to grow — and there are usually more paths than they first appear. These questions help me understand what's worked, what's available, and what you have the appetite and capability to pursue." },
  process_scalability: { label: 'Process & Scalability',          icon: '⚙️', blurb: "Your challenge points to an operational question — consistency, systemisation, or scalability. These questions help me understand where the friction is and what's preventing the business from running reliably without heroics." },
  make_vs_buy:         { label: 'Supplier & Make-vs-Buy',         icon: '🤝', blurb: "Your challenge involves a sourcing or capability decision — whether to build, buy, or partner. These questions help me understand what you're really optimising for, what the real cost of each option is, and where the risks sit." },
  capital_allocation:  { label: 'Investment & Capital Allocation', icon: '📈', blurb: "Your challenge involves choosing where to put financial capital when more than one thing is competing for it. These questions help me understand the options, what you're trying to achieve, and what's really at stake if you get this wrong." },
  founder_leverage:    { label: 'Founder Leverage',               icon: '⚖️', blurb: "Your challenge centres on your role in the business — and whether it's limiting what the business can become. These questions help me understand where you're stretched, what's getting in the way of letting go, and what needs to change." },
  team_effectiveness:  { label: 'Team Effectiveness',             icon: '🌱', blurb: "Your challenge points to how well the team is working together — alignment, accountability, and motivation. These questions help me understand where performance is breaking down and what the root cause is most likely to be." },
  people_decisions:    { label: 'People & Performance Decisions', icon: '💬', blurb: "Your challenge involves a specific person or relationship in your business. These questions help me understand the nature of the situation, what's already been tried, and what outcome you're really looking for." },
  multi:               { label: 'Multiple areas',                 icon: '❓', blurb: "Your challenge cuts across more than one area of the business. These questions help me build enough of a picture to give you a grounded recommendation rather than a generic one." },
  // Legacy aliases
  team:   { label: 'Team & Capacity Planning',    icon: '👥', blurb: "Your challenge points to a people and capacity question. These questions help me diagnose where the real constraint is — before recommending who to hire, what to change, or what to stop." },
  market: { label: 'Market Choice & Focus',       icon: '🎯', blurb: "Your challenge points to a market and positioning question. These questions help me understand where you are already winning, where you're spreading too thin, and where the real growth opportunity lies." },
  profit: { label: 'Profitability & Cash Health', icon: '💰', blurb: "Your challenge points to a profitability or cash question. These questions help me understand your cost structure, where margin is being lost, and what the most important financial levers are." },
};

function lensDisplay(lens) {
  return LENS_DISPLAY[lens] || LENS_DISPLAY.multi;
}

// ── Per-lens recommendation framing ────────────────────────────────
// Sent to the backend as a context block (appended to strategyContext)
// so the recommendation prompt is framed with the right consulting
// approach for the diagnosed lens. Mirrors Section 4 of the spec.
const LENS_FRAMING = {
  team_capacity: {
    core_question: 'Do we have the right people and enough of them?',
    consulting_approach: 'McKinsey org design · Theory of Constraints · capability planning',
    framing: "Diagnose where the real constraint is — skills gap, headcount gap, or structure gap — before recommending who to hire, what to change, or what to stop. Surface bottlenecks before headcount.",
    key_outputs: 'Bottleneck diagnosis · structural recommendation (hire / restructure / outsource / accept) · 30-day plan that frees the business leader',
  },
  market_focus: {
    core_question: 'Where should we compete and grow?',
    consulting_approach: 'Porter · Ansoff · Bain segmentation · Blue Ocean',
    framing: "Understand where the business is already winning, where it is spreading too thin, and where the real growth opportunity lies. Surface revenue concentration risk and segment fit. Reflect the leader's VP and differentiators back at them.",
    key_outputs: 'Segment prioritisation · what to double down on · what to deprioritise · what to test',
  },
  profitability: {
    core_question: 'Where is the money really made and lost?',
    consulting_approach: 'Contribution margin analysis · unit economics · Bain profitability segmentation · working capital analysis',
    framing: 'Diagnose the cost structure, where margin is being lost, and the most important financial levers. Always work through cost vs pricing vs cash position. Show the maths where you can.',
    key_outputs: 'Margin diagnosis by line · cost lever priorities · pricing / cash collection recommendations · realistic timeline to improvement',
  },
  positioning: {
    core_question: 'Why do we win and how defensible is it?',
    consulting_approach: 'Porter generic strategies · value disciplines · HBR differentiation research · pricing power theory',
    framing: "Test the differentiation: real or perceived, defensible or replicable, consistently messaged or fragmented. Where positioning is weak, name it. Where pricing power is being left on the table, surface it. The recommendation should sharpen the leader's competitive story, not soften it.",
    key_outputs: 'Differentiation reality check · pricing power assessment · positioning sharpening recommendation · messaging consistency actions',
  },
  growth_path: {
    core_question: 'How should we grow from here?',
    consulting_approach: 'Ansoff matrix · Playing to Win (strategy cascade) · McKinsey growth research',
    framing: 'There are usually more growth paths than appear at first glance. Surface each one (existing market deepening, new segment, new product, geographic, M&A / partnership). Apply Ansoff. Anchor the recommendation against capacity, funding and risk appetite — not just ambition.',
    key_outputs: 'Growth path options across Ansoff quadrants · which to pursue first · what conditions need to be true · sequencing and risk profile',
  },
  process_scalability: {
    core_question: 'What is limiting our ability to scale reliably?',
    consulting_approach: 'Theory of Constraints · operational maturity models · EOS',
    framing: 'Identify the binding constraint — process, documentation, automation, or skill dependency. Recommend the change that unlocks the next stage of scale without overbuilding. Heroics is a smell, not a strategy.',
    key_outputs: 'Bottleneck diagnosis · documentation / automation / system priorities · sequenced 90-day plan',
  },
  make_vs_buy: {
    core_question: 'Should we build internally or outsource?',
    consulting_approach: 'Transaction cost economics · make-vs-buy frameworks · Bain sourcing strategy',
    framing: 'Test what is actually being optimised for (cost / quality / control / capability / speed). Score build vs buy vs partner against strategic importance and dependency risk. Cost is rarely the only driver — surface the others explicitly.',
    key_outputs: 'Build vs buy vs partner scoring · dependency risk assessment · transition plan if changing current arrangement',
  },
  capital_allocation: {
    core_question: 'Where should we invest our capital?',
    consulting_approach: 'Bain capital allocation research · BCG portfolio thinking · ROI comparison frameworks',
    framing: "Compare the investment options on expected return, payback period, risk, and implementation complexity. Cross-reference each option against the leader's stated VALUES and MISSION — flag tension explicitly where an option contradicts what the business is meant to stand for. Investment goals that misalign with mission are a red flag.",
    key_outputs: 'Option-by-option ROI framing · risk-adjusted comparison · mission/values alignment check · funding-source recommendation',
  },
  founder_leverage: {
    core_question: 'Is the leader the bottleneck?',
    consulting_approach: 'Executive coaching models · entrepreneurial leadership theory · McKinsey org health',
    framing: "The challenge centres on the leader's role. Diagnose where they are stretched and what is preventing delegation — trust, systems, or capability of the team. Recommend changes that free the leader without destabilising delivery. Time freed is the bonus criterion.",
    key_outputs: "Diagnosis of where the business leader's time is going · delegation path · what the team needs to absorb · 90-day handover priorities",
  },
  team_effectiveness: {
    core_question: 'Is the team aligned and accountable?',
    consulting_approach: 'McKinsey Org Health Index · motivation theory · RACI models · decision rights design',
    framing: 'Identify whether the breakdown is accountability, alignment, motivation, or decision rights. Use RACI and decision-rights thinking. Where avoided conversations exist, name that as the root — performance problems are usually conversation problems first.',
    key_outputs: 'Root-cause diagnosis (alignment / accountability / motivation / decisions) · specific structural changes · what to address with whom and when',
  },
  people_decisions: {
    core_question: 'How do I handle a situation involving a specific person?',
    consulting_approach: 'Performance management frameworks · crucial conversations methodology · co-founder dynamics',
    framing: 'Branch the recommendation on the core concern (see branching block below). Always include UK employment law caveats prominently. Treat every recommendation as a starting point — not a substitute for HR or employment law guidance.',
    key_outputs: 'Branch-specific action plan · script for the conversation · timeline and documentation steps · explicit signposting for HR / legal support',
  },
  multi: {
    core_question: 'The challenge spans more than one lens. Build a recommendation that addresses the primary driver while flagging the connected ones.',
    consulting_approach: 'Apply the relevant frameworks for each implicated lens. Lead with the root-cause lens; treat the others as constraints, not separate decisions.',
    framing: 'Resist the temptation to give multiple disconnected answers. Identify the lens that, if resolved, most relieves the others. Anchor the recommendation there.',
    key_outputs: 'Root-cause lens identification · primary recommendation · secondary impacts on connected lenses',
  },
};

// Section 4 / Lens 11: branch the recommendation structure on which core
// concern the leader picked. Each branch tells the LLM what kind of plan to
// build. Sent into the recommendation prompt only when lens is people_decisions.
const PEOPLE_DECISIONS_BRANCHES = {
  'Underperformance — not meeting the standard needed': {
    label: 'Underperformance',
    structure: "Performance improvement plan. Specific standards to be met, support to provide, documentation cadence, review timeline (typically 4–12 weeks), and clearly stated consequences if standards are not met. Where dismissal becomes a possibility, signpost UK employment law obligations and HR support.",
  },
  'Behaviour or attitude — how they work with others': {
    label: 'Behaviour / attitude',
    structure: 'Direct conversation structure (what is happening, the impact, what good looks like, what changes by when), follow-up cadence, and consequences if behaviour does not change. Connect to stated cultural norms from the canvas where they exist.',
  },
  "Role misalignment — good person, wrong fit for where we're going": {
    label: 'Role misalignment',
    structure: "Restructure options (redesign the role, move the person, agree exit terms). Honest conversation framing — separate the person's value from the fit problem. Where redundancy is on the table, flag UK redundancy process obligations explicitly.",
  },
  'Risk of losing them — worried they might leave': {
    label: 'Retention risk',
    structure: 'Diagnose what is actually driving the risk (compensation, role design, leadership relationship, market pull). Recommend what would change it. Surface cost-of-losing-them vs cost-of-retaining. Where counter-offers are involved, give the realistic playbook (and its risks).',
  },
  'I need to end the relationship': {
    label: 'End relationship',
    structure: 'Process steps in order: documentation review, HR / legal consultation FIRST, conversation sequencing, exit logistics, communication to team and customers. UK employment law obligations are mandatory context. Strongly recommend HR or employment law support before any action.',
  },
};

// ── Universal pre-lens setup questions ─────────────────────────────
// Shown before every lens batch. Both optional with explicit Skip.
// Answers flow into the standard `answers` state and are picked up by
// the recommendation prompt automatically.
const PRE_LENS_QUESTIONS = [
  {
    field: 'what_tried',
    question: "What have you already tried to address this — and what happened?",
    type: 'text',
    rows: 2,
    helper: "Even partial attempts or things that helped short-term are worth capturing — they tell me whether this is a tactical gap or something more structural.",
    optional: true,
  },
  {
    field: 'temporary_vs_systemic',
    question: "Is this a temporary pressure or something more systemic?",
    type: 'choice',
    options: [
      "Temporary — a specific situation is causing this right now",
      "Tactical — there's a clear fix I haven't implemented yet",
      "Systemic — it keeps coming back regardless of what I try",
      "I'm not sure — that's part of what I need to work out",
    ],
    optional: true,
  },
];

// ── Canvas gap dependency map per lens ─────────────────────────────
// Per lens: which canvas sections are most relevant. The gap warning at
// the top of the questions step checks these sections; presence rules:
//   - all listed sections empty  → "Significant" severity
//   - 1+ but not all empty       → "Advisory" severity
//   - none empty                 → no warning
// people_decisions intentionally returns null — culture / people context is
// passed to the recommendation prompt only, never gated on a gap warning.
const LENS_CANVAS_DEPENDENCIES = {
  team_capacity: { fields: [
    { canvas: 'operating_model', section: 'people_roles', label: 'Team structure and roles' },
  ]},
  market_focus: { fields: [
    { canvas: 'strategy', section: 'value_proposition', label: 'Value proposition' },
    { canvas: 'strategy', section: 'differentiators',   label: 'Differentiators' },
    { canvas: 'strategy', section: 'customers',         label: 'Customers' },
    { canvas: 'strategy', section: 'competitors',       label: 'Competitors' },
  ]},
  profitability: { fields: [
    { canvas: 'strategy',        section: 'revenue_model',      label: 'Revenue model' },
    { canvas: 'strategy',        section: 'cost_model',         label: 'Cost model' },
    { canvas: 'operating_model', section: 'finance_commercial', label: 'Finance & commercial' },
  ]},
  positioning: { fields: [
    { canvas: 'strategy',        section: 'value_proposition', label: 'Value proposition' },
    { canvas: 'strategy',        section: 'differentiators',   label: 'Differentiators' },
    { canvas: 'strategy',        section: 'competitors',       label: 'Competitors' },
    { canvas: 'operating_model', section: 'portfolio',         label: 'Product & service portfolio' },
  ]},
  growth_path: { fields: [
    { canvas: 'strategy',        section: 'vision',            label: 'Vision' },
    { canvas: 'strategy',        section: 'value_proposition', label: 'Value proposition' },
    { canvas: 'strategy',        section: 'differentiators',   label: 'Differentiators' },
    { canvas: 'strategy',        section: 'go_to_market',      label: 'Go-to-market' },
    { canvas: 'operating_model', section: 'portfolio',         label: 'Portfolio direction' },
  ]},
  process_scalability: { fields: [
    { canvas: 'operating_model', section: 'process_ways_of_work', label: 'Process and ways of work' },
  ]},
  make_vs_buy: { fields: [
    { canvas: 'operating_model', section: 'suppliers_partners', label: 'Suppliers and partners' },
  ]},
  capital_allocation: { fields: [
    { canvas: 'strategy',        section: 'revenue_model',      label: 'Revenue model' },
    { canvas: 'strategy',        section: 'cost_model',         label: 'Cost model' },
    { canvas: 'operating_model', section: 'finance_commercial', label: 'Finance & commercial' },
  ]},
  founder_leverage: { fields: [
    { canvas: 'operating_model', section: 'people_roles',       label: 'Team structure and roles' },
    { canvas: 'operating_model', section: 'culture_leadership', label: 'Culture and leadership' },
  ]},
  team_effectiveness: { fields: [
    { canvas: 'operating_model', section: 'people_roles',       label: 'Team structure and roles' },
    { canvas: 'operating_model', section: 'culture_leadership', label: 'Culture and leadership' },
  ]},
  people_decisions: null,
  multi: null,
  // Legacy aliases
  team:   { fields: [{ canvas: 'operating_model', section: 'people_roles', label: 'Team structure and roles' }] },
  market: { fields: [
    { canvas: 'strategy', section: 'value_proposition', label: 'Value proposition' },
    { canvas: 'strategy', section: 'differentiators',   label: 'Differentiators' },
    { canvas: 'strategy', section: 'customers',         label: 'Customers' },
  ]},
  profit: { fields: [
    { canvas: 'strategy',        section: 'revenue_model',      label: 'Revenue model' },
    { canvas: 'strategy',        section: 'cost_model',         label: 'Cost model' },
    { canvas: 'operating_model', section: 'finance_commercial', label: 'Finance & commercial' },
  ]},
};

// ── Decision Guru answers → Canvas back-sync ───────────────────────
// Section 12. After the recommendation renders, write selected answers
// back into the leader's canvases. Strictly ADDITIVE — only fills fields
// that are currently empty; never overwrites the leader's own input.
// Storage shapes differ between the two canvases:
//   - guruone_strategy:        { user_X: { ...sections, lastUpdated } }
//   - guruone_operating_model: { ...sections } (no user wrapper)
const SYNC_MAP = [
  // ── Direction (strategy) canvas ─────────────────────────────────
  {
    answer: 'time_horizon',
    storageKey: 'guruone_strategy',
    path: 'founder_purpose.target_type',
    type: 'choice_map',
    label: 'Founder Purpose · target type',
    map: {
      'Building long term — no exit in mind': 'Revenue',
      'Exit in 1–3 years': 'Exit',
      'Exit in 3–5 years': 'Exit',
    },
  },
  {
    answer: 'investment_goal',
    storageKey: 'guruone_strategy',
    path: 'founder_purpose.target_type',
    type: 'choice_map',
    label: 'Founder Purpose · target type',
    map: {
      'Drive new revenue': 'Revenue',
      'Make the business more attractive to a buyer': 'Exit',
    },
  },
  // ── Design (operating_model) canvas — finance_commercial ────────
  {
    answer: 'gross_margin',
    storageKey: 'guruone_operating_model',
    path: 'finance_commercial.current.gross_margin',
    type: 'number_from_input',
    label: 'Finance · gross margin %',
  },
  {
    answer: 'pricing_confidence',
    storageKey: 'guruone_operating_model',
    path: 'finance_commercial.current.pricing_approach',
    type: 'choice_map',
    label: 'Finance · pricing approach',
    map: {
      'Confident — priced well and could increase': 'Based on the value we deliver',
      'Confident — priced right but no room to increase': 'Based on market rates',
      'Concerned — definitely underpriced': 'Based on our costs',
    },
  },
  {
    answer: 'cash_runway',
    storageKey: 'guruone_operating_model',
    path: 'finance_commercial.current.cash_position',
    type: 'choice_map',
    label: 'Finance · cash position',
    map: {
      'Less than 3 months': 'Often tight — waiting on invoices',
      '3–6 months': 'Manageable',
      '6–12 months': 'Manageable',
      'More than 12 months': 'Generally comfortable',
    },
  },
  // ── Design canvas — process_ways_of_work ────────────────────────
  {
    answer: 'documentation_status',
    storageKey: 'guruone_operating_model',
    path: 'process_ways_of_work.current.documentation_status',
    type: 'choice_map',
    label: 'Process · documentation status',
    map: {
      'Fully documented — playbooks a new person could follow': 'Fully documented',
      'Mostly documented but patchy': 'Partially documented',
      'Some documentation — the important things only': 'Partially documented',
      "Almost entirely in people's heads": 'Mostly undocumented',
    },
  },
  {
    answer: 'systems_integration',
    storageKey: 'guruone_operating_model',
    path: 'process_ways_of_work.current.systems_integration',
    type: 'choice_map',
    label: 'Process · systems integration',
    map: {
      'Well integrated — data flows without manual intervention': 'Well integrated',
      'Partial — some manual handoffs between systems': 'Partial integration — some manual handoffs',
      "Largely manual — systems don't connect and we bridge the gaps": "Largely manual — systems don't talk to each other",
    },
  },
  {
    answer: 'automation_maturity',
    storageKey: 'guruone_operating_model',
    path: 'process_ways_of_work.current.ai_automation_in_use',
    type: 'text_from_choice',
    label: 'Process · AI / automation in use',
    map: {
      'Highly manual — almost everything is done by hand or in spreadsheets':
        'Highly manual today — limited automation in place.',
      'Partially automated — some tools in place but significant manual work remains':
        'Partially automated — some tools in place, significant manual work remains.',
      'Mostly automated — systems handle the routine; people handle exceptions':
        'Mostly automated — systems handle routine work, people handle exceptions.',
      "Deliberately automated — we've invested in this and it shows":
        'Deliberately automated — significant ongoing investment in automation.',
    },
  },
  // ── Design canvas — culture_leadership ──────────────────────────
  {
    answer: 'bottleneck_severity',
    storageKey: 'guruone_operating_model',
    path: 'culture_leadership.current.team_empowerment',
    type: 'choice_map',
    label: 'Culture · team empowerment',
    map: {
      "Yes — things wait for me; I'm the primary bottleneck": 'Low — most things go through business leader',
      "Partly — I'm one constraint among several": 'Moderate — some autonomy',
      'No — the team operates well without me day to day': 'High — people act autonomously',
    },
  },
  // ── Design canvas — suppliers_partners ──────────────────────────
  {
    answer: 'dependency_exposure',
    storageKey: 'guruone_operating_model',
    path: 'suppliers_partners.current.dependency_risk',
    type: 'choice_map',
    label: 'Suppliers · dependency risk',
    map: {
      'Low — we could replace them without major disruption': 'Low',
      'Medium — it would be painful but manageable': 'Medium',
      'High — losing them would seriously affect delivery': 'High',
      'Critical — they are the only viable option we know of': 'High',
    },
  },
  {
    answer: 'capability_in_question',
    storageKey: 'guruone_operating_model',
    path: 'suppliers_partners.current.make_vs_buy_flagged',
    type: 'text_direct',
    label: 'Suppliers · make vs buy flagged',
  },
];

function extractChoiceValue(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.choice || null;
  return null;
}

// Single-field writer. Returns true if a value was written; false if the
// field was already populated (additive rule) or the write failed.
function syncToCanvasField(storageKey, path, value) {
  try {
    const raw = localStorage.getItem(storageKey);
    let all = {};
    try { all = raw ? JSON.parse(raw) : {}; } catch { all = {}; }
    if (!all || typeof all !== 'object') all = {};

    // Strategy canvas wraps sections in `user_X`; OM canvas stores sections directly.
    const isUserScoped = storageKey === 'guruone_strategy';
    let container;
    if (isUserScoped) {
      let user = null;
      try { user = JSON.parse(localStorage.getItem('guruone_user')); } catch { /* ignore */ }
      const userKey = user?.id ? `user_${user.id}` : 'default';
      if (!all[userKey] || typeof all[userKey] !== 'object') all[userKey] = {};
      container = all[userKey];
    } else {
      container = all;
    }

    const parts = path.split('.');
    let cursor = container;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      if (!cursor[k] || typeof cursor[k] !== 'object') cursor[k] = {};
      cursor = cursor[k];
    }
    const leaf = parts[parts.length - 1];

    // ADDITIVE rule — only write when the existing value is genuinely empty.
    const current = cursor[leaf];
    const isEmpty =
      current === null ||
      current === undefined ||
      current === '' ||
      (Array.isArray(current) && current.length === 0);
    if (!isEmpty) return false;

    cursor[leaf] = value;
    if (isUserScoped) container.lastUpdated = new Date().toISOString();
    localStorage.setItem(storageKey, JSON.stringify(all));
    return true;
  } catch (e) {
    console.warn('syncToCanvasField failed for', storageKey, path, e);
    return false;
  }
}

// Walks SYNC_MAP and writes resolvable answers back to the canvases.
// Returns a summary array of what got synced for the toast.
function syncAnswersToCanvases(ans) {
  if (!ans || typeof ans !== 'object') return [];
  const synced = [];
  for (const m of SYNC_MAP) {
    const rawAns = ans[m.answer];
    if (rawAns == null) continue;

    let value = null;
    if (m.type === 'choice_map') {
      const v = extractChoiceValue(rawAns);
      if (v && m.map[v] != null) value = m.map[v];
    } else if (m.type === 'number_from_input') {
      // choice_with_input answer shape: { choice, input_value }
      const obj = (rawAns && typeof rawAns === 'object') ? rawAns : null;
      const n = obj ? parseFloat(obj.input_value) : NaN;
      if (!isNaN(n) && n >= 0 && n <= 100) value = Math.round(n);
    } else if (m.type === 'text_from_choice') {
      const v = extractChoiceValue(rawAns);
      if (v && m.map[v] != null) value = m.map[v];
    } else if (m.type === 'text_direct') {
      const v = typeof rawAns === 'string'
        ? rawAns.trim()
        : (rawAns && typeof rawAns === 'object') ? ((rawAns.choice || rawAns.input_value || '') + '').trim() : '';
      if (v) value = v;
    }
    if (value == null || value === '') continue;

    if (syncToCanvasField(m.storageKey, m.path, value)) {
      synced.push({
        canvas: m.storageKey === 'guruone_strategy' ? 'Direction' : 'Design',
        path: m.path,
        label: m.label,
      });
    }
  }
  return synced;
}

// Section content check — works for both Direction and Design canvas shapes.
//   Direction: section is an object of primitive fields, optionally with .entries[]
//   Design:    section has .current { ...fields }
function canvasSectionHasContent(canvas, sectionKey) {
  const section = canvas?.[sectionKey];
  if (!section || typeof section !== 'object') return false;
  if (section.entries && Array.isArray(section.entries)) {
    if (section.entries.some(e => Object.values(e).some(isFieldValueFilled))) return true;
  }
  if (section.current && typeof section.current === 'object') {
    if (Object.values(section.current).some(isFieldValueFilled)) return true;
  }
  // Direction-style: object of primitive fields, no .entries / .current wrapper
  if (Object.values(section).some(v => {
    if (v && typeof v === 'object') return false;
    return isFieldValueFilled(v);
  })) return true;
  return false;
}

// Map a populated Direction Canvas to the flat shape the existing
// CDDD flow expects. Best-effort — fields we cannot infer are left
// as the same neutral defaults the "No website" path already uses.
function buildCanvasFromCanvases(strategyData, omData) {
  const sd = strategyData || {};
  const od = omData || {};
  const vp = sd.value_proposition || {};
  const rm = sd.revenue_model || {};
  const gtm = sd.go_to_market || {};
  const cm = sd.cost_model || {};
  const fp = sd.founder_purpose || {};
  const comp = sd.competitors || {};
  const diffEntries = sd.differentiators?.entries || [];
  const top3 = diffEntries.map(d => d.differentiator).filter(Boolean).slice(0, 3).join('; ');

  const peopleCur  = od.people_roles?.current || {};
  const peopleFut  = od.people_roles?.future || {};
  const culture    = od.culture_leadership?.current || {};
  const finance    = od.finance_commercial?.current || {};
  const portfolio  = od.portfolio?.current || {};
  const bd         = od.business_development?.current || {};
  const suppliers  = od.suppliers_partners?.current || {};
  const process    = od.process_ways_of_work?.current || {};
  const governance = od.governance_controls?.current || {};

  return {
    // ── Existing flat fields (preserve callers) ────────────────
    sector: '',
    primary_problem_solved: vp.primary_problem_solved || '',
    target_customer_type: vp.target_customer_type || '',
    price_positioning: (vp.price_positioning || 'mid-market').toLowerCase().replace(' ', '-'),
    top_3_differentiators: top3,
    primary_acquisition_channel: gtm.primary_motion || 'direct',
    revenue_model: (rm.primary_revenue_type || 'project-based').toLowerCase().replace(' ', '-'),
    sales_approach: gtm.sales_approach || 'founder-led',
    repeatability_of_delivery: 'medium',
    cash_runway: null,
    profit_breakdown: null,
    pricing_confidence: null,
    cash_collection: null,

    // ── Section 11 expansion — Direction canvas ────────────────
    market_positioning:    vp.market_positioning,
    target_type:           fp.target_type,
    target_detail:         fp.target_detail,
    success_definition:    fp.success_definition,
    vision:                fp.vision,
    customer_segments:     sd.customers?.entries,
    differentiators:       diffEntries,
    competitor_types:      comp.alternative_types,
    competitor_drivers:    comp.customer_drivers,
    retention_mechanism:   gtm.retention_mechanism,
    revenue_blend:         rm.revenue_blend,
    monthly_fixed_costs:   cm.monthly_fixed_costs,
    fixed_variable_ratio:  cm.fixed_variable_ratio,
    cost_model_fixed:      cm.fixed_costs,

    // ── Section 11 expansion — OM canvas ───────────────────────
    role_definition_status:     peopleCur.role_definition_status,
    decision_rights:            peopleCur.decision_rights,
    key_gaps:                   peopleCur.key_gaps,
    team_challenges:            peopleCur.team_challenges,
    responsibilities_documented: peopleCur.responsibilities_documented,
    leader_time_split:          peopleCur.leader_time_split,
    hiring_priorities:          peopleFut.hiring_priorities,
    founder_role_shift:         peopleFut.founder_role,
    decision_making_style:      culture.decision_making,
    team_empowerment:           culture.team_empowerment,
    what_celebrated:            culture.what_celebrated,
    what_not_tolerated:         culture.what_not_tolerated,
    leadership_development:     culture.leadership_development,
    culture_challenges:         culture.culture_challenges,
    om_gross_margin:            finance.gross_margin,
    om_pricing_approach:        finance.pricing_approach,
    cash_position_current:      finance.cash_position,
    revenue_concentration:      finance.revenue_concentration,
    portfolio_description:      portfolio.portfolio_description,
    portfolio_focus:            portfolio.portfolio_focus,
    portfolio_strategic_dir:    portfolio.strategic_direction,
    portfolio_challenges:       portfolio.portfolio_challenges,
    bd_founder_dependency:      bd.founder_dependency_bd,
    bd_pipeline_definition:     bd.pipeline_definition,
    bd_referral_activation:     bd.referral_activation,
    bd_existing_expansion:      bd.existing_client_expansion,
    bd_strategic_partnerships:  suppliers.strategic_partnerships,
    make_vs_buy_flagged:        suppliers.make_vs_buy_flagged,
    supplier_dependency_risk:   suppliers.dependency_risk,
    key_partners:               suppliers.key_partners,
    outsource_pct:              suppliers.outsource_pct,
    doc_status:                 process.documentation_status,
    key_bottlenecks:            process.key_bottlenecks,
    process_challenges:         process.process_challenges,
    systems_integration:        process.systems_integration,
    delivery_process_description: process.delivery_process_description,
    skill_judgment_dependency:  process.skill_judgment_dependency,
    ai_automation_in_use:       process.ai_automation_in_use,
    governance_decision_making: governance.decision_making,
    action_capture:             governance.action_capture,
  };
}

// ── How to use Decision Guru — slide-in panel ────────────────────
// Shared "How to use" section block — icon + bigger title + body
function HowToSection({ Icon, colour, title, children, isLast }) {
  return (
    <div style={{ paddingBottom: isLast ? 0 : '1rem', marginBottom: isLast ? 0 : '1rem', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.45rem' }}>
        <Icon colour={colour} />
        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
      </div>
      <div style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function HowToUseDecisionPanel({ onClose }) {
  const accent = '#48B650';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 380, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Help</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>How to use Decision Guru</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
          <HowToSection Icon={PurposeIcon} colour={accent} title="What Decision Guru is for">
            <p style={{ margin: 0 }}>
              Decision Guru is your Non-Executive Director — an independent thinking partner for any significant decision you are facing. It does not tell you what to do. It structures your thinking, surfaces what you might be missing, and helps you build a recommendation you can defend in front of an advisor, investor or your own team.
            </p>
          </HowToSection>

          <HowToSection Icon={FlowIcon} colour={accent} title="How it works">
            <p style={{ margin: 0 }}>
              You describe the challenge in your own words. Decision Guru diagnoses the type of decision (people, market, profit), gathers the context it needs from your canvases or short follow-up questions, builds a set of options, scores each one against the criteria that matter most to you, and delivers a structured recommendation with a 30-day action plan and a roadmap beyond that.
            </p>
          </HowToSection>

          <HowToSection Icon={OutcomeIcon} colour={accent} title="What you will get out of it">
            <p style={{ margin: 0 }}>
              A structured recommendation with up to five options, scored against criteria you choose, with a confidence rating, a roadmap and immediate next actions. The more canvas context you have provided in Direction Guru and Design Guru, the more specific and grounded the output — generic advice is what you avoid by giving GuruOne your real picture.
            </p>
          </HowToSection>

          <HowToSection Icon={TipsIcon} colour={accent} title="Tips for getting the most from it" isLast>
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              <li style={{ marginBottom: '0.4rem' }}>Describe the decision in your own words — do not try to frame it perfectly first.</li>
              <li style={{ marginBottom: '0.4rem' }}>Add your own options if you already have ideas — Decision Guru blends them with its own.</li>
              <li style={{ marginBottom: '0.4rem' }}>Use the criteria weighting to reflect what actually matters for this specific decision, not what should matter in general.</li>
              <li style={{ marginBottom: '0.4rem' }}>Come back after you have decided to reflect on how it played out — context across decisions compounds.</li>
            </ul>
          </HowToSection>
        </div>

        <div style={{ padding: '0.9rem 1.4rem', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ width: '100%', padding: '0.65rem', borderRadius: 8, background: 'white', border: '1.5px solid #e0e0e0', color: '#555', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload context panel — "Add more context" ────────────────────
// Lets the user attach business plans / decks / financials / marketing
// materials. Their extracted content is appended as additional context
// to the LLM input on handleChallenge / handleAnswers via the
// onTextExtracted callback.
function UploadContextPanel({ collapsedByDefault, onTextExtracted, onSectionsExtracted }) {
  const [files, setFiles] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [collapsed, setCollapsed] = useState(!!collapsedByDefault);
  const [analysing, setAnalysing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const fileInputRef = useRef(null);
  const ACCEPTED = '.pdf,.docx,.pptx,.txt,.md';

  const hasInput = files.length > 0 || websiteUrl.trim().length > 0;

  const handleFilesPicked = (e) => {
    const incoming = Array.from(e.target.files);
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...incoming.filter(f => !existingNames.has(f.name))];
    });
    e.target.value = '';
  };

  const removeFile = (name) => setFiles(prev => prev.filter(f => f.name !== name));

  const handleAnalyse = async () => {
    if (!hasInput || analysing) return;
    setAnalysing(true);
    setStatusMessage('');
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const trimmedUrl = websiteUrl.trim();
      if (trimmedUrl) formData.append('url', trimmedUrl);
      const res = await axios.post(`${UPLOAD_API}/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const sections = res.data?.sections || {};
      const sourceLabel = (() => {
        const parts = [];
        if (files.length) parts.push(`${files.length} document${files.length === 1 ? '' : 's'}`);
        if (trimmedUrl) parts.push('your website');
        return parts.join(' and ');
      })();
      if (Object.keys(sections).length === 0) {
        // Honest feedback — the request succeeded but nothing usable came
        // back (a scanned PDF we couldn't read, a thin document, etc.).
        setStatusMessage("We couldn't pull anything usable from " + sourceLabel + ". This can happen with a scanned/image-only PDF we couldn't read, a password-protected file, or a file with very little text. Try a clearer or text-based file, paste a website URL, or email Glenn at glenn@guruone.ai.");
      } else {
        // Flatten the structured sections into a readable text block — the
        // LLM still benefits from the full extracted blob being appended to
        // the prompt, even though we're also merging into canvas fields.
        const lines = Object.entries(sections).map(([k, v]) => {
          const stringValue = typeof v === 'string' ? v : JSON.stringify(v);
          return `- ${k.replace(/_/g, ' ')}: ${stringValue}`;
        });
        const extracted = lines.length > 0 ? lines.join('\n') : '';
        onTextExtracted(extracted);
        // Hand the structured sections back to the parent so it can merge
        // them into the visible canvas snapshot and close this panel.
        if (typeof onSectionsExtracted === 'function') {
          onSectionsExtracted(sections, { sourceLabel });
        }
        setStatusMessage(`${sourceLabel} added to context.`);
      }
    } catch (e) {
      setStatusMessage('Could not analyse — please try again.');
    }
    setAnalysing(false);
  };

  const showHeaderToggle = !!collapsedByDefault;
  const bodyVisible = !showHeaderToggle || !collapsed;

  return (
    <div style={{ marginBottom: '1.25rem', border: '1.5px solid #e0e0e0', borderRadius: 10, background: '#f9fffe', overflow: 'hidden' }}>
      {showHeaderToggle ? (
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.6rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e' }}>+ Add more context</span>
          <span style={{ fontSize: '0.7rem', color: '#888' }}>{collapsed ? '▼' : '▲'}</span>
        </button>
      ) : (
        <div style={{ padding: '0.6rem 0.9rem 0', fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e' }}>
          Add more context before we start
        </div>
      )}
      {bodyVisible && (
        <div style={{ padding: '0.6rem 0.9rem 0.85rem', borderTop: showHeaderToggle ? '1px solid #f0f0f0' : 'none' }}>
          <div style={{ fontSize: '0.72rem', color: '#666', lineHeight: 1.5, marginBottom: '0.65rem' }}>
            Paste your website URL or upload your business plan, financial documents, marketing materials or any other relevant files to help me give you a more grounded and specific response.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>🌐 Website</span>
            <input
              type="url"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              style={{ flex: '1 1 220px', minWidth: 180, fontSize: '0.78rem', padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>📄 Documents</span>
            <input ref={fileInputRef} type="file" accept={ACCEPTED} multiple onChange={handleFilesPicked} style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()}
              style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: '1px solid #1D9E75', background: 'white', color: '#1D9E75', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
              + Add files
            </button>
            <span style={{ fontSize: '0.68rem', color: '#ccc' }}>PDF, Word, PowerPoint</span>
            {hasInput && (
              <button onClick={handleAnalyse} disabled={analysing}
                style={{ marginLeft: 'auto', padding: '0.35rem 0.9rem', borderRadius: 6, background: analysing ? '#ccc' : '#1D9E75', border: 'none', color: 'white', fontSize: '0.75rem', fontWeight: 700, cursor: analysing ? 'not-allowed' : 'pointer', width: 'auto', fontFamily: 'inherit' }}>
                {analysing ? 'Analysing…' : 'Analyse'}
              </button>
            )}
          </div>
          {files.length > 0 && (
            <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {files.map((f, i) => (
                <span key={i} style={{ fontSize: '0.68rem', background: '#E1F5EE', color: '#0F6E56', padding: '2px 6px 2px 8px', borderRadius: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                  {f.name}
                  <button onClick={() => removeFile(f.name)} style={{ background: 'none', border: 'none', color: '#0F6E56', cursor: 'pointer', padding: 0, fontSize: '0.75rem', lineHeight: 1, width: 'auto', opacity: 0.7 }}>×</button>
                </span>
              ))}
            </div>
          )}
          {statusMessage && <div style={{ marginTop: '0.45rem', fontSize: '0.68rem', color: '#0F6E56', fontStyle: 'italic' }}>{statusMessage}</div>}
        </div>
      )}
    </div>
  );
}

// Local masthead so the Decision Guru wordmark renders in #48B650 per the
// standardised guru-name colour.
function DecisionMasthead({ tagline, spacing }) {
  const bottomMargin = spacing === 'tight' ? '0.75rem' : '1.5rem';
  return (
    <div style={{ textAlign: 'center', margin: `0 0 ${bottomMargin}` }}>
      <div style={{ fontSize: '2.5rem', fontWeight: 300, color: '#1a1a2e', letterSpacing: '-0.02em', lineHeight: 1 }}>
        Decision
        <strong style={{ color: '#48B650', fontWeight: 700 }}> Guru</strong>
      </div>
      {tagline && (
        <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.4rem', letterSpacing: '0.04em' }}>
          {tagline}
        </div>
      )}
    </div>
  );
}

// Simple tooltip component
function Tooltip({ children, content, show }) {
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {show && content && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '5px',
          padding: '6px 10px',
          background: '#1a1a2e',
          color: 'white',
          fontSize: '0.7rem',
          borderRadius: '6px',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          {content}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid #1a1a2e',
          }} />
        </div>
      )}
    </div>
  );
}

// ── Journey progress ──────────────────────────────────────────────
const PROGRESS_STAGES = [
  'Know your business',
  'Frame the challenge',
  'Build your options',
  'Your recommendation',
];

// ── Loading screen content ────────────────────────────────────────
const STATUS_MESSAGES = [
  'Reading your challenge...',
  'Weighing your options...',
  'Checking for tensions...',
  'Building your recommendation...',
];

const QUOTES = [
  'Clarity beats effort.',
  'Clear problems get solved faster.',
  'Write it down — decisions get easier.',
  'Gut feel starts it. Clear thinking finishes it.',
  "If the reason isn't clear, the decision won't stick.",
  'Saying no does most of the work.',
  'Delay quietly kills good options.',
  'Advice without context is guess-work.',
  "Unsure? Start with what's actually true.",
  'Up close, the problem usually changes shape.',
  "Busy doesn't mean progress.",
  'If everything matters, nothing moves.',
  'Focus creates momentum.',
  'Progress often starts when something stops.',
  'Direction first. Work second.',
  'Unclear goals create messy work.',
  'Not choosing is still a decision.',
  'Commitment clears the path.',
  'Explore the opposite — see what it tells you.',
  "More effort won't fix the wrong focus.",
  "It's easy to fix a symptom but miss the problem.",
  "An option you haven't considered can't save you.",
  'The option you favour most deserves the most scrutiny, not the least.',
  "Rigour isn't about slowing down. It's about not having to go back.",
  'Instinct picks the option. Rigour checks if it\'s right.',
  "Good leaders back their instincts — after they've tested them.",
];

// ── Split field config ────────────────────────────────────────────
const SPLIT_FIELDS = {
  revenue_model: {
    opts: [
      { key: 'project',  label: 'Project-based', inferKeys: ['project'] },
      { key: 'retainer', label: 'Retainer',       inferKeys: ['retainer'] },
      { key: 'sub',      label: 'Subscription',   inferKeys: ['subscription'] },
      { key: 'trans',    label: 'Transactional',  inferKeys: ['transactional'] },
    ],
    palette: ['#1D9E75','#185FA5','#9B6B3A','#7F77DD'],
  },
  go_to_market: {
    opts: [
      { key: 'founder',  label: 'Founder-led',     inferKeys: ['founder','direct'] },
      { key: 'salesteam',label: 'In-house sales',  inferKeys: ['sales team','in-house','sales-team'] },
      { key: 'inbound',  label: 'Inbound',          inferKeys: ['inbound','content'] },
      { key: 'partners', label: 'Partners',         inferKeys: ['partners','referral'] },
      { key: 'self',     label: 'Self-serve',       inferKeys: ['self-serve','self'] },
    ],
    palette: ['#1D9E75','#185FA5','#9B6B3A','#7F77DD','#C0556E'],
  },
  delivery_model: {
    opts: [
      { key: 'people',      label: 'People-led',   inferKeys: ['people','service','consulting'] },
      { key: 'productised', label: 'Productised',  inferKeys: ['productised','packaged'] },
      { key: 'platform',    label: 'Platform',     inferKeys: ['platform','software','saas'] },
      { key: 'mixed',       label: 'Mixed',        inferKeys: ['mixed'] },
    ],
    palette: ['#1D9E75','#185FA5','#9B6B3A','#7F77DD'],
  },
};

function parseSplitValue(fieldKey, inferredStr) {
  const config = SPLIT_FIELDS[fieldKey];
  const str = (inferredStr || '').toLowerCase();
  if (!str || str === 'unclear') {
    return { active: [config.opts[0].key], splits: { [config.opts[0].key]: 100 } };
  }
  if (str === 'mixed') {
    const first2 = config.opts.slice(0, 2);
    return { active: first2.map(o => o.key), splits: Object.fromEntries(first2.map(o => [o.key, 50])) };
  }
  for (const opt of config.opts) {
    if (opt.inferKeys.some(k => str.includes(k))) {
      return { active: [opt.key], splits: { [opt.key]: 100 } };
    }
  }
  return { active: [config.opts[0].key], splits: { [config.opts[0].key]: 100 } };
}

function splitToString(fieldKey, splitState) {
  const config = SPLIT_FIELDS[fieldKey];
  if (!splitState || !splitState.active || !splitState.active.length) return '';
  const label = k => config.opts.find(o => o.key === k)?.label || k;
  if (splitState.active.length === 1) return label(splitState.active[0]);
  return splitState.active.map(k => `${label(k)} (${splitState.splits[k] || 0}%)`).join(', ');
}

function normaliseSplitState(splitState) {
  if (!splitState || !splitState.active.length) return splitState;
  const total = splitState.active.reduce((s, k) => s + (splitState.splits[k] || 0), 0);
  if (total === 0 || total === 100) return splitState;
  const newSplits = { ...splitState.splits };
  let sum = 0;
  splitState.active.forEach((k, i) => {
    if (i === splitState.active.length - 1) { newSplits[k] = 100 - sum; }
    else { newSplits[k] = Math.round((splitState.splits[k] || 0) * 100 / total); sum += newSplits[k]; }
  });
  return { ...splitState, splits: newSplits };
}

// ── SplitField component ──────────────────────────────────────────
function SplitField({ fieldKey, splitState, onChange, editing }) {
  const config = SPLIT_FIELDS[fieldKey];
  const barRef = React.useRef(null);
  const getColor = (idx) => config.palette[idx % config.palette.length];
  const getTotal = (st) => (st?.active || []).reduce((s, k) => s + (st.splits[k] || 0), 0);

  function updateSplit(key, val, st) {
    const newSplits = { ...st.splits, [key]: Math.max(1, Math.min(100, val)) };
    onChange({ ...st, splits: newSplits });
  }

  function toggleOption(key) {
    const st = splitState;
    if (st.active.includes(key)) {
      if (st.active.length === 1) return;
      const removed = st.splits[key] || 0;
      const newActive = st.active.filter(k => k !== key);
      const newSplits = { ...st.splits };
      delete newSplits[key];
      const last = newActive[newActive.length - 1];
      newSplits[last] = (newSplits[last] || 0) + removed;
      onChange(normaliseSplitState({ active: newActive, splits: newSplits }));
    } else {
      const newActive = [...st.active, key];
      const newSplits = { ...st.splits, [key]: 20 };
      onChange(normaliseSplitState({ active: newActive, splits: newSplits }));
    }
  }

  function startDrag(e, leftIdx) {
    if (!barRef.current) return;
    e.preventDefault();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const barW = barRef.current.getBoundingClientRect().width;
    const lk = splitState.active[leftIdx];
    const rk = splitState.active[leftIdx + 1];
    const sl = splitState.splits[lk];
    const sr = splitState.splits[rk];

    const move = (cx) => {
      const dp = ((cx - startX) / barW) * 100;
      const newL = Math.round(Math.min(Math.max(sl + dp, 5), sl + sr - 5));
      onChange({ ...splitState, splits: { ...splitState.splits, [lk]: newL, [rk]: sl + sr - newL } });
    };
    const mm = ev => move(ev.clientX);
    const mu = () => { document.removeEventListener('mousemove', mm); document.removeEventListener('mouseup', mu); };
    document.addEventListener('mousemove', mm);
    document.addEventListener('mouseup', mu);
  }

  const st = splitState || { active: [config.opts[0].key], splits: { [config.opts[0].key]: 100 } };
  const total = getTotal(st);

  // View mode — just show the bar
  if (!editing) {
    return (
      <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '0.75rem 1rem' }}>
        <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', display: 'block', marginBottom: '0.4rem' }}>
          {fieldKey === 'revenue_model' ? 'Revenue model' : fieldKey === 'go_to_market' ? 'Go to market' : 'Delivery model'}
        </span>
        <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden' }}>
          {st.active.map((key, idx) => {
            const pct = st.splits[key] || 0;
            const color = getColor(idx);
            return (
              <div key={key} style={{ width: `${pct}%`, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
                {pct > 15 && <span style={{ fontSize: 10, fontWeight: 500, color: 'white', padding: '0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{config.opts.find(o => o.key === key)?.label}</span>}
              </div>
            );
          })}
        </div>
        {st.active.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
            {st.active.map((key, idx) => (
              <span key={key} style={{ fontSize: 10, color: getColor(idx), fontWeight: 500 }}>
                {config.opts.find(o => o.key === key)?.label} {st.splits[key]}%
              </span>
            ))}
          </div>
        )}
        {st.active.length === 1 && (
          <p style={{ fontSize: '0.82rem', color: '#1a1a2e', margin: '0.25rem 0 0' }}>
            {config.opts.find(o => o.key === st.active[0])?.label}
          </p>
        )}
      </div>
    );
  }

  // Edit mode — full interactive component
  return (
    <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '0.75rem 1rem' }}>
      <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', display: 'block', marginBottom: '0.5rem' }}>
        {fieldKey === 'revenue_model' ? 'Revenue model' : fieldKey === 'go_to_market' ? 'Go to market' : 'Delivery model'}
      </span>

      {/* Bar */}
      <div ref={barRef} style={{ display: 'flex', height: 36, borderRadius: 6, overflow: 'hidden', userSelect: 'none', cursor: 'default' }}>
        {st.active.map((key, idx) => {
          const pct = st.splits[key] || 0;
          const color = getColor(idx);
          return (
            <div key={key} style={{ width: `${pct}%`, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', minWidth: 0 }}>
              {pct > 12 && <span style={{ fontSize: 10, fontWeight: 500, color: 'white', padding: '0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{config.opts.find(o => o.key === key)?.label}</span>}
              {idx < st.active.length - 1 && (
                <div onMouseDown={e => startDrag(e, idx)} style={{ position: 'absolute', top: 0, bottom: 0, right: -5, width: 10, cursor: 'col-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.3)' }}>
                  <div style={{ width: 2, height: 14, background: 'rgba(255,255,255,0.8)', borderRadius: 1 }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Per-segment inputs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
        {st.active.map((key, idx) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: getColor(idx), flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#1a1a2e', flex: 1 }}>{config.opts.find(o => o.key === key)?.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <input type="number" min="1" max="100" step="1" value={Math.round(st.splits[key] || 0)}
                onChange={e => updateSplit(key, parseInt(e.target.value) || 0, st)}
                style={{ width: 46, fontSize: 12, fontWeight: 500, padding: '3px 5px', border: '0.5px solid #ddd', borderRadius: 5, textAlign: 'center', fontFamily: 'inherit' }} />
              <span style={{ fontSize: 11, color: '#aaa' }}>%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Pills + total */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 10 }}>
        {config.opts.map((opt, idx) => {
          const isActive = st.active.includes(opt.key);
          const color = isActive ? getColor(st.active.indexOf(opt.key)) : '#ccc';
          return (
            <div key={opt.key} onClick={() => toggleOption(opt.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px 3px 7px', borderRadius: 20, border: `0.5px solid ${isActive ? 'transparent' : '#ddd'}`, fontSize: 11, cursor: 'pointer', background: isActive ? color : 'white', color: isActive ? 'white' : '#888' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? 'rgba(255,255,255,0.55)' : '#ddd' }} />
              {opt.label}
            </div>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 20, background: total === 100 ? '#E1F5EE' : '#FCEBEB', color: total === 100 ? '#0F6E56' : '#A32D2D' }}>
          {total}%
        </span>
      </div>
    </div>
  );
}

// ── ProgressTrack component ───────────────────────────────────────
// Visual treatment matches the Direction Guru / Design Guru journey
// indicators (20px markers, 2px borders, 12px stage labels). The
// underlying stage logic and the CDDD progression itself are unchanged.
function ProgressTrack({ activeIndex }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', width: '100%',
      marginBottom: '2rem', padding: '0.75rem 0.25rem 0',
    }}>
      {PROGRESS_STAGES.map((label, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex;
        return (
          <div key={i} style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', position: 'relative', minWidth: 0,
          }}>
            {i < PROGRESS_STAGES.length - 1 && (
              <div style={{
                position: 'absolute', top: 9,
                left: 'calc(50% + 12px)', right: 'calc(-50% + 12px)',
                height: '1.5px',
                background: done ? '#1D9E75' : '#e0e0e0',
                zIndex: 0,
              }} />
            )}
            <div style={{
              width: 20, height: 20, borderRadius: '50%', zIndex: 1, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: done ? '#1D9E75' : 'white',
              border: `2px solid ${done || active ? '#1D9E75' : '#ccc'}`,
            }}>
              {done && (
                <svg viewBox="0 0 8 8" width="9" height="9">
                  <polyline points="1,4 3,6.5 7,1.5" fill="none" stroke="white"
                    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {active && (
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#1D9E75' }} />
              )}
            </div>
            <div style={{
              fontSize: 12, marginTop: 6, textAlign: 'center', lineHeight: 1.25,
              color: done ? '#0F6E56' : active ? '#1a1a2e' : '#999',
              fontWeight: active ? 700 : done ? 700 : 600,
            }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── CDDD stage labels ─────────────────────────────────────────────
const CDDD_STAGES = [
  'Understanding your situation',
  'Mapping what makes this hard',
  'Weighing the considerations',
  'Building your recommendation',
];

// ── LoadingScreen component ───────────────────────────────────────
function LoadingScreen() {
  const [quoteIdx, setQuoteIdx] = React.useState(
    () => Math.floor(Math.random() * QUOTES.length)
  );
  const [visible, setVisible] = React.useState(true);
  const [stageIdx, setStageIdx] = React.useState(0);

  React.useEffect(() => {
    const quoteTimer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setQuoteIdx(i => (i + 1) % QUOTES.length);
        setVisible(true);
      }, 400);
    }, 4000);
    const stageTimer = setInterval(() => {
      setStageIdx(i => Math.min(i + 1, CDDD_STAGES.length - 1));
    }, 4500);
    return () => { clearInterval(quoteTimer); clearInterval(stageTimer); };
  }, []);

  return (
    <div style={{ padding: '3rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2.75rem' }}>

      {/* Brand — Decision Guru mast during processing */}
      <DecisionMasthead tagline="Structure your thinking. Make better decisions." spacing="tight" />

      {/* Horizontal CDDD track */}
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', maxWidth: 580 }}>
        {CDDD_STAGES.map((label, i) => {
          const state = i < stageIdx ? 'done' : i === stageIdx ? 'active' : 'waiting';
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {i < CDDD_STAGES.length - 1 && (
                <div style={{
                  position: 'absolute', top: 11,
                  left: 'calc(50% + 12px)', right: 'calc(-50% + 12px)',
                  height: 1.5,
                  background: state === 'done' ? '#1D9E75' : '#e0e0e0',
                  zIndex: 0,
                }} />
              )}
              <div style={{
                width: 22, height: 22, borderRadius: '50%', zIndex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                background: state === 'done' ? '#1D9E75' : 'white',
                border: `${state === 'active' ? 2 : 1.5}px solid ${state === 'waiting' ? '#ddd' : '#1D9E75'}`,
              }}>
                {state === 'done' && (
                  <svg viewBox="0 0 8 8" width="10" height="10">
                    <polyline points="1,4 3,6.5 7,1.5" fill="none" stroke="white"
                      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {state === 'active' && (
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%', background: '#1D9E75',
                    animation: 'gu-pulse 1.2s ease-in-out infinite',
                  }} />
                )}
              </div>
              <div style={{
                fontSize: '0.72rem', textAlign: 'center', marginTop: 6,
                lineHeight: 1.35, padding: '0 4px',
                color: state === 'done' ? '#0F6E56' : state === 'active' ? '#1a1a2e' : '#bbb',
                fontWeight: state === 'active' ? 500 : 400,
              }}>{label}</div>
            </div>
          );
        })}
      </div>

      {/* Quote */}
      <div style={{ textAlign: 'center', maxWidth: 520, opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
        <p style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a2e', lineHeight: 1.4, margin: '0 0 0.35rem' }}>
          {QUOTES[quoteIdx]}
        </p>
        <p style={{ fontSize: '0.72rem', color: '#bbb', margin: 0 }}>GuruOne</p>
      </div>

    </div>
  );
}

function parseRevision(text) {
  const sections = { wrong: '', changes: [], same: [], recommendation: '', firstDays: [] };
  const lines = text.split('\n').map(l => l.trim()).filter(l => l);
  let current = null;
  for (const line of lines) {
    if (line.includes('WHAT I GOT WRONG')) { current = 'wrong'; continue; }
    if (line.includes('WHAT CHANGES')) { current = 'changes'; continue; }
    if (line.includes('WHAT STAYS THE SAME')) { current = 'same'; continue; }
    if (line.includes('REVISED RECOMMENDATION')) { current = 'recommendation'; continue; }
    if (line.includes('REVISED FIRST 30 DAYS')) { current = 'firstDays'; continue; }
    if (!current) continue;
    const cleaned = line.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '').trim();
    if (!cleaned) continue;
    if (current === 'wrong') sections.wrong += (sections.wrong ? ' ' : '') + cleaned;
    else if (current === 'changes') sections.changes.push(cleaned);
    else if (current === 'same') sections.same.push(cleaned);
    else if (current === 'recommendation') sections.recommendation += (sections.recommendation ? ' ' : '') + cleaned;
    else if (current === 'firstDays') sections.firstDays.push(cleaned);
  }
  return sections;
}

function ExpandableSection({ title, summary, content }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: 'white', borderRadius: '10px', marginBottom: '0.75rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden'
    }}>
      <div onClick={() => setOpen(!open)} style={{
        padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', cursor: 'pointer', gap: '1rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 }}>
          <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1D9E75' }}>{title}</span>
          {summary && <span style={{ fontSize: '0.82rem', color: '#666', lineHeight: '1.4' }}>{summary}</span>}
        </div>
        <span style={{ fontSize: '0.75rem', color: '#aaa', flexShrink: 0, marginTop: '0.2rem' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>
      {open && (
        <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid #f0f0f0' }}>
          {content}
        </div>
      )}
    </div>
  );
}

function RevisedResult({ text }) {
  const parsed = parseRevision(text);
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '1.1rem', color: '#185FA5' }}>↻</span>
        <span style={{ fontSize: '1rem', fontWeight: '600', color: '#185FA5' }}>Revised recommendation</span>
      </div>
      {parsed.wrong && (
        <div style={{ background: 'white', borderRadius: '10px', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ background: '#FFF3E0', padding: '0.6rem 1.25rem', borderBottom: '1px solid #FFE0B2' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#E65100' }}>What I got wrong</span>
          </div>
          <div style={{ padding: '1rem 1.25rem' }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#333', lineHeight: '1.6' }}>{parsed.wrong}</p>
          </div>
        </div>
      )}
      {parsed.changes.length > 0 && (
        <div style={{ background: 'white', borderRadius: '10px', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ background: '#E3F2FD', padding: '0.6rem 1.25rem', borderBottom: '1px solid #BBDEFB' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#0D47A1' }}>What changes</span>
          </div>
          <div style={{ padding: '0.75rem 1.25rem' }}>
            {parsed.changes.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.5rem 0', borderBottom: i < parsed.changes.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <span style={{ color: '#185FA5', fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>↑</span>
                <span style={{ fontSize: '0.875rem', color: '#1a1a2e', lineHeight: '1.5' }}>{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {parsed.same.length > 0 && (
        <div style={{ background: 'white', borderRadius: '10px', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ background: '#F5F5F5', padding: '0.6rem 1.25rem', borderBottom: '1px solid #E0E0E0' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#555' }}>What stays the same</span>
          </div>
          <div style={{ padding: '0.75rem 1.25rem' }}>
            {parsed.same.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.5rem 0', borderBottom: i < parsed.same.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <span style={{ color: '#888', fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>=</span>
                <span style={{ fontSize: '0.875rem', color: '#555', lineHeight: '1.5' }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {parsed.recommendation && (
        <div style={{ background: '#E1F5EE', borderRadius: '10px', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', borderLeft: '4px solid #1D9E75' }}>
          <div style={{ background: '#C8EDE0', padding: '0.6rem 1.25rem', borderBottom: '1px solid #A5D6C3' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#0F6E56' }}>★ Revised recommendation</span>
          </div>
          <div style={{ padding: '1rem 1.25rem' }}>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#1a1a2e', lineHeight: '1.7', fontWeight: '500' }}>{parsed.recommendation}</p>
          </div>
        </div>
      )}
      {parsed.firstDays.length > 0 && (
        <div style={{ background: 'white', borderRadius: '10px', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ background: '#E1F5EE', padding: '0.6rem 1.25rem', borderBottom: '1px solid #A5D6C3' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#0F6E56' }}>→ Revised first 30 days</span>
          </div>
          <div style={{ padding: '0.75rem 1.25rem' }}>
            {parsed.firstDays.map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.5rem 0', borderBottom: i < parsed.firstDays.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <span style={{ color: '#1D9E75', fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>→</span>
                <span style={{ fontSize: '0.875rem', color: '#333', lineHeight: '1.5' }}>{a}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Confidence pill for canvas sections ─────────────────────────
const CONF_PILL = {
  high:   { label: 'High confidence',   bg: '#f0fdf8', border: '#a7f3d0', color: '#065f46' },
  medium: { label: 'Medium confidence', bg: '#fffbeb', border: '#fde68a', color: '#78350f' },
  low:    { label: 'Low confidence',    bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
};
const CONF_ORDER = ['high', 'medium', 'low'];

function sectionConfidence(confObj, keys) {
  if (!confObj) return null;
  const vals = keys.map(k => confObj[k]).filter(Boolean);
  if (!vals.length) return null;
  // Return the lowest confidence found across the keys
  return vals.sort((a, b) => CONF_ORDER.indexOf(b) - CONF_ORDER.indexOf(a))[0];
}

function ConfidencePill({ conf }) {
  if (!conf || !CONF_PILL[conf]) return null;
  const s = CONF_PILL[conf];
  return (
    <span style={{
      fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 10,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      marginLeft: '0.5rem', verticalAlign: 'middle',
    }}>
      {s.label}
    </span>
  );
}

function CanvasItem({ label, value, editing, onChange, type, options, labelMap }) {
  const displayVal = (v) => (labelMap && labelMap[v]) ? labelMap[v] : v;
  const isUnclear = !editing && (!value || value === 'unclear' || value === '' || value === 'not provided');

  if (!editing) {
    if (Array.isArray(value) && value.length > 0) {
      return (
        <div className={`canvas-item${isUnclear ? ' unclear' : ''}`}>
          <span>{label}</span>
          <ul className="canvas-bullets">
            {value.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        </div>
      );
    }
    const strVal = Array.isArray(value) ? value.join('\n') : (value || '');
    const lines = strVal.split('\n').map(l => l.replace(/^[-•*]\s*/, '').trim()).filter(l => l);
    return (
      <div className={`canvas-item${isUnclear ? ' unclear' : ''}`}>
        <span>{label}{isUnclear ? ' ⚠' : ''}</span>
        {isUnclear ? (
          <p style={{ color: '#BA7517', fontSize: '0.78rem', fontStyle: 'italic' }}>Not confirmed — please edit</p>
        ) : lines.length > 1 ? (
          <ul className="canvas-bullets">
            {lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        ) : (
          <p className={label === 'Price positioning' ? `price-tag price-${value}` : ''}>
            {displayVal(strVal)}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="canvas-item canvas-item-editing">
      <span>{label}</span>
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)}
          style={{ fontSize: '0.8rem', padding: '4px 6px', marginTop: '4px', width: '100%' }}>
          {options.map(o => <option key={o} value={o}>{displayVal(o)}</option>)}
        </select>
      ) : (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3}
          style={{ fontSize: '0.8rem', padding: '4px 6px', marginTop: '4px', width: '100%',
            border: '1px solid #1D9E75', borderRadius: '4px', resize: 'vertical' }} />
      )}
    </div>
  );
}

// ── HowPanel component ────────────────────────────────────────────
function HowPanel({ option, canvas, lens, onClose, cachedContent, onCacheContent }) {
  const [content, setContent] = React.useState(cachedContent || null);
  const [loading, setLoading] = React.useState(!cachedContent);
  const [loadError, setLoadError] = React.useState(null);
  const [stepIdx, setStepIdx] = React.useState(0);
  const [expanded, setExpanded] = React.useState(false);
  const [quoteIdx, setQuoteIdx] = React.useState(() => Math.floor(Math.random() * QUOTES.length));
  const [statusIdx, setStatusIdx] = React.useState(0);
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    let quoteTimer, statusTimer;
    if (loading) {
      quoteTimer = setInterval(() => {
        setVisible(false);
        setTimeout(() => { setQuoteIdx(i => (i + 1) % QUOTES.length); setVisible(true); }, 380);
      }, 4000);
      statusTimer = setInterval(() => setStatusIdx(i => (i + 1) % STATUS_MESSAGES.length), 2200);
    }
    return () => { clearInterval(quoteTimer); clearInterval(statusTimer); };
  }, [loading]);

  React.useEffect(() => {
    if (!loading) return;

    const today = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const geography = canvas.geography || 'UK';
    const currency = canvas.currency || (geography.toLowerCase().includes('uk') ? 'GBP (£)' : 'local currency');
    const systemContext = `You are GuruOne, a strategic advisor for SME businesses.
Today is ${today}. This business operates in ${geography}, use ${currency} for all monetary figures — never use $.
The business leader is considering Option ${option.label} — "${option.title}": ${option.description}
Business context: Sector: ${canvas.sector || 'unknown'}. Revenue model: ${canvas.revenue_model || 'unknown'}. Key challenges: ${canvas.key_challenges || 'not provided'}. Annual revenue: ${canvas.annual_revenue || 'not provided'}. Lens: ${lens || 'not provided'}.
Respond with ONLY valid JSON. No markdown. No code blocks. Start with { and end with }.`;

    const userPrompt = `Provide a practical implementation guide for this option.
Return exactly this JSON structure:
{
  "summary": "2-3 sentences on what success looks like, specific to this business",
  "steps": [
    { "title": "max 6 words", "detail": "2-3 sentences of specific practical guidance" },
    { "title": "max 6 words", "detail": "2-3 sentences of specific practical guidance" },
    { "title": "max 6 words", "detail": "2-3 sentences of specific practical guidance" },
    { "title": "max 6 words", "detail": "2-3 sentences of specific practical guidance" },
    { "title": "max 6 words", "detail": "2-3 sentences of specific practical guidance" },
    { "title": "max 6 words", "detail": "2-3 sentences of specific practical guidance" }
  ]
}`;

    axios.post(`${API}/chat`, {
      messages: [{ role: 'user', content: userPrompt }],
      context: systemContext
    })
      .then(res => {
        const raw = res.data.reply;
        const start = raw.indexOf('{');
        const end = raw.lastIndexOf('}');
        if (start === -1 || end === -1) throw new Error('No JSON in response');
        const howContent = JSON.parse(raw.slice(start, end + 1));
        setContent(howContent);
        if (onCacheContent) onCacheContent(howContent);
        setLoading(false);
      })
      .catch(e => {
        const msg = e.response?.data?.error || e.message || 'Unknown error';
        setLoadError(msg);
        setLoading(false);
      });
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="how-panel">
        <button className="how-close" onClick={onClose}>✕ Close</button>
        <div className="how-loading">
          <div className="loading-spinner" />
          <p style={{ fontSize: '0.83rem', color: '#888', margin: '0 0 2rem' }}>
            {STATUS_MESSAGES[statusIdx]}
          </p>
          <div style={{ borderLeft: '2.5px solid #1D9E75', padding: '0.5rem 0 0.5rem 1.1rem', textAlign: 'left', display: 'inline-block', opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease', minWidth: '520px', maxWidth: '600px' }}>
            <p style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e', margin: '0 0 0.25rem' }}>{QUOTES[quoteIdx]}</p>
            <p style={{ fontSize: '0.72rem', color: '#bbb', margin: 0 }}>GuruOne</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) return (
    <div className="how-panel">
      <button className="how-close" onClick={onClose}>✕ Close</button>
      <p style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#555', marginBottom: '1rem' }}>Option {option.label} — {option.title}</p>
      <p style={{ fontSize: '0.82rem', color: '#A32D2D', margin: '0 0 0.75rem' }}>
        Could not generate the how guide: {loadError}
      </p>
      <button onClick={() => { setLoadError(null); setLoading(true); }}
        style={{ fontSize: '0.78rem', padding: '0.4rem 1rem', borderRadius: 6, border: '1px solid #1D9E75', background: 'white', color: '#1D9E75', cursor: 'pointer' }}>
        Try again
      </button>
    </div>
  );

  if (!content) return null;

  const half = Math.ceil(content.steps.length / 2);

  return (
    <div className="how-panel">
      <button className="how-close" onClick={onClose}>✕ Close</button>
      <p style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#555', marginBottom: '1rem' }}>Option {option.label} — {option.title}</p>

      <div className="how-summary">{content.summary}</div>

      {/* Numbered track */}
      <div className="how-track">
        {content.steps.map((s, i) => (
          <React.Fragment key={i}>
            <div className="how-track-dot" onClick={() => { setStepIdx(i); setExpanded(false); }}>
              <div className={`how-track-circle ${!expanded && i === stepIdx ? 'active' : ''}`}>{i + 1}</div>
              <div className={`how-track-label ${!expanded && i === stepIdx ? 'active' : ''}`}>{s.title}</div>
            </div>
            {i < content.steps.length - 1 && <div className="how-track-line" />}
          </React.Fragment>
        ))}
        <button
          style={{ marginLeft: '1rem', fontSize: 11, padding: '4px 12px', borderRadius: 20, border: `0.5px solid ${expanded ? '#1D9E75' : '#ddd'}`, background: expanded ? '#1D9E75' : 'white', color: expanded ? 'white' : '#888', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, width: 'auto', minWidth: 0 }}
          onClick={() => setExpanded(e => !e)}>
          {expanded ? '▲ Collapse' : 'Expand all ↓'}
        </button>
      </div>

      {!expanded ? (
        <div className="how-step-detail">
          <div className="how-step-num">Step {stepIdx + 1} of {content.steps.length}</div>
          <div className="how-step-title">{content.steps[stepIdx].title}</div>
          <div className="how-step-body">{content.steps[stepIdx].detail}</div>
          <div className="how-step-nav">
            <button className="how-nav-btn" disabled={stepIdx === 0} style={{ opacity: stepIdx === 0 ? 0.35 : 1 }}
              onClick={() => setStepIdx(i => Math.max(0, i - 1))}>← Previous</button>
            <button className="how-nav-btn next"
              onClick={() => { if (stepIdx < content.steps.length - 1) setStepIdx(i => i + 1); }}>
              {stepIdx === content.steps.length - 1 ? 'Done ✓' : 'Next →'}
            </button>
          </div>
        </div>
      ) : (
        <div className="how-expand-grid">
          {[content.steps.slice(0, half), content.steps.slice(half)].map((col, ci) => (
            <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              {col.map((s, i) => (
                <div key={i} className="how-expand-item">
                  <div className="how-expand-circle">{ci === 0 ? i + 1 : half + i + 1}</div>
                  <div className="how-expand-card">
                    <div className="how-expand-title">{s.title}</div>
                    <div className="how-expand-detail">{s.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── WhyPanel component ────────────────────────────────────────────
function WhyPanel({ loading, error, content, onClose }) {
  return (
    <div className="why-panel">
      <button className="why-close" onClick={onClose}>✕</button>
      <div className="why-panel-label">Why this recommendation?</div>
      {loading && <p style={{ fontSize: '0.82rem', color: '#888', margin: 0 }}>Explaining the reasoning…</p>}
      {error && <p style={{ fontSize: '0.82rem', color: '#A32D2D', margin: 0 }}>{error}</p>}
      {content && (
        <>
          <p style={{ fontSize: '0.88rem', fontWeight: 500, color: '#1a1a2e', lineHeight: 1.6, margin: '0 0 1rem' }}>
            {content.headline}
          </p>
          {content.assertions?.map((a, i) => (
            <div key={i} style={{ borderTop: i > 0 ? '1px solid #cce0f5' : 'none', paddingTop: i > 0 ? '0.75rem' : 0, marginTop: i > 0 ? '0.75rem' : 0 }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#185FA5', margin: '0 0 0.25rem' }}>{a.claim}</p>
              <p style={{ fontSize: '0.8rem', color: '#333', lineHeight: 1.6, margin: 0 }}>{a.reasoning}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── DecisionGuru main component ───────────────────────────────────
export default function DecisionGuru() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get user for localStorage key
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; }
  })();
  const userKey = user?.id ? `decision_${user.id}` : 'decision_default';

  // Load persisted state from localStorage
  const loadPersistedState = () => {
    try {
      const saved = localStorage.getItem(`guruone_${userKey}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const persistedState = loadPersistedState();

  // One-shot prefill from another Guru (e.g. Delivery Guru "Take it to
  // Decision Guru" link). Read once on mount and clear immediately so it
  // does not persist into future visits. The title becomes the challenge;
  // the description is intentionally only carried so callers can pass
  // richer context if we wire it through later.
  const decisionPrefillTitle = (() => {
    try {
      const raw = localStorage.getItem('guruone_decision_prefill');
      if (!raw) return null;
      localStorage.removeItem('guruone_decision_prefill');
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed.title === 'string' && parsed.title.trim()) ? parsed.title : null;
    } catch {
      return null;
    }
  })();

  // Canvas is computed synchronously on first render so the challenge
  // step always has data to display — no blank-screen flash on mount.
  const [_initialCanvasFromExisting, _initialCanvas] = (() => {
    if (persistedState?.canvas) return [false, persistedState.canvas];
    const sd = readUserScopedCanvas('guruone_strategy');
    const od = readUserScopedCanvas('guruone_operating_model');
    const sp = calcStrategyPctRaw(sd);
    const bp = calcBlueprintPctRaw(od);
    if (sp >= 50 || bp >= 50) return [true, buildCanvasFromCanvases(sd, od)];
    return [false, {
      sector: '', primary_problem_solved: '', target_customer_type: '',
      price_positioning: 'mid-market', top_3_differentiators: '',
      primary_acquisition_channel: 'direct', revenue_model: 'project-based',
      sales_approach: 'founder-led', repeatability_of_delivery: 'medium',
      cash_runway: null, profit_breakdown: null,
      pricing_confidence: null, cash_collection: null,
    }];
  })();

  const [step, setStep] = useState(
    persistedState?.step && persistedState.step !== 'start'
      ? persistedState.step
      : 'challenge'
  );
  // Controls the "continue previous session" banner. Dismissed by either
  // banner button so the user is never stuck in a loop.
  const [bannerVisible, setBannerVisible] = useState(
    !!(persistedState?.canvas && (
      persistedState.step === 'questions' ||
      persistedState.step === 'result' ||
      (persistedState.step === 'challenge' && persistedState.challenge?.trim().length > 0)
    ))
  );
  const [helpOpen, setHelpOpen] = useState(false);
  const [addContextOpen, setAddContextOpen] = useState(false);
  // Snapshot panel visibility for the low-context path — kept as state so
  // the canvas grid render condition can gate on it; the previous toolbar
  // toggle has been removed, so the setter is intentionally not destructured.
  const [showCanvasSnapshot] = useState(true);
  // True when the canvas state was auto-built from the user's existing
  // Direction / Design canvases (≥50% complete in either) on mount —
  // hides the canvas context panel per the launch-experience spec.
  // Settable so the server-hydration effect below can flip it on once
  // it finds data the localStorage cache didn't have yet.
  const [canvasFromExisting, setCanvasFromExisting] = useState(_initialCanvasFromExisting);
  // Brief toast shown after "Add context" pulls fresh canvas fields in.
  // Cleared automatically after a few seconds so the leader sees an
  // unmistakable confirmation without it lingering.
  const [contextAppliedToast, setContextAppliedToast] = useState(null);
  // Extracted text from uploaded documents, appended to the LLM input
  // on handleChallenge and handleAnswers to enrich the context.
  const [uploadedDocText, setUploadedDocText] = useState('');
  const [url] = useState(persistedState?.url || '');
  const [challenge, setChallenge] = useState(decisionPrefillTitle || persistedState?.challenge || '');
  const [canvas, setCanvas] = useState(_initialCanvas);
  const [questions, setQuestions] = useState(persistedState?.questions || []);
  const [answers, setAnswers] = useState(persistedState?.answers || {});
  const [lens, setLens] = useState(persistedState?.lens || '');
  const [result, setResult] = useState(persistedState?.result || null);
  const [decisionId, setDecisionId] = useState(persistedState?.decisionId || null);
  const [isConfirmed, setIsConfirmed] = useState(persistedState?.isConfirmed || false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [feedbackNudgeDismissed, setFeedbackNudgeDismissed] = useState(false);
  const feedbackRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [buildingRecommendation, setBuildingRecommendation] = useState(false);
  const [error, setError] = useState('');
  // Clarify step — sits between answers submission and recommendation. The
  // backend probe applies the operating principles to the business leader's answers
  // and either green-lights the recommendation or returns up to 3 follow-ups.
  // State is intentionally not persisted: a mid-clarify reload sends the
  // business leader back to the questions step, which is acceptable for v1.
  const [clarifyQuestions, setClarifyQuestions] = useState([]);
  const [clarifyAnswers, setClarifyAnswers] = useState({});
  const [clarifyIteration, setClarifyIteration] = useState(0);
  const [allClarifications, setAllClarifications] = useState([]);
  const [userOptions, setUserOptions] = useState(persistedState?.userOptions || ['', '']);
  const [chatOpen, setChatOpen] = useState(false);
  const [consultantOpen, setConsultantOpen] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [revisedResult, setRevisedResult] = useState(null);
  const [howOption, setHowOption] = useState(null);
  const [howCache, setHowCache] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [criteriaExpanded, setCriteriaExpanded] = useState({});
  const [whyOpen, setWhyOpen] = useState(false);
  const [whyData, setWhyData] = useState({ loading: false, content: null, error: null });
  const [criteriaWeights, setCriteriaWeights] = useState(persistedState?.criteriaWeights || {});
  const [customCriterion, setCustomCriterion] = useState(persistedState?.customCriterion || '');
  const [showCustomInput, setShowCustomInput] = useState(false);
  // Canvas gap warning is dismissible — track which lens the dismissal was
  // for so a fresh lens (e.g. user re-enters with a different challenge)
  // shows its own warning again.
  const [gapWarningDismissedFor, setGapWarningDismissedFor] = useState(null);

  // Universal pre-lens setup questions — `what_tried` and `temporary_vs_systemic`
  // appear before every lens batch. Both optional, with explicit Skip.
  // Track which the leader has chosen to skip so the field can collapse to a
  // compact placeholder; clicking "Answer this" un-skips.
  const [preLensSkipped, setPreLensSkipped] = useState(persistedState?.preLensSkipped || {});

  // Track which answers were pre-filled and where they came from.
  //   { field_key: 'canvas' | 'narrative' | '..._edited' }
  // Shown as a small confirmation pill beneath the question label.
  const [prefillSources, setPrefillSources] = useState(persistedState?.prefillSources || {});

  // Relationship type for the people_decisions lens — parsed from narrative
  // by the backend, rendered as an editable chip at the top of the
  // questions step. Not asked as a blank question.
  const [relationshipType, setRelationshipType] = useState(persistedState?.relationshipType || null);

  // make_vs_buy: if the canvas already has the decision flagged, show a
  // small confirmation banner. Session-scoped so it doesn't reappear
  // after acknowledgement.
  const [makeVsBuyConfirmDismissed, setMakeVsBuyConfirmDismissed] = useState(false);

  // Section 12 — canvas back-sync toast. Populated after a recommendation
  // is rendered and the answers have been written back to canvases.
  const [syncToast, setSyncToast] = useState(null); // { synced: [...], visible: bool }
  const [discardedOptions, setDiscardedOptions] = useState({});
  const [discardingOption, setDiscardingOption] = useState(null);
  const [discardReason, setDiscardReason] = useState('');
  const [generatingAlt, setGeneratingAlt] = useState(null);
  const [altError, setAltError] = useState(null);
  const [altInputOption, setAltInputOption] = useState(null);
  const [altInputText, setAltInputText] = useState('');
  const [editingSection, setEditingSection] = useState(null); // null | 'value_proposition' | 'market_position' | 'business_model' | 'business_context'
  const [canvasEdits, setCanvasEdits] = useState({});
  const [splits, setSplits] = useState({
    revenue_model: null,
    go_to_market: null,
    delivery_model: null,
  });
  // Strategy context passed from StrategyPage
  const [routeStrategyContext] = useState(location.state?.strategyContext || null);
  const chatEndRef = React.useRef(null);

  // SplitSelector — visual split input with drag handles, absolute/%
  // mode toggle, chip-style pills for inactive options, and user-defined
  // custom categories. Mirrors the drag UX of the revenue/delivery/go-to-
  // market sliders in the results view. Currency derives from canvas.
  function SplitSelector({ config, value, onChange }) {
    const presetOpts = Array.isArray(config.opts) ? config.opts : [];
    const allowCustom = config.allow_custom === true;
    const seedSplits = () => {
      if (value?.splits) return value.splits;
      if (presetOpts.length > 0) return { [presetOpts[0].key]: 100 };
      return {};
    };
    const [mode, setMode] = useState(value?.mode || 'percentage');
    const [splits, setSplits] = useState(seedSplits);
    const [absoluteValues, setAbsoluteValues] = useState(value?.absoluteValues || {});
    const [customOpts, setCustomOpts] = useState(value?.customOpts || []);
    const [addingCustom, setAddingCustom] = useState(false);
    const [customInput, setCustomInput] = useState('');
    const barRef = React.useRef(null);

    const allOpts = [...presetOpts, ...customOpts];
    const activeSplits = mode === 'percentage' ? splits : absoluteValues;
    // Include zero-value entries so newly-added categories stay visible
    // and editable even when there's no remaining target capacity.
    const activeKeys = Object.keys(activeSplits);
    const total = Object.values(activeSplits).reduce((s, v) => s + (v || 0), 0);

    // Currency symbol pulled from canvas (e.g. "GBP (£)" -> "£"). Falls back to £.
    const currencySymbol = (() => {
      const cur = canvas?.currency || '';
      const m = cur.match(/\(([^)]+)\)/);
      return m ? m[1] : '£';
    })();

    // Parse a free-text currency amount to a number (e.g. "£28k" -> 28000,
    // "28,500" -> 28500, "3m" -> 3000000). Returns null if not parseable.
    function parseCurrencyAmount(str) {
      if (str == null) return null;
      const s = String(str).trim();
      if (!s) return null;
      const match = s.match(/([\d,]+(?:\.\d+)?)\s*(k|m)?/i);
      if (!match) return null;
      const n = parseFloat(match[1].replace(/,/g, ''));
      if (isNaN(n)) return null;
      const suffix = (match[2] || '').toLowerCase();
      if (suffix === 'k') return n * 1000;
      if (suffix === 'm') return n * 1000000;
      return n;
    }

    // If config.absoluteTotalFrom points at another answer field, use that
    // answer as the target total in absolute mode. Otherwise target is null.
    function getAbsoluteTarget() {
      if (!config.absoluteTotalFrom) return null;
      const raw = answers?.[config.absoluteTotalFrom];
      return parseCurrencyAmount(raw);
    }

    const palette = ['hsl(142,70%,45%)','hsl(219,70%,45%)','hsl(262,70%,45%)','hsl(39,70%,45%)','hsl(335,70%,45%)','hsl(185,70%,45%)','hsl(310,70%,45%)'];
    const getColor = (idx) => palette[idx % palette.length];

    // In absolute mode with a known target, segment widths reflect share
    // of the TARGET (not share of allocated). This makes "unallocated"
    // visible as the remaining portion of the bar.
    const absoluteTarget = mode === 'absolute' ? getAbsoluteTarget() : null;
    const useTargetScale = absoluteTarget != null && absoluteTarget > 0 && total <= absoluteTarget;
    const unallocated = useTargetScale ? Math.max(0, absoluteTarget - total) : 0;
    const over = absoluteTarget != null && total > absoluteTarget ? total - absoluteTarget : 0;

    const getDisplayPct = (key) => {
      if (mode === 'percentage') return activeSplits[key] || 0;
      if (useTargetScale) {
        return ((activeSplits[key] || 0) / absoluteTarget) * 100;
      }
      return total > 0 ? ((activeSplits[key] || 0) / total) * 100 : 0;
    };

    // Emit full value — always includes customOpts so they survive reload.
    function emit(nextSplits, nextAbs, nextCustomOpts, nextMode) {
      onChange({
        mode: nextMode !== undefined ? nextMode : mode,
        splits: nextSplits !== undefined ? nextSplits : splits,
        absoluteValues: nextAbs !== undefined ? nextAbs : absoluteValues,
        customOpts: nextCustomOpts !== undefined ? nextCustomOpts : customOpts,
      });
    }

    function switchMode(nextMode) {
      if (nextMode === mode) return;
      setMode(nextMode);
      emit(undefined, undefined, undefined, nextMode);
    }

    function updateSplit(key, newValue) {
      if (mode === 'percentage') {
        const u = { ...splits, [key]: Math.max(0, Math.min(100, newValue)) };
        setSplits(u); emit(u, absoluteValues, customOpts);
      } else {
        const u = { ...absoluteValues, [key]: Math.max(0, newValue) };
        setAbsoluteValues(u); emit(splits, u, customOpts);
      }
    }

    // Add a new option. In % mode we rescale existing entries so the
    // resulting total is always 100. In absolute mode we just seed a
    // sensible starting value — user's entering concrete amounts.
    function addOption(key) {
      if (mode === 'percentage') {
        const existingKeys = Object.keys(splits).filter(k => (splits[k] || 0) > 0);
        if (existingKeys.length === 0) {
          // First entry gets 100%
          const u = { [key]: 100 };
          setSplits(u); emit(u, absoluteValues, customOpts);
          return;
        }
        const defaultShare = 20;
        const availableForExisting = 100 - defaultShare;
        const existingTotal = existingKeys.reduce((s, k) => s + splits[k], 0);
        const u = { ...splits };
        let given = 0;
        existingKeys.forEach((k, i) => {
          if (i === existingKeys.length - 1) {
            u[k] = Math.max(1, availableForExisting - given);
          } else {
            u[k] = Math.max(1, Math.round((splits[k] / existingTotal) * availableForExisting));
            given += u[k];
          }
        });
        u[key] = defaultShare;
        setSplits(u); emit(u, absoluteValues, customOpts);
      } else {
        // Absolute mode: seed with a reasonable default. If a target total
        // is known (via absoluteTotalFrom), seed with remaining capacity
        // up to a cap so we don't overshoot.
        let seed = 1000;
        const target = getAbsoluteTarget();
        if (target) {
          const allocated = Object.values(absoluteValues).reduce((s, v) => s + (v || 0), 0);
          const remaining = Math.max(0, target - allocated);
          seed = remaining > 0 ? remaining : 0;
        }
        const u = { ...absoluteValues, [key]: seed };
        setAbsoluteValues(u); emit(splits, u, customOpts);
      }
    }

    // Remove an option. In % mode we redistribute the removed share
    // proportionally across remaining entries so the total stays at
    // its prior level (100 in normal flow). In absolute mode the
    // removed amount is simply unallocated again.
    function removeOption(key) {
      if (mode === 'percentage') {
        const removed = splits[key] || 0;
        const { [key]: _x, ...rest } = splits;
        const remainingKeys = Object.keys(rest);
        if (remainingKeys.length === 0 || removed === 0) {
          setSplits(rest); emit(rest, absoluteValues, customOpts);
          return;
        }
        const remainingTotal = remainingKeys.reduce((s, k) => s + rest[k], 0);
        const u = { ...rest };
        if (remainingTotal > 0) {
          let given = 0;
          remainingKeys.forEach((k, i) => {
            if (i === remainingKeys.length - 1) {
              u[k] = rest[k] + (removed - given);
            } else {
              const add = Math.round((rest[k] / remainingTotal) * removed);
              u[k] = rest[k] + add;
              given += add;
            }
          });
        }
        setSplits(u); emit(u, absoluteValues, customOpts);
      } else {
        const { [key]: _removed, ...u } = absoluteValues;
        setAbsoluteValues(u); emit(splits, u, customOpts);
      }
    }

    // Drag handle between two adjacent active segments. Works in both modes.
    // In % mode: segments sum stays constant (L+R constant). In absolute mode:
    // sum of the two dragged values stays constant — total doesn't change.
    function startDrag(e, leftIdx) {
      if (!barRef.current || activeKeys.length < 2) return;
      e.preventDefault();
      const startX = e.clientX ?? e.touches?.[0]?.clientX;
      const barW = barRef.current.getBoundingClientRect().width;
      const lk = activeKeys[leftIdx];
      const rk = activeKeys[leftIdx + 1];

      if (mode === 'percentage') {
        const sl = splits[lk] || 0;
        const sr = splits[rk] || 0;
        const move = (cx) => {
          const dp = ((cx - startX) / barW) * 100;
          const newL = Math.round(Math.min(Math.max(sl + dp, 1), sl + sr - 1));
          const u = { ...splits, [lk]: newL, [rk]: sl + sr - newL };
          setSplits(u); emit(u, absoluteValues, customOpts);
        };
        const mm = ev => move(ev.clientX);
        const mu = () => { document.removeEventListener('mousemove', mm); document.removeEventListener('mouseup', mu); };
        document.addEventListener('mousemove', mm);
        document.addEventListener('mouseup', mu);
      } else {
        const sl = absoluteValues[lk] || 0;
        const sr = absoluteValues[rk] || 0;
        const sum = sl + sr;
        if (sum === 0) return;
        const move = (cx) => {
          const pctDelta = ((cx - startX) / barW) * 100;
          const lShareNow = (sl / sum) * 100;
          const newLShare = Math.min(Math.max(lShareNow + pctDelta, 1), 99);
          const newL = Math.round((newLShare / 100) * sum);
          const newR = sum - newL;
          const u = { ...absoluteValues, [lk]: newL, [rk]: newR };
          setAbsoluteValues(u); emit(splits, u, customOpts);
        };
        const mm = ev => move(ev.clientX);
        const mu = () => { document.removeEventListener('mousemove', mm); document.removeEventListener('mouseup', mu); };
        document.addEventListener('mousemove', mm);
        document.addEventListener('mouseup', mu);
      }
    }

    function handleAddCustom() {
      const label = customInput.trim();
      if (!label) { setAddingCustom(false); setCustomInput(''); return; }
      const safeKey = 'custom_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
      if (!safeKey || safeKey === 'custom_') { setAddingCustom(false); setCustomInput(''); return; }
      if (allOpts.find(o => o.key === safeKey)) {
        setAddingCustom(false); setCustomInput(''); return; // silent dedupe
      }
      const newCustomOpts = [...customOpts, { key: safeKey, label }];
      setCustomOpts(newCustomOpts);
      if (mode === 'percentage') {
        const u = { ...splits, [safeKey]: 20 };
        setSplits(u); emit(u, absoluteValues, newCustomOpts);
      } else {
        const u = { ...absoluteValues, [safeKey]: 1000 };
        setAbsoluteValues(u); emit(splits, u, newCustomOpts);
      }
      setAddingCustom(false); setCustomInput('');
    }

    // ── Shared chip style ───────────────────────────────────────────
    const chipPillBase = {
      padding: '0.3rem 0.75rem',
      fontSize: '0.72rem',
      borderRadius: 20,
      background: 'white',
      color: '#555',
      cursor: 'pointer',
      fontFamily: 'inherit',
      border: '1.5px solid #e0e0e0',
      width: 'auto',
      whiteSpace: 'nowrap',
      transition: 'all 0.12s',
    };

    return (
      <div style={{ marginTop: '0.75rem' }}>
        {/* Mode toggle — only when allowAbsolute */}
        {config.allowAbsolute && (
          <div style={{ marginBottom: '0.9rem', display: 'inline-flex', border: '1px solid #e0e0e0', borderRadius: 6, padding: 2, background: '#f8f9fc' }}>
            <button
              type="button"
              onClick={() => switchMode('percentage')}
              style={{
                padding: '0.3rem 0.75rem', fontSize: '0.72rem', border: 'none', borderRadius: 4,
                background: mode === 'percentage' ? '#1D9E75' : 'transparent',
                color: mode === 'percentage' ? 'white' : '#666',
                cursor: 'pointer', fontWeight: 600, width: 'auto', fontFamily: 'inherit',
              }}>
              Percentages
            </button>
            <button
              type="button"
              onClick={() => switchMode('absolute')}
              style={{
                padding: '0.3rem 0.75rem', fontSize: '0.72rem', border: 'none', borderRadius: 4,
                background: mode === 'absolute' ? '#1D9E75' : 'transparent',
                color: mode === 'absolute' ? 'white' : '#666',
                cursor: 'pointer', fontWeight: 600, width: 'auto', fontFamily: 'inherit',
              }}>
              Amounts ({currencySymbol})
            </button>
          </div>
        )}

        {/* Visual bar with drag handles */}
        {(activeKeys.length > 0 || (mode === 'absolute' && absoluteTarget)) && (
          <div ref={barRef} style={{ display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0', userSelect: 'none', marginBottom: '0.75rem' }}>
            {activeKeys.map((key, idx) => {
              const pct = getDisplayPct(key);
              const color = getColor(idx);
              const opt = allOpts.find(o => o.key === key);
              return (
                <div key={key} style={{ width: `${pct}%`, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', minWidth: 0 }}>
                  {pct > 12 && (
                    <span style={{ fontSize: 10, fontWeight: 500, color: 'white', padding: '0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {opt?.label}
                    </span>
                  )}
                  {idx < activeKeys.length - 1 && (
                    <div
                      onMouseDown={e => startDrag(e, idx)}
                      style={{ position: 'absolute', top: 0, bottom: 0, right: -5, width: 10, cursor: 'col-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.3)' }}
                      title="Drag to adjust">
                      <div style={{ width: 2, height: 14, background: 'rgba(255,255,255,0.85)', borderRadius: 1 }} />
                    </div>
                  )}
                </div>
              );
            })}
            {/* Unallocated tail in absolute mode with known target */}
            {useTargetScale && unallocated > 0 && (
              <div style={{
                width: `${(unallocated / absoluteTarget) * 100}%`,
                background: 'repeating-linear-gradient(45deg, #f0f0f4, #f0f0f4 6px, #e6e6ec 6px, #e6e6ec 12px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 0, overflow: 'hidden'
              }}>
                {(unallocated / absoluteTarget) * 100 > 12 && (
                  <span style={{ fontSize: 10, fontWeight: 500, color: '#888', padding: '0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Unallocated
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-segment numeric inputs with coloured dot and remove × */}
        <div style={{ marginBottom: '0.9rem' }}>
          {activeKeys.map((key, idx) => {
            const opt = allOpts.find(o => o.key === key);
            const v = activeSplits[key] || 0;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: getColor(idx), flexShrink: 0 }} />
                <span style={{ fontSize: '0.75rem', color: '#1a1a2e', flex: 1, fontWeight: 500 }}>
                  {opt?.label || key}
                </span>
                {mode === 'absolute' && (
                  <span style={{ fontSize: '0.72rem', color: '#888' }}>{currencySymbol}</span>
                )}
                <input
                  type="number"
                  min="0"
                  max={mode === 'percentage' ? 100 : undefined}
                  step={mode === 'percentage' ? 1 : 100}
                  value={Math.round(v) || ''}
                  placeholder="0"
                  onChange={(e) => updateSplit(key, parseFloat(e.target.value) || 0)}
                  style={{ width: 64, fontSize: '0.75rem', fontWeight: 500, padding: '3px 6px', border: '0.5px solid #ddd', borderRadius: 5, textAlign: 'center', fontFamily: 'inherit' }}
                />
                {mode === 'percentage' && (
                  <span style={{ fontSize: '0.7rem', color: '#aaa', minWidth: 12 }}>%</span>
                )}
                {activeKeys.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeOption(key)}
                    style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer', fontSize: '0.95rem', width: 'auto', padding: '0 4px', lineHeight: 1 }}
                    title="Remove"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Chip-style pills: inactive categories + add-custom */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: '0.75rem' }}>
          {allOpts
            .filter(o => !activeKeys.includes(o.key))
            .map(o => (
              <button
                key={o.key}
                type="button"
                onClick={() => addOption(o.key)}
                style={chipPillBase}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.color = '#0F6E56'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.color = '#555'; }}
              >
                + {o.label}
              </button>
            ))}

          {allowCustom && (addingCustom ? (
            <input
              type="text"
              autoFocus
              value={customInput}
              onChange={e => setCustomInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleAddCustom(); }
                else if (e.key === 'Escape') { setAddingCustom(false); setCustomInput(''); }
              }}
              onBlur={handleAddCustom}
              placeholder="Category name…"
              style={{
                padding: '0.3rem 0.75rem', fontSize: '0.72rem', borderRadius: 20,
                border: '1.5px solid #1D9E75', outline: 'none', fontFamily: 'inherit',
                minWidth: 140,
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingCustom(true)}
              style={{ ...chipPillBase, borderStyle: 'dashed', color: '#888' }}
              title="Add your own category"
            >
              + Add category
            </button>
          ))}
        </div>

        {/* Total */}
        <div style={{ fontSize: '0.72rem', textAlign: 'right', lineHeight: 1.5 }}>
          {mode === 'percentage' ? (
            <span style={{ color: Math.round(total) === 100 ? '#0F6E56' : '#BA7517' }}>
              Total: {Math.round(total)}%{total !== 100 ? ' (aim for 100%)' : ' ✓'}
            </span>
          ) : absoluteTarget ? (
            <span style={{ color: over > 0 ? '#A32D2D' : unallocated > 0 ? '#666' : '#0F6E56' }}>
              Allocated: {currencySymbol}{total.toLocaleString()} of {currencySymbol}{absoluteTarget.toLocaleString()}
              {over > 0 && <> — {currencySymbol}{over.toLocaleString()} over</>}
              {unallocated > 0 && <> — {currencySymbol}{unallocated.toLocaleString()} unallocated</>}
              {over === 0 && unallocated === 0 && <> ✓</>}
            </span>
          ) : (
            <span style={{ color: '#666' }}>
              Total: {currencySymbol}{total.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── Shared chip styles for the new question component types ────────
  const newChipBase = {
    padding: '0.4rem 0.85rem',
    fontSize: '0.78rem',
    borderRadius: 20,
    border: '1.5px solid #e0e0e0',
    background: 'white',
    color: '#555',
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: 'auto',
    whiteSpace: 'nowrap',
    transition: 'all 0.12s',
  };
  const newChipSelected = {
    border: '1.5px solid #48B650',
    background: '#e6f5ec',
    color: '#185F2A',
    fontWeight: 600,
  };

  // Read the "active segments" from a prior split answer (used by ranked,
  // per_segment_multi, multi_with_text via depends_on). Returns an array of
  // { key, label }. Custom segments carry labels in the answer's customOpts.
  function getDependsOnSegments(field) {
    const prior = answers?.[field];
    if (!prior || typeof prior !== 'object') return [];
    const activeMap = prior.mode === 'absolute' ? prior.absoluteValues : prior.splits;
    if (!activeMap || typeof activeMap !== 'object') return [];
    const keys = Object.keys(activeMap);
    const customOpts = Array.isArray(prior.customOpts) ? prior.customOpts : [];
    const priorQuestion = questions.find(q => q.field === field);
    const presetOpts = priorQuestion && Array.isArray(priorQuestion.options) ? priorQuestion.options : [];
    const findLabel = (k) => {
      const c = customOpts.find(o => o.key === k);
      if (c) return c.label;
      const p = presetOpts.find(o => o && o.key === k);
      if (p) return p.label;
      return k;
    };
    return keys.map(k => ({ key: k, label: findLabel(k) }));
  }

  // 5.1 — choice_with_input. Choice chips; selected option expands to show
  // an inline input. Detects "[__%]" or "[__]" placeholder in option labels.
  function ChoiceWithInput({ question, value, onChange }) {
    const v = value && typeof value === 'object' ? value : { choice: '', input_value: '' };
    const choice = v.choice || '';
    const inputValue = v.input_value || '';
    function selectChoice(opt) {
      onChange({ choice: opt, input_value: opt === choice ? inputValue : '' });
    }
    function updateInput(text) {
      onChange({ choice, input_value: text });
    }
    return (
      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {(question.options || []).map((opt) => {
          const isSelected = choice === opt;
          const hasPlaceholder = /\[__%?\]/.test(opt);
          const cleanLabel = opt.replace(/\s*[—-]\s*\[__%?\]\s*/, '').replace(/\s*\[__%?\]\s*/, '').trim();
          const isPct = /%/.test(opt);
          return (
            <React.Fragment key={opt}>
              <button type="button" onClick={() => selectChoice(opt)}
                style={{ ...newChipBase, ...(isSelected ? newChipSelected : {}) }}>
                {cleanLabel}
              </button>
              {isSelected && hasPlaceholder && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input type="number" value={inputValue}
                    onChange={(e) => updateInput(e.target.value)}
                    placeholder={isPct ? '%' : ''}
                    style={{
                      width: 70, padding: '0.3rem 0.5rem', fontSize: '0.85rem',
                      border: '1px solid #ccc', borderRadius: 6, fontFamily: 'inherit',
                    }} />
                  {isPct && <span style={{ color: '#888', fontSize: '0.8rem' }}>%</span>}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // 5.2 — rated_list. Editable list of labelled items; each carries a rating.
  // initialItems comes from canvas prefill. Value shape: [{ label, rating }].
  function RatedList({ question, value, onChange, initialItems }) {
    const ratingOptions = Array.isArray(question.rating_options) && question.rating_options.length
      ? question.rating_options
      : ['High margin', 'Medium margin', 'Low margin', 'Loss-making', "Don't know"];
    const rows = Array.isArray(value) && value.length
      ? value
      : (Array.isArray(initialItems) && initialItems.length
          ? initialItems.map(label => ({ label, rating: '' }))
          : [{ label: '', rating: '' }]);
    function setRow(i, patch) {
      const next = rows.map((r, idx) => idx === i ? { ...r, ...patch } : r);
      onChange(next);
    }
    function addRow() {
      onChange([...rows, { label: '', rating: '' }]);
    }
    function removeRow(i) {
      const next = rows.filter((_, idx) => idx !== i);
      onChange(next.length ? next : [{ label: '', rating: '' }]);
    }
    return (
      <div style={{ marginTop: '0.5rem' }}>
        {rows.map((row, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
            marginBottom: 8, padding: '0.5rem 0.6rem',
            background: '#f8f9fc', border: '1px solid #e0e0e0', borderRadius: 8,
          }}>
            <input type="text" value={row.label}
              onChange={(e) => setRow(i, { label: e.target.value })}
              placeholder="Name a product, service or customer type"
              style={{
                flex: '1 1 200px', minWidth: 160,
                padding: '0.35rem 0.6rem', fontSize: '0.82rem',
                border: '1px solid #d0d0d6', borderRadius: 6, fontFamily: 'inherit',
              }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {ratingOptions.map(r => {
                const isSel = row.rating === r;
                return (
                  <button key={r} type="button" onClick={() => setRow(i, { rating: r })}
                    style={{ ...newChipBase, ...(isSel ? newChipSelected : {}), padding: '0.3rem 0.65rem', fontSize: '0.72rem' }}>
                    {r}
                  </button>
                );
              })}
            </div>
            {rows.length > 1 && (
              <button type="button" onClick={() => removeRow(i)} title="Remove"
                style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer', fontSize: '1rem', width: 'auto', padding: '0 4px' }}>
                ×
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={addRow}
          style={{ ...newChipBase, borderStyle: 'dashed', color: '#888' }}>
          + Add item
        </button>
      </div>
    );
  }

  // 5.3 — process_matrix. 2–5 rows, each with 6 inputs.
  function ProcessMatrix({ value, onChange }) {
    const MIN_ROWS = 2;
    const MAX_ROWS = 5;
    const emptyRow = { name: '', repeatability: '', volume: '', error_frequency: '', time_intensity: '', customer_touchpoint: '' };
    const rows = Array.isArray(value) && value.length >= MIN_ROWS
      ? value
      : [emptyRow, emptyRow];
    function setRow(i, patch) {
      const next = rows.map((r, idx) => idx === i ? { ...r, ...patch } : r);
      onChange(next);
    }
    function addRow() {
      if (rows.length >= MAX_ROWS) return;
      onChange([...rows, emptyRow]);
    }
    function removeRow(i) {
      if (rows.length <= MIN_ROWS) return;
      onChange(rows.filter((_, idx) => idx !== i));
    }
    const Group = ({ field, label, opts, row, i }) => (
      <div style={{ marginTop: 6 }}>
        <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: 3, fontWeight: 500 }}>{label}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {opts.map(o => {
            const isSel = row[field] === o;
            return (
              <button key={o} type="button" onClick={() => setRow(i, { [field]: o })}
                style={{ ...newChipBase, ...(isSel ? newChipSelected : {}), padding: '0.25rem 0.55rem', fontSize: '0.7rem' }}>
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
    return (
      <div style={{ marginTop: '0.5rem' }}>
        {rows.map((row, i) => (
          <div key={i} style={{
            marginBottom: 12, padding: '0.7rem 0.8rem',
            background: '#f8f9fc', border: '1px solid #e0e0e0', borderRadius: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="text" value={row.name}
                onChange={(e) => setRow(i, { name: e.target.value })}
                placeholder={`Process ${i + 1} name`}
                style={{
                  flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.85rem',
                  border: '1px solid #d0d0d6', borderRadius: 6, fontFamily: 'inherit',
                }} />
              {rows.length > MIN_ROWS && (
                <button type="button" onClick={() => removeRow(i)} title="Remove"
                  style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer', fontSize: '1rem', width: 'auto', padding: '0 4px' }}>×</button>
              )}
            </div>
            <Group field="repeatability" label="Repeatability"
              opts={['Highly repeatable', 'Mostly repeatable', 'Variable', 'Bespoke']} row={row} i={i} />
            <Group field="volume" label="Volume"
              opts={['High', 'Medium', 'Low']} row={row} i={i} />
            <Group field="error_frequency" label="Error frequency"
              opts={['Rare', 'Occasional', 'Frequent']} row={row} i={i} />
            <Group field="time_intensity" label="Time intensity"
              opts={['Quick', 'Medium', 'Significant', 'Time-intensive']} row={row} i={i} />
            <Group field="customer_touchpoint" label="Customer touchpoint"
              opts={['Yes', 'No']} row={row} i={i} />
          </div>
        ))}
        {rows.length < MAX_ROWS && (
          <button type="button" onClick={addRow}
            style={{ ...newChipBase, borderStyle: 'dashed', color: '#888' }}>
            + Add process
          </button>
        )}
      </div>
    );
  }

  // 5.4 — ranked. Drag-to-reorder list. Items pulled from depends_on.
  function Ranked({ question, value, onChange }) {
    const segments = getDependsOnSegments(question.depends_on);
    const segmentKeys = segments.map(s => s.key);
    const initialOrder = Array.isArray(value) && value.length
      ? value.filter(k => segmentKeys.includes(k)).concat(segmentKeys.filter(k => !value.includes(k)))
      : segmentKeys;
    const order = initialOrder;
    const dragKeyRef = useRef(null);

    function move(fromIdx, toIdx) {
      if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || toIdx >= order.length) return;
      const next = [...order];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      onChange(next);
    }
    if (!segments.length) {
      return <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.4rem' }}>Answer the previous question first.</div>;
    }
    return (
      <div style={{ marginTop: '0.5rem' }}>
        {order.map((key, idx) => {
          const seg = segments.find(s => s.key === key);
          if (!seg) return null;
          return (
            <div key={key}
              draggable
              onDragStart={() => { dragKeyRef.current = key; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const from = order.indexOf(dragKeyRef.current);
                const to = idx;
                if (from !== -1) move(from, to);
                dragKeyRef.current = null;
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '0.55rem 0.75rem', marginBottom: 6,
                background: '#f8f9fc', border: '1px solid #e0e0e0', borderRadius: 8,
                cursor: 'grab',
              }}>
              <span style={{ color: '#aaa', fontSize: '1rem', userSelect: 'none' }}>⋮⋮</span>
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: '#48B650', color: 'white', fontSize: '0.75rem', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{idx + 1}</span>
              <span style={{ fontSize: '0.85rem', color: '#1a1a2e', flex: 1 }}>{seg.label}</span>
              <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0}
                title="Move up"
                style={{
                  border: 'none', background: 'none', color: idx === 0 ? '#ddd' : '#666',
                  cursor: idx === 0 ? 'default' : 'pointer', fontSize: '0.9rem',
                  width: 'auto', padding: '0 4px',
                }}>▲</button>
              <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === order.length - 1}
                title="Move down"
                style={{
                  border: 'none', background: 'none', color: idx === order.length - 1 ? '#ddd' : '#666',
                  cursor: idx === order.length - 1 ? 'default' : 'pointer', fontSize: '0.9rem',
                  width: 'auto', padding: '0 4px',
                }}>▼</button>
            </div>
          );
        })}
      </div>
    );
  }

  // 5.5 — per_segment_multi. One row per segment from depends_on; each row is
  // a multi-choice chip strip. Value: { segment_key: ['option', ...] }.
  function PerSegmentMulti({ question, value, onChange }) {
    const segments = getDependsOnSegments(question.depends_on);
    const opts = Array.isArray(question.options) ? question.options : [];
    const current = value && typeof value === 'object' ? value : {};
    function togglePick(segKey, opt) {
      const picks = Array.isArray(current[segKey]) ? current[segKey] : [];
      const next = picks.includes(opt) ? picks.filter(p => p !== opt) : [...picks, opt];
      onChange({ ...current, [segKey]: next });
    }
    if (!segments.length) {
      return <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.4rem' }}>Answer the previous question first.</div>;
    }
    return (
      <div style={{ marginTop: '0.5rem' }}>
        {segments.map(seg => {
          const picks = Array.isArray(current[seg.key]) ? current[seg.key] : [];
          return (
            <div key={seg.key} style={{
              marginBottom: 10, padding: '0.6rem 0.75rem',
              background: '#f8f9fc', border: '1px solid #e0e0e0', borderRadius: 8,
            }}>
              <div style={{ fontSize: '0.82rem', color: '#1a1a2e', fontWeight: 600, marginBottom: 6 }}>{seg.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {opts.map(opt => {
                  const isSel = picks.includes(opt);
                  return (
                    <button key={opt} type="button" onClick={() => togglePick(seg.key, opt)}
                      style={{ ...newChipBase, ...(isSel ? newChipSelected : {}), padding: '0.3rem 0.65rem', fontSize: '0.72rem' }}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // 5.6 — multi_with_text. Chips drawn from depends_on segments + optional
  // "Tell me more" textarea. Value: { selected: [...], detail: 'string' }.
  function MultiWithText({ question, value, onChange }) {
    const segments = getDependsOnSegments(question.depends_on);
    const v = value && typeof value === 'object' ? value : { selected: [], detail: '' };
    const selected = Array.isArray(v.selected) ? v.selected : [];
    const detail = v.detail || '';
    function toggle(key) {
      const next = selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key];
      onChange({ selected: next, detail });
    }
    function setDetail(text) {
      onChange({ selected, detail: text });
    }
    if (!segments.length) {
      return <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.4rem' }}>Answer the previous question first.</div>;
    }
    return (
      <div style={{ marginTop: '0.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {segments.map(seg => {
            const isSel = selected.includes(seg.key);
            return (
              <button key={seg.key} type="button" onClick={() => toggle(seg.key)}
                style={{ ...newChipBase, ...(isSel ? newChipSelected : {}) }}>
                {seg.label}
              </button>
            );
          })}
        </div>
        <textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={2}
          placeholder="Tell me more (optional)…"
          style={{ width: '100%', fontFamily: 'inherit' }}
        />
      </div>
    );
  }

  // 5.7 — conditional_text. Renders a textarea after a choice question when
  // q.conditional_text === 'always'. Combined value: { choice, conditional_text }.
  function ChoiceWithConditionalText({ question, value, onChange }) {
    const v = value && typeof value === 'object' ? value : { choice: '', conditional_text: '' };
    const choice = v.choice || '';
    const ct = v.conditional_text || '';
    return (
      <div style={{ marginTop: '0.4rem' }}>
        <select value={choice}
          onChange={(e) => onChange({ choice: e.target.value, conditional_text: ct })}>
          <option value=''>Select an option...</option>
          {(question.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {question.has_other_input && choice === 'Other' && (
          <input type="text"
            value={v.other_input || ''}
            onChange={(e) => onChange({ choice, conditional_text: ct, other_input: e.target.value })}
            placeholder="Please specify…"
            style={{
              marginTop: '0.4rem', width: '100%', padding: '0.4rem 0.6rem',
              fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 6, fontFamily: 'inherit',
            }} />
        )}
        <div style={{ marginTop: '0.5rem' }}>
          {question.conditional_text_label && (
            <div style={{ fontSize: '0.78rem', color: '#444', marginBottom: 4, fontStyle: 'italic' }}>
              {question.conditional_text_label}
            </div>
          )}
          <textarea
            value={ct}
            onChange={(e) => onChange({ choice, conditional_text: e.target.value, ...(v.other_input ? { other_input: v.other_input } : {}) })}
            rows={2}
            placeholder="Add detail…"
            style={{ width: '100%', fontFamily: 'inherit' }}
          />
        </div>
      </div>
    );
  }

  // Choice with optional "Other" inline input. Used when q.has_other_input is
  // true and there is no conditional_text on the question.
  function ChoiceWithOtherInput({ question, value, onChange }) {
    const v = value && typeof value === 'object' ? value : { choice: typeof value === 'string' ? value : '', other_input: '' };
    const choice = v.choice || '';
    const other = v.other_input || '';
    return (
      <div style={{ marginTop: '0.4rem' }}>
        <select value={choice}
          onChange={(e) => onChange({ choice: e.target.value, other_input: e.target.value === 'Other' ? other : '' })}>
          <option value=''>Select an option...</option>
          {(question.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {choice === 'Other' && (
          <input type="text"
            value={other}
            onChange={(e) => onChange({ choice, other_input: e.target.value })}
            placeholder="Please specify…"
            style={{
              marginTop: '0.4rem', width: '100%', padding: '0.4rem 0.6rem',
              fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: 6, fontFamily: 'inherit',
            }} />
        )}
      </div>
    );
  }

  // Multi-choice chip strip — value is an array of selected options.
  function MultiChoice({ question, value, onChange }) {
    const selected = Array.isArray(value) ? value : (value && Array.isArray(value.selected) ? value.selected : []);
    function toggle(opt) {
      const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
      onChange(next);
    }
    return (
      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {(question.options || []).map(opt => {
          const isSel = selected.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              style={{ ...newChipBase, ...(isSel ? newChipSelected : {}) }}>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  // Save key state to localStorage
  const saveState = () => {
    try {
      const stateToSave = {
        step,
        url,
        challenge,
        canvas,
        questions,
        answers,
        lens,
        result,
        decisionId,
        isConfirmed,
        userOptions,
        criteriaWeights,
        customCriterion,
        prefillSources,
        relationshipType,
        preLensSkipped,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(`guruone_${userKey}`, JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save Decision Guru state:', error);
    }
  };

  // Auto-save state when key values change
  useEffect(() => {
    const hasProgress = challenge.trim().length > 0
      || result !== null
      || Object.keys(answers).length > 0
      || isConfirmed;
    if (hasProgress) {
      saveState();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, canvas, result, answers, criteriaWeights, userOptions, challenge, url, lens, decisionId, isConfirmed, prefillSources, relationshipType, preLensSkipped]);

  const clearDecisionGuruState = () => {
    // Remove the persisted key first, then reload immediately.
    // Do NOT set any React state here — any setState call would trigger
    // the auto-save useEffect and re-write to localStorage before the
    // reload fires, causing the "continue session" banner to reappear.
    localStorage.removeItem(`guruone_${userKey}`);
    window.location.reload();
  };

  // Canvas-aware launch: on first mount, if the user has either a
  // Direction or Design canvas already past 50% complete, populate the
  // Decision Guru canvas state silently from that data and jump straight
  // to the challenge step. The user never sees the URL-input start
  // screen if they have already invested in their canvases.
  useEffect(() => {
    // Canvas and canvasFromExisting are now initialised synchronously above.
    // This effect only needs to set the splits for the low-context default path.
    if (!_initialCanvasFromExisting && !persistedState?.canvas) {
      setSplits({
        revenue_model: parseSplitValue('revenue_model', 'project-based'),
        go_to_market: parseSplitValue('go_to_market', 'founder-led'),
        delivery_model: parseSplitValue('delivery_model', 'people-led'),
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-hydration backstop. Canvas state lives on the server now and
  // localStorage is just a per-browser cache. If the leader arrives at
  // Decision Guru first after a fresh login (cache empty), the synchronous
  // localStorage read above sees nothing and falls back to the empty
  // canvas — even though their Direction / Design canvases are populated
  // server-side. This effect fixes that race by fetching from the canvas-
  // state endpoints, writing the result back to localStorage (so every
  // downstream code path that still reads cache works), and rebuilding
  // the Decision Guru canvas state from those values. Runs once on mount.
  useEffect(() => {
    let cancelled = false;
    // Skip if we already hydrated from localStorage or are resuming a
    // persisted Decision Guru session — both paths already have data.
    if (_initialCanvasFromExisting || persistedState?.canvas) return undefined;

    (async () => {
      try {
        const token = localStorage.getItem('guruone_token');
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const apiRoot = API.replace('/canvas', '');
        const [stratRes, omRes] = await Promise.all([
          axios.get(`${apiRoot}/canvas-state/strategy`, { headers }).catch(() => null),
          axios.get(`${apiRoot}/canvas-state/operating_model`, { headers }).catch(() => null),
        ]);
        if (cancelled) return;
        const stratData = stratRes?.data?.data || null;
        const omData = omRes?.data?.data || null;
        const stratHas = stratData && Object.keys(stratData).length > 0;
        const omHas = omData && Object.keys(omData).length > 0;
        if (!stratHas && !omHas) return;

        // Cache write — match the storage shapes documented in the
        // handover so other readers (Ask GuruOne, One-pager, etc.) work.
        try {
          if (stratHas) {
            const u = JSON.parse(localStorage.getItem('guruone_user') || 'null');
            const userKey = u?.id ? `user_${u.id}` : 'default';
            const existing = (() => {
              try { return JSON.parse(localStorage.getItem('guruone_strategy') || '{}'); }
              catch { return {}; }
            })();
            existing[userKey] = { ...stratData, lastUpdated: new Date().toISOString() };
            localStorage.setItem('guruone_strategy', JSON.stringify(existing));
          }
          if (omHas) {
            localStorage.setItem('guruone_operating_model', JSON.stringify(omData));
          }
        } catch (cacheErr) {
          console.warn('Decision Guru cache write failed:', cacheErr.message);
        }

        const sp = calcStrategyPctRaw(stratHas ? stratData : null);
        const bp = calcBlueprintPctRaw(omHas ? omData : null);
        if (sp >= 50 || bp >= 50) {
          const built = buildCanvasFromCanvases(
            stratHas ? stratData : null,
            omHas ? omData : null,
          );
          setCanvas(built);
          setCanvasFromExisting(true);
        }
      } catch (err) {
        console.warn('Decision Guru server hydration failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-clear the "context applied" toast after a few seconds.
  useEffect(() => {
    if (!contextAppliedToast) return undefined;
    const t = setTimeout(() => setContextAppliedToast(null), 6000);
    return () => clearTimeout(t);
  }, [contextAppliedToast]);

  // Additively merge the upload's nested Direction-canvas sections into
  // the server-side Direction canvas state. Only fills empty fields and
  // empty repeatable-section entries; preserves any existing values,
  // _explore narratives, _evaluation scores and _confidence ratings.
  // Returns the count of fields/entries newly added (0 on no-op or any
  // failure — always non-blocking for the local Decision Guru flow).
  async function mergeUploadedIntoDirectionCanvas(sections) {
    if (!sections || typeof sections !== 'object') return 0;
    try {
      const apiRoot = API.replace('/canvas', '');
      const current = await axios.get(`${apiRoot}/canvas-state/strategy`);
      const existing = current?.data?.data || {};
      const merged = JSON.parse(JSON.stringify(existing));
      let added = 0;

      const isEmpty = (v) =>
        v === null || v === undefined || v === '' ||
        (Array.isArray(v) && v.length === 0);

      for (const sectionKey of Object.keys(sections)) {
        const incoming = sections[sectionKey];
        if (!incoming || typeof incoming !== 'object') continue;
        if (!merged[sectionKey]) merged[sectionKey] = {};
        const existingSection = merged[sectionKey];

        // Repeatable sections (customers, differentiators, values, etc.)
        // arrive as { entries: [...] }. Only populate if existing is empty.
        if (Array.isArray(incoming.entries)) {
          if (isEmpty(existingSection.entries)) {
            existingSection.entries = incoming.entries;
            added += incoming.entries.length;
          }
          continue;
        }

        for (const fieldKey of Object.keys(incoming)) {
          if (fieldKey.startsWith('_')) continue; // preserve metadata
          const incomingVal = incoming[fieldKey];
          if (incomingVal === null || incomingVal === undefined || incomingVal === '') continue;
          if (isEmpty(existingSection[fieldKey])) {
            existingSection[fieldKey] = incomingVal;
            added += 1;
          }
        }
      }

      if (added > 0) {
        await axios.put(`${apiRoot}/canvas-state/strategy`, {
          data: merged,
          origin: 'decision_guru_upload_spillover'
        });
        // Refresh the localStorage cache so other readers (Ask GuruOne,
        // One-pager, Direction canvas mount) pick the new values up
        // without waiting for a server hydration round-trip.
        try {
          const u = JSON.parse(localStorage.getItem('guruone_user') || 'null');
          const userKey = u?.id ? `user_${u.id}` : 'default';
          const cached = (() => {
            try { return JSON.parse(localStorage.getItem('guruone_strategy') || '{}'); }
            catch { return {}; }
          })();
          cached[userKey] = { ...merged, lastUpdated: new Date().toISOString() };
          localStorage.setItem('guruone_strategy', JSON.stringify(cached));
        } catch (cacheErr) {
          console.warn('Direction canvas cache refresh failed:', cacheErr.message);
        }
      }
      return added;
    } catch (err) {
      console.warn('Direction canvas spillover failed:', err.message);
      return 0;
    }
  }

  // Merge the structured sections returned by /api/upload/document into
  // the Decision Guru canvas state. Additive: only fills fields that are
  // currently empty, so a previous "Add context" or an earlier canvas
  // can't be silently overwritten. Returns the count of fields applied
  // so the success toast can be specific.
  function applyUploadedSections(sections) {
    if (!sections || typeof sections !== 'object') return 0;
    const built = buildCanvasFromCanvases(sections, null);
    const isEmpty = (v) =>
      v === null || v === undefined || v === '' ||
      (Array.isArray(v) && v.length === 0);
    let applied = 0;
    setCanvas(prev => {
      const next = { ...prev };
      for (const key of Object.keys(built)) {
        const incoming = built[key];
        if (incoming === undefined || incoming === null) continue;
        if (isEmpty(next[key])) {
          // Skip baked-in default values that would otherwise count as
          // "populated" and block additive writes for fields the leader
          // hasn't actually filled in. Match the empty-canvas defaults.
          next[key] = incoming;
          applied += 1;
        } else if (key === 'price_positioning' && next[key] === 'mid-market' && incoming) {
          next[key] = incoming;
          applied += 1;
        } else if (key === 'primary_acquisition_channel' && next[key] === 'direct' && incoming) {
          next[key] = incoming;
          applied += 1;
        } else if (key === 'revenue_model' && next[key] === 'project-based' && incoming) {
          next[key] = incoming;
          applied += 1;
        } else if (key === 'sales_approach' && next[key] === 'founder-led' && incoming) {
          next[key] = incoming;
          applied += 1;
        }
      }
      return next;
    });
    return applied;
  }

  const handleDownloadDecision = () => {
    if (!result) return;
    alert('Coming soon — decision summary download is in development.');
  };

  // Pre-populate challenge if navigated from dashboard or strategy canvas.
  // When a challenge arrives via route-state, treat it as a new decision:
  // preserve the canvas (so we don't need to re-scan the business) but
  // clear stale answers/result so the user lands on a fresh challenge
  // screen instead of a leftover result view.
  // Rehydration guard: if we land on the clarify step with no questions in
  // memory (e.g. user reloaded mid-clarify and the auto-save persisted step
  // = 'clarifying' but we don't persist clarify state), bounce back to
  // questions so the business leader isn't stuck on a blank card.
  useEffect(() => {
    if (step === 'clarifying' && clarifyQuestions.length === 0) {
      setStep('questions');
    }
  }, [step, clarifyQuestions.length]);

  // Auto-dismiss the canvas back-sync toast after ~12s so it stays
  // non-intrusive without forcing the leader to click ×.
  useEffect(() => {
    if (!syncToast?.visible) return;
    const t = setTimeout(() => setSyncToast(prev => prev ? { ...prev, visible: false } : null), 12000);
    return () => clearTimeout(t);
  }, [syncToast?.visible]);

  useEffect(() => {
    if (location.state?.challenge) {
      setChallenge(location.state.challenge);
      setAnswers({});
      setResult(null);
      setDecisionId(null);
      setIsConfirmed(false);
      setUserOptions(['', '']);
      setCriteriaWeights({});
      setCustomCriterion('');
      setRevisedResult(null);
      setHowCache({});
      setDiscardedOptions({});
      // Jump to challenge step if we have a canvas, otherwise stay at start
      setStep(persistedState?.canvas ? 'challenge' : 'start');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function getCanvasValue(field) {
    return canvasEdits[field] !== undefined ? canvasEdits[field] : (canvas ? canvas[field] : '');
  }

  function applyCanvasEdits() {
    if (Object.keys(canvasEdits).length > 0) {
      setCanvas({ ...canvas, ...canvasEdits });
      setCanvasEdits({});
    }
    setEditingSection(null);
  }

  function canvasWithSplits() {
    return {
      ...canvas,
      ...canvasEdits,
      revenue_model: splitToString('revenue_model', splits.revenue_model),
      go_to_market: splitToString('go_to_market', splits.go_to_market),
      delivery_model: splitToString('delivery_model', splits.delivery_model),
    };
  }

  // Build the user-facing challenge with any uploaded-document context
  // appended so the LLM has the extra detail without changing the
  // value the user sees in the textarea.
  const buildChallengeWithContext = () => {
    if (!uploadedDocText) return challenge;
    return `${challenge}\n\n[Additional context from uploaded documents:]\n${uploadedDocText}`;
  };

  async function handleChallenge() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/questions`, { canvas: canvasWithSplits(), userInput: buildChallengeWithContext() });
      const qs = res.data.questionSet.questions || [];
      const primaryLens = res.data.questionSet.primary_lens;
      const narrativePrefills = res.data.questionSet.narrative_prefills || {};
      const parsedRelationshipType = res.data.questionSet.relationship_type || null;

      setQuestions(qs);
      setLens(primaryLens);

      // Apply pre-fills: canvas first, then narrative on top (narrative wins
      // for the same field, because the leader has just told us about it).
      // Existing answers also win — never overwrite something the user
      // already typed into a previous attempt.
      const canvasPrefills = computeCanvasPrefills(qs);
      const seedAnswers = { ...answers };
      const seedSources = { ...prefillSources };
      for (const [field, val] of Object.entries(canvasPrefills)) {
        if (seedAnswers[field] === undefined || seedAnswers[field] === '' || seedAnswers[field] === null) {
          seedAnswers[field] = val;
          seedSources[field] = 'canvas';
        }
      }
      for (const [field, entry] of Object.entries(narrativePrefills)) {
        if (!entry || entry.value == null) continue;
        if (seedAnswers[field] === undefined || seedAnswers[field] === '' || seedAnswers[field] === null
            || seedSources[field] === 'canvas') {
          seedAnswers[field] = entry.value;
          seedSources[field] = 'narrative';
        }
      }
      setAnswers(seedAnswers);
      setPrefillSources(seedSources);
      setRelationshipType(parsedRelationshipType);
      setMakeVsBuyConfirmDismissed(false);

      setStep('questions');
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not generate questions');
    }
    setLoading(false);
  }

  // Pull the per-user strategy context once, used by both the clarify probe
  // and the recommendation call. Falls back to localStorage when no route
  // state was passed — same scheme StrategyCanvas writes under.
  function buildBaseStrategyContext() {
    return routeStrategyContext || (() => {
      try {
        const raw = localStorage.getItem('guruone_strategy');
        if (!raw) return '';
        const all = JSON.parse(raw);
        const strategyKey = user?.id ? `user_${user.id}` : 'default';
        return formatStrategyContext(all[strategyKey] || {});
      } catch { return ''; }
    })();
  }

  // Format accumulated clarification Q&A as a context block to prepend to
  // the strategy context passed to /recommend. Mirrors the backend helper.
  function formatClarificationsContextFE(clarifications) {
    if (!clarifications || clarifications.length === 0) return '';
    const lines = ['Business leader follow-up clarifications:'];
    for (const c of clarifications) {
      lines.push(`Q: ${c.question}`);
      lines.push(`A: ${c.answer}`);
      lines.push('');
    }
    return lines.join('\n').trim();
  }

  // Build a lens-specific framing block for the recommendation prompt.
  // Includes consulting approach, key outputs, capital-allocation
  // values/mission cross-reference, and people_decisions branching +
  // employment law callout. Returned as a single text block ready to be
  // appended to strategyContext.
  function buildLensFramingContext(lensKey, ans, canvasObj, narrativeSources, relType) {
    const framing = LENS_FRAMING[lensKey] || LENS_FRAMING.multi;
    const lensLabel = lensDisplay(lensKey).label;
    const roleLabel = getRoleLabel(user);
    const lines = [];

    lines.push(`──────────────────────────────────────────────`);
    lines.push(`LENS FRAMING — ${lensLabel}`);
    lines.push(`──────────────────────────────────────────────`);
    // Personalisation — the user has chosen how they're addressed. Use their
    // role term consistently in the recommendation output. Default is
    // "business leader" when they haven't set one.
    if (roleLabel && roleLabel !== DEFAULT_ROLE_LABEL) {
      lines.push(`User addresses themselves as: ${roleLabel}. Use this term throughout the recommendation when referring to the user (in place of "founder" / "business leader" / "leader").`);
    }
    lines.push(`Core question: ${framing.core_question}`);
    lines.push(`Consulting approach: ${framing.consulting_approach}`);
    lines.push(`Framing: ${framing.framing}`);
    lines.push(`Key outputs to produce: ${framing.key_outputs}`);

    // ── capital_allocation: explicit values + mission cross-reference ──
    if (lensKey === 'capital_allocation') {
      const sd = readUserScopedCanvas('guruone_strategy');
      const valuesEntries = sd?.values?.entries || [];
      const valuesList = valuesEntries
        .map(v => v?.value || v?.name || v?.label)
        .filter(Boolean);
      const mission = sd?.mission?.mission_statement || sd?.mission?.statement || sd?.mission?.mission || '';
      const vision = sd?.vision?.vision_statement || sd?.vision?.statement || sd?.vision?.vision || canvasObj?.vision || '';

      lines.push('');
      lines.push('VALUES & MISSION CROSS-REFERENCE — MANDATORY:');
      if (valuesList.length) {
        lines.push(`- The leader's stated values: ${valuesList.join(', ')}`);
      } else {
        lines.push('- The leader has not yet captured values in the Direction canvas.');
      }
      if (mission) {
        lines.push(`- Mission: "${mission}"`);
      } else {
        lines.push('- Mission has not yet been captured.');
      }
      if (vision) {
        lines.push(`- Vision: "${vision}"`);
      }
      lines.push('For EACH investment option, explicitly check whether it aligns with or contradicts the values and mission above.');
      lines.push("If an option contradicts a stated value or the mission, surface the tension clearly in the recommendation — do not bury it. Leaders frequently don't see this tension until it is pointed out.");
    }

    // ── people_decisions: branching + employment law ──
    if (lensKey === 'people_decisions') {
      const coreConcernAnswer = ans?.core_concern;
      const concern = (coreConcernAnswer && typeof coreConcernAnswer === 'object')
        ? coreConcernAnswer.choice
        : coreConcernAnswer;
      const branch = concern ? PEOPLE_DECISIONS_BRANCHES[concern] : null;

      lines.push('');
      lines.push('PEOPLE_DECISIONS BRANCHING:');
      if (relType) {
        lines.push(`- Relationship type (parsed from challenge): ${relType}`);
      }
      if (branch) {
        lines.push(`- Core concern: ${branch.label}`);
        lines.push(`- Recommendation structure for this branch: ${branch.structure}`);
      } else if (concern) {
        lines.push(`- Core concern (not matched to a standard branch): ${concern}`);
        lines.push('- Use general crucial-conversations methodology, then signpost HR / employment law support.');
      } else {
        lines.push('- Core concern not yet specified. Diagnose from the narrative + answers, then apply the matching branch (underperformance / behaviour / role misalignment / retention risk / end relationship).');
      }

      lines.push('');
      lines.push('EMPLOYMENT LAW CALLOUT — MANDATORY:');
      lines.push('Every recommendation MUST include this callout prominently (do not hide it in a footnote):');
      lines.push('  "⚠️ Any recommendation involving ending employment, changing terms, or formal performance management must be implemented in line with UK employment law. Treat the suggestions here as a starting point, not a substitute for HR or employment law guidance."');
      const hrSupport = ans?.hr_support;
      if (typeof hrSupport === 'string' && hrSupport.startsWith('No')) {
        lines.push('The leader has indicated they do not have HR / legal support — STRONGLY recommend they engage an employment law specialist or HR consultant BEFORE acting on any termination or formal-performance-management recommendation.');
      }
    }

    // ── narrative + canvas prefill summary ──
    if (narrativeSources && Object.keys(narrativeSources).length) {
      const canvasFields = [];
      const narrativeFields = [];
      Object.entries(narrativeSources).forEach(([f, src]) => {
        if (typeof src !== 'string') return;
        if (src.startsWith('canvas'))    canvasFields.push(f);
        if (src.startsWith('narrative')) narrativeFields.push(f);
      });
      if (canvasFields.length || narrativeFields.length) {
        lines.push('');
        lines.push('PRE-FILL PROVENANCE (treat these as full answers, not blanks):');
        if (canvasFields.length)    lines.push(`- Pre-filled from canvas: ${canvasFields.join(', ')}`);
        if (narrativeFields.length) lines.push(`- Pre-filled from leader's challenge narrative: ${narrativeFields.join(', ')}`);
      }
    }

    return lines.join('\n');
  }

  async function runRecommendation(clarifications) {
    setLoading(true);
    setBuildingRecommendation(true);
    setError('');
    try {
      const enrichedAnswers = { ...answers, key_challenges: challenge };
      const filteredOptions = userOptions.filter(o => o.trim() !== '');
      const criteriaString = buildCriteriaString();
      const baseContext = buildBaseStrategyContext();
      const clarifyContext = formatClarificationsContextFE(clarifications);
      const framingContext = buildLensFramingContext(lens, enrichedAnswers, canvasWithSplits(), prefillSources, relationshipType);
      const strategyContext = [framingContext, clarifyContext, baseContext]
        .filter(Boolean)
        .join('\n\n');
      const res = await axios.post(`${API}/recommend`, {
        canvas: canvasWithSplits(), answers: enrichedAnswers, lens, userInput: buildChallengeWithContext(), url,
        userOptions: filteredOptions,
        criteriaWeights: criteriaString || null,
        strategyContext: strategyContext || null,
        userId: user?.id || null,
        relationshipType: lens === 'people_decisions' ? relationshipType : null,
        prefillSources,
      });
      const cddd = res.data.result.cddd;
      setResult(cddd);
      setDecisionId(res.data.result.decisionId || null);
      setIsConfirmed(false); // New recommendation resets confirmation state
      setChatOpen(false);
      setChatMessages([]);
      setRevisedResult(null);
      setHowOption(null);
      setStep('result');
      track('decision_recommendation_generated', { lens });
      // Section 12 — write resolvable answers back to the canvases.
      // Additive only; only newly-filled fields surface in the toast.
      try {
        const syncedFields = syncAnswersToCanvases(enrichedAnswers);
        if (syncedFields.length > 0) {
          setSyncToast({ synced: syncedFields, visible: true });
          track('decision_canvas_sync', { lens, count: syncedFields.length });
        }
      } catch (syncErr) {
        console.warn('Canvas sync failed:', syncErr);
      }
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not generate recommendation');
    }
    setLoading(false);
    setBuildingRecommendation(false);
  }

  async function handleAnswers() {
    // Reset any prior clarify state from a previous run-through.
    setClarifyQuestions([]);
    setClarifyAnswers({});
    setClarifyIteration(0);
    setAllClarifications([]);

    setLoading(true);
    track('decision_session_started', { lens });
    setError('');
    try {
      const enrichedAnswers = { ...answers, key_challenges: challenge };
      const baseContext = buildBaseStrategyContext();
      const framingContext = buildLensFramingContext(lens, enrichedAnswers, canvasWithSplits(), prefillSources, relationshipType);
      const combinedContext = [framingContext, baseContext].filter(Boolean).join('\n\n');
      const probeRes = await axios.post(`${API}/clarify`, {
        canvas: canvasWithSplits(),
        answers: enrichedAnswers,
        lens,
        userInput: buildChallengeWithContext(),
        strategyContext: combinedContext || null,
        iteration: 0,
      });
      if (probeRes.data?.ready === false && Array.isArray(probeRes.data.questions) && probeRes.data.questions.length > 0) {
        setClarifyQuestions(probeRes.data.questions);
        setClarifyAnswers({});
        setClarifyIteration(1); // next probe call will be iter=1
        setStep('clarifying');
        window.scrollTo(0, 0);
        setLoading(false);
        return;
      }
      // Probe says ready (or returned nothing useful) — go straight to recommendation
      setLoading(false);
      await runRecommendation([]);
    } catch (e) {
      // Clarify failure must never block the recommendation path
      setLoading(false);
      await runRecommendation([]);
    }
  }

  // Submit the current batch of clarification answers. If skip=true the
  // business leader is bailing on the probe loop — go straight to the recommendation
  // with whatever has been answered so far.
  async function submitClarify({ skip = false } = {}) {
    const batchEntries = clarifyQuestions
      .map(q => ({ question: q.question, answer: (clarifyAnswers[q.id] || '').trim() }))
      .filter(c => c.answer.length > 0);
    const accumulated = [...allClarifications, ...batchEntries];
    setAllClarifications(accumulated);

    if (skip) {
      await runRecommendation(accumulated);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const enrichedAnswers = { ...answers, key_challenges: challenge };
      const baseContext = buildBaseStrategyContext();
      const clarifyContext = formatClarificationsContextFE(accumulated);
      const probeStrategyContext = clarifyContext
        ? (baseContext ? `${clarifyContext}\n\n${baseContext}` : clarifyContext)
        : baseContext;
      const probeRes = await axios.post(`${API}/clarify`, {
        canvas: canvasWithSplits(),
        answers: enrichedAnswers,
        lens,
        userInput: buildChallengeWithContext(),
        strategyContext: probeStrategyContext || null,
        iteration: clarifyIteration,
      });
      if (probeRes.data?.ready === false && Array.isArray(probeRes.data.questions) && probeRes.data.questions.length > 0) {
        setClarifyQuestions(probeRes.data.questions);
        setClarifyAnswers({});
        setClarifyIteration(clarifyIteration + 1);
        window.scrollTo(0, 0);
        setLoading(false);
        return;
      }
      // Server says ready (or hit cap) — proceed
      setLoading(false);
      await runRecommendation(accumulated);
    } catch (e) {
      setLoading(false);
      await runRecommendation(accumulated);
    }
  }

  async function handleChat() {
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    try {
      const c = canvasWithSplits();
      const today = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      const geo = c.geography || 'UK';
      const cur = c.currency || (geo.toLowerCase().includes('uk') ? 'GBP (£)' : 'local currency');
      const systemContext = `You are GuruOne, a strategic advisor for SME businesses.
Today is ${today}. This business operates in ${geo}, use ${cur} for all monetary figures — never use $.
Business: Sector: ${c.sector || 'unknown'}. Revenue: ${c.annual_revenue || 'unknown'}. Challenges: ${c.key_challenges || 'not provided'}. Lens: ${lens}.
Original challenge: "${challenge}".
Current recommendation: "${result?.recommendation || 'not yet given'}".
The business leader is correcting an assumption or providing additional context. Acknowledge what changed, explain how it affects the recommendation, and provide a revised recommendation if appropriate.
Format your response with clear sections: WHAT I GOT WRONG, WHAT CHANGES, WHAT STAYS THE SAME, REVISED RECOMMENDATION, REVISED FIRST 30 DAYS.`;
      const allMessages = [...chatMessages, userMsg];
      const res = await axios.post(`${API}/chat`, {
        messages: allMessages,
        context: systemContext
      });
      const assistantMsg = { role: 'assistant', content: res.data.reply };
      setChatMessages(prev => [...prev, assistantMsg]);
      if (res.data.reply.includes('REVISED RECOMMENDATION') || res.data.reply.includes('WHAT I GOT WRONG')) {
        setRevisedResult(res.data.reply);
      }
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    }
    setChatLoading(false);
  }

  function friendlyError(msg) {
    if (!msg) return msg;
    if (msg.toLowerCase().includes('overloaded') || msg.includes('529')) {
      return 'The AI service is temporarily busy. Please wait a moment and try again.';
    }
    return msg;
  }

  const CRITERIA_BASE = [
    { key: 'speed',         label: 'Speed',          tip: 'How important are quick wins and immediate benefits vs waiting for bigger long-term gains?' },
    { key: 'cost',          label: 'Cost',            tip: 'How important is minimising cost or financial exposure?' },
    { key: 'reversibility', label: 'Reversibility',  tip: 'How important is it to be able to undo or change course?' },
    { key: 'longterm',      label: 'Long term fit',  tip: 'How important are sustainable long-term benefits vs immediate improvements?' },
    { key: 'teamimpact',    label: 'Team stability',    tip: 'How important is maintaining team morale, job security and stability?' },
    { key: 'customerimpact', label: 'Customer impact', tip: 'How directly will this affect customer experience and relationships?' },
    { key: 'complexity',    label: 'Operational complexity', tip: 'How complex will this be to implement and manage ongoing?' },
  ];
  const CRITERIA_RISK = {
    team_capacity:       { key: 'risk', label: 'People & morale risk',          tip: 'Could this damage team trust, motivation or key relationships?' },
    market_focus:        { key: 'risk', label: 'Customer & market risk',        tip: 'Could this damage customer relationships or market positioning?' },
    profitability:       { key: 'risk', label: 'Financial risk',                tip: 'Could this threaten cash flow, create debt or overextend the business financially?' },
    positioning:         { key: 'risk', label: 'Competitive & positioning risk', tip: 'Could this weaken your competitive position or make you easier to replace?' },
    growth_path:         { key: 'risk', label: 'Strategic & execution risk',    tip: 'What is the downside risk if this growth path underperforms or fails?' },
    process_scalability: { key: 'risk', label: 'Operational & delivery risk',   tip: 'Could this create delivery failures, client dissatisfaction, or team burnout?' },
    make_vs_buy:         { key: 'risk', label: 'Supplier & dependency risk',    tip: 'How exposed are you if an external supplier fails, underperforms or raises prices significantly?' },
    capital_allocation:  { key: 'risk', label: 'Capital & execution risk',      tip: 'What is the financial and delivery risk if this investment underperforms or takes longer than expected?' },
    founder_leverage:    { key: 'risk', label: 'Key-person & dependency risk',  tip: 'Does this increase or decrease how much the business relies on you personally?' },
    team_effectiveness:  { key: 'risk', label: 'Team & culture risk',           tip: 'Could this damage trust, morale or key relationships in the team?' },
    people_decisions:    { key: 'risk', label: 'Relationship & legal risk',     tip: 'Could this decision damage trust, create legal exposure, or destabilise the team?' },
    multi:               { key: 'risk', label: 'Risk',                          tip: 'What is the downside risk of this option?' },
    // Legacy aliases
    team:   { key: 'risk', label: 'People risk',            tip: 'Could this damage team trust, motivation or key relationships?' },
    market: { key: 'risk', label: 'Customer & market risk', tip: 'Could this damage customer relationships or market positioning?' },
    profit: { key: 'risk', label: 'Financial risk',         tip: 'Could this threaten cash flow or overextend the business financially?' },
  };
  const CRITERIA_BONUS = {
    team_capacity:       { key: 'bonus', label: 'Time freed up',              tip: 'How significantly does this reduce demands on you personally?' },
    market_focus:        { key: 'bonus', label: 'Revenue concentration risk', tip: 'Does this reduce dangerous over-reliance on a single segment or client?' },
    profitability:       { key: 'bonus', label: 'Cash flow timing',           tip: 'Consider both short-term cash impact and medium-term improvement in profitability.' },
    positioning:         { key: 'bonus', label: 'Pricing power gained',       tip: 'Does this improve your ability to charge more and defend your rates?' },
    growth_path:         { key: 'bonus', label: 'Optionality preserved',      tip: 'Does this option keep future paths open, or does it foreclose them?' },
    process_scalability: { key: 'bonus', label: 'Scalability unlocked',       tip: 'How significantly does this remove a constraint on the business scaling reliably?' },
    make_vs_buy:         { key: 'bonus', label: 'Strategic control gained',   tip: 'Does this option give you more ownership over something that matters to how you compete?' },
    capital_allocation:  { key: 'bonus', label: 'Return on capital clarity',  tip: "How clearly does this option demonstrate a return relative to what's invested?" },
    founder_leverage:    { key: 'bonus', label: 'Time reclaimed',             tip: 'How much high-quality time does this free for you to work on the business rather than in it?' },
    team_effectiveness:  { key: 'bonus', label: 'Accountability unlocked',    tip: 'Does this create clearer ownership and higher-quality decisions across the team?' },
    people_decisions:    { key: 'bonus', label: 'Clarity and resolution',     tip: 'Does this option bring the situation to a clear, fair resolution for all involved?' },
    multi:               { key: 'bonus', label: 'Compound benefit',           tip: 'Does this option address more than one problem at once?' },
    // Legacy aliases
    team:   { key: 'bonus', label: 'Capacity freed up',          tip: 'How significantly does this increase capacity or reduce the bottleneck?' },
    market: { key: 'bonus', label: 'Revenue concentration risk', tip: 'Does this reduce over-reliance on a single segment or client?' },
    profit: { key: 'bonus', label: 'Cash flow improvement',      tip: 'How significantly does this improve cash position or profitability?' },
  };

  function getCriteria() {
    const risk  = CRITERIA_RISK[lens]  || CRITERIA_RISK.multi;
    const bonus = CRITERIA_BONUS[lens] || CRITERIA_BONUS.multi;
    return [...CRITERIA_BASE, risk, bonus];
  }

  // Inspect the user's Direction + Design canvases against the current
  // lens's dependency list. Returns null when no warning is needed, or
  // { severity, missing: [{ canvas, label }], canvases: ['strategy', 'operating_model'] }.
  function evaluateCanvasGaps(lensKey) {
    const dep = LENS_CANVAS_DEPENDENCIES[lensKey];
    if (!dep || !Array.isArray(dep.fields) || !dep.fields.length) return null;
    const strategyData = readUserScopedCanvas('guruone_strategy');
    const omData = readUserScopedCanvas('guruone_operating_model');
    const missing = dep.fields.filter(f => {
      const src = f.canvas === 'strategy' ? strategyData : omData;
      return !canvasSectionHasContent(src, f.section);
    });
    if (missing.length === 0) return null;
    const severity = missing.length === dep.fields.length ? 'significant' : 'advisory';
    const canvases = Array.from(new Set(missing.map(f => f.canvas)));
    return { severity, missing, canvases };
  }

  // Read a value via dotted path: 'people_roles.current.key_bottlenecks'.
  function readByPath(root, path) {
    if (!root || typeof root !== 'object' || !path) return undefined;
    return path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), root);
  }

  // Apply a single canvas_prefill descriptor against the raw canvases.
  // Returns the resolved answer value (in question's storage shape), or
  // undefined if nothing usable was found.
  function applyCanvasPrefill(question, descriptor, sources) {
    if (!descriptor) return undefined;
    const src = descriptor.canvas === 'strategy' ? sources.strategy : sources.operating_model;
    if (!src) return undefined;
    const raw = readByPath(src, descriptor.field_path);
    if (raw == null || raw === '' || (Array.isArray(raw) && !raw.length)) return undefined;

    const t = descriptor.transform || 'direct';

    if (t === 'direct') {
      // For choice questions, only accept if it matches one of the options.
      if (question.type === 'choice' && Array.isArray(question.options)) {
        return question.options.includes(raw) ? raw : undefined;
      }
      return raw;
    }

    if (t === 'map' && descriptor.map && typeof descriptor.map === 'object') {
      const mapped = descriptor.map[raw];
      if (mapped == null) return undefined;
      if (question.type === 'choice' && Array.isArray(question.options)) {
        return question.options.includes(mapped) ? mapped : undefined;
      }
      return mapped;
    }

    if (t === 'segment_names_to_split_opts' && Array.isArray(raw)) {
      // strategy.customers.entries[].segment_name → SplitSelector customOpts
      const customOpts = raw.map((e, i) => {
        const label = e?.segment_name || e?.name || e?.segment || `Segment ${i + 1}`;
        const key = 'seg_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
        return { key, label };
      }).filter(o => o.label && o.key && o.key !== 'seg_');
      if (!customOpts.length) return undefined;
      const share = Math.floor(100 / customOpts.length);
      const splits = {};
      let used = 0;
      customOpts.forEach((o, i) => {
        const s = i === customOpts.length - 1 ? 100 - used : share;
        splits[o.key] = s;
        used += s;
      });
      return { mode: 'percentage', splits, absoluteValues: {}, customOpts };
    }

    if (t === 'segment_names_to_list_items' && Array.isArray(raw)) {
      const items = raw.map(e => e?.segment_name || e?.name || e?.segment).filter(Boolean);
      if (!items.length) return undefined;
      return items.map(label => ({ label, rating: '' }));
    }

    if (t === 'parse_to_list_items' && typeof raw === 'string') {
      const items = raw.split(/[\n;,]/).map(s => s.trim()).filter(Boolean);
      if (!items.length) return undefined;
      return items.slice(0, 8).map(label => ({ label, rating: '' }));
    }

    if (t === 'parse_process_names' && typeof raw === 'string') {
      const names = raw.split(/[\n;,]/).map(s => s.trim()).filter(Boolean).slice(0, 5);
      if (!names.length) return undefined;
      return names.map(name => ({
        name, repeatability: '', volume: '', error_frequency: '',
        time_intensity: '', customer_touchpoint: '',
      }));
    }

    return undefined;
  }

  // Walk every question's canvas_prefill descriptor(s) and merge results
  // into a single { fieldKey: value } map for seeding `answers`.
  function computeCanvasPrefills(qs) {
    const sources = {
      strategy: readUserScopedCanvas('guruone_strategy'),
      operating_model: readUserScopedCanvas('guruone_operating_model'),
    };
    const out = {};
    (qs || []).forEach(q => {
      const desc = q.canvas_prefill;
      if (!desc) return;
      const list = Array.isArray(desc) ? desc : [desc];
      for (const d of list) {
        const val = applyCanvasPrefill(q, d, sources);
        if (val !== undefined) {
          out[q.field] = val;
          break; // first successful descriptor wins
        }
      }
    });
    return out;
  }

  function CanvasGapWarning({ gap, onDismiss }) {
    const isSignificant = gap.severity === 'significant';
    const canvasLabel = (() => {
      const labels = gap.canvases.map(c => c === 'strategy' ? 'Direction' : 'Design');
      if (labels.length === 1) return `your ${labels[0]} canvas`;
      return `your ${labels.join(' and ')} canvases`;
    })();
    const fieldLabels = gap.missing.map(f => f.label);
    const fieldsList = fieldLabels.length <= 2
      ? fieldLabels.join(' and ')
      : `${fieldLabels.slice(0, -1).join(', ')}, and ${fieldLabels[fieldLabels.length - 1]}`;
    return (
      <div role="alert" style={{
        background: isSignificant ? '#fef2f2' : '#fffbeb',
        border: `1px solid ${isSignificant ? '#fecaca' : '#fde68a'}`,
        borderLeft: `4px solid ${isSignificant ? '#dc2626' : '#d97706'}`,
        borderRadius: 8,
        padding: '0.85rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.7rem',
      }}>
        <div style={{ fontSize: '1.05rem', lineHeight: 1, marginTop: 1 }}>
          {isSignificant ? '⚠️' : 'ℹ️'}
        </div>
        <div style={{ flex: 1, fontSize: '0.84rem', lineHeight: 1.55, color: isSignificant ? '#7f1d1d' : '#7B4A0F' }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>
            {isSignificant
              ? 'Key information is missing from your canvases'
              : 'Some context that would sharpen this recommendation is missing'}
          </div>
          {isSignificant ? (
            <>
              Key information is missing from {canvasLabel} that will materially affect the quality of this recommendation — specifically {fieldsList}. We strongly recommend completing this before continuing, or answer the additional questions below and I'll work with what you give me.
            </>
          ) : (
            <>
              Some information that would improve this recommendation is missing from {canvasLabel} — specifically {fieldsList}. You can complete it now, or answer the additional questions below.
            </>
          )}
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            border: 'none', background: 'none', color: isSignificant ? '#991b1b' : '#92400e',
            cursor: 'pointer', fontSize: '0.95rem', width: 'auto', padding: '0 4px', lineHeight: 1,
          }}>
          ✕
        </button>
      </div>
    );
  }

  function cyclePill(key) {
    setCriteriaWeights(prev => ({ ...prev, [key]: ((prev[key] || 0) + 1) % 3 }));
  }

  function buildCriteriaString() {
    const all = getCriteria();
    if (customCriterion.trim()) all.push({ key: 'custom', label: customCriterion.trim() });
    const critical = all.filter(c => (criteriaWeights[c.key] || 0) === 2).map(c => c.label);
    const matters  = all.filter(c => (criteriaWeights[c.key] || 0) === 1).map(c => c.label);
    if (!critical.length && !matters.length) return '';
    let s = 'Business leader\'s decision criteria — ';
    if (critical.length) s += `Critical: ${critical.join(', ')}. `;
    if (matters.length)  s += `Also important: ${matters.join(', ')}.`;
    return s.trim();
  }

  function getRecommendedLabel() {
    if (!result) return null;
    if (result.recommended_label) return result.recommended_label.toUpperCase();
    if (!result.recommendation) return null;
    const rec = result.recommendation.toLowerCase();
    const m = rec.match(/(?:recommend|suggest|go with|choose|pursue|select|best|prefer).*?\boption\s+([a-e])\b/);
    if (m) return m[1].toUpperCase();
    const m2 = rec.match(/\boption\s+([a-e])\b/);
    if (m2) return m2[1].toUpperCase();
    return null;
  }

  function isRecommended(optTitle, optLabel) {
    const recLabel = getRecommendedLabel();
    if (!recLabel || !optLabel) return false;
    return recLabel === optLabel.toUpperCase();
  }

  async function handleWhyToggle() {
    setWhyOpen(w => !w);
    if (whyData.content || whyData.loading) return;
    setWhyData({ loading: true, content: null, error: null });
    const c = canvasWithSplits();
    const today = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const geo = c.geography || 'UK';
    const cur = c.currency || (geo.toLowerCase().includes('uk') ? 'GBP (£)' : 'local currency');
    const systemContext = `You are GuruOne, a strategic advisor for SME businesses.
Today is ${today}. This business operates in ${geo}, use ${cur} — never use $.
Business: Sector: ${c.sector || 'unknown'}. Revenue: ${c.annual_revenue || 'unknown'}. Challenges: ${c.key_challenges || 'not provided'}. Lens: ${lens}.
Respond with ONLY valid JSON. No markdown. No code blocks. Start with { and end with }.`;
    const userPrompt = `Explain why "${result?.recommendation?.slice(0,100)}" is the recommended option.
Return this JSON:
{
  "headline": "one sentence summary of the core reasoning",
  "assertions": [
    { "claim": "max 8 words", "reasoning": "2-3 sentences" },
    { "claim": "max 8 words", "reasoning": "2-3 sentences" },
    { "claim": "max 8 words", "reasoning": "2-3 sentences" }
  ]
}`;
    try {
      const res = await axios.post(`${API}/chat`, {
        messages: [{ role: 'user', content: userPrompt }],
        context: systemContext
      });
      const raw = res.data.reply;
      const s = raw.indexOf('{'); const e2 = raw.lastIndexOf('}');
      const parsed = JSON.parse(raw.slice(s, e2 + 1));
      setWhyData({ loading: false, content: parsed, error: null });
    } catch (e) {
      setWhyData({ loading: false, content: null, error: e.response?.data?.error || e.message });
    }
  }

  async function handleGenerateAlternative(optLabel) {
    setGeneratingAlt(optLabel);
    setAltError(null);
    try {
      const remaining = result.options.filter(o => {
        const l = o.label || String.fromCharCode(65 + result.options.indexOf(o));
        return l !== optLabel && !discardedOptions[l];
      });
      const discarded = Object.entries(discardedOptions).map(([l, d]) => ({ label: l, title: d.title, reason: d.reason }));
      const c = canvasWithSplits();
      const systemContext = `You are GuruOne, a strategic advisor for SME businesses.
Business: Sector: ${c.sector || 'unknown'}. Revenue: ${c.annual_revenue || 'unknown'}. Challenges: ${c.key_challenges || 'not provided'}. Lens: ${lens}.
Respond with ONLY valid JSON. No markdown. No code blocks. Start with { and end with }.`;

      const remainingSummary = remaining.map(o => `- ${o.label}: ${o.title}`).join('\n') || 'None';
      const discardedSummary = discarded.map(d => `- ${d.title}: ${d.reason}`).join('\n') || 'None';

      const userPrompt = `Generate ONE new alternative option meaningfully different from what remains and what was discarded.
Options still considered:\n${remainingSummary}
Options discarded and why:\n${discardedSummary}
${altInputText.trim() ? `\nBusiness leader wants: "${altInputText.trim()}"` : ''}
Return exactly this JSON:
{
  "label": "New",
  "title": "max 5 words",
  "description": "one sentence",
  "pros": [{"headline": "max 5 words", "detail": "one sentence"}, {"headline": "max 5 words", "detail": "one sentence"}],
  "cons": [{"headline": "max 5 words", "detail": "one sentence"}, {"headline": "max 5 words", "detail": "one sentence"}],
  "score": 6
}`;

      const res = await axios.post(`${API}/chat`, {
        messages: [{ role: 'user', content: userPrompt }],
        context: systemContext
      });
      const raw = res.data.reply;
      const start = raw.indexOf('{');
      const end = raw.lastIndexOf('}');
      if (start === -1 || end === -1) throw new Error('No JSON in response');
      const newOpt = { ...JSON.parse(raw.slice(start, end + 1)), label: optLabel };
      setResult(prev => ({
        ...prev,
        options: prev.options.map(o =>
          (o.label || String.fromCharCode(65 + prev.options.indexOf(o))) === optLabel ? newOpt : o
        )
      }));
      setDiscardedOptions(prev => { const n = { ...prev }; delete n[optLabel]; return n; });
      setAltInputOption(null);
      setAltInputText('');
    } catch (e) {
      setAltError(e.response?.data?.error || e.message || 'Something went wrong. Try again.');
    }
    setGeneratingAlt(null);
  }

  // Commit this decision to the business leader's business memory. The backend
  // flags the decision as confirmed and will feed it into the strategy
  // context of future AI calls so GuruOne reasons consistently.
  async function handleConfirmDecision() {
    if (!decisionId) {
      setConfirmError('Decision not yet saved. Please wait a moment and try again.');
      return;
    }
    if (!user?.id) {
      setConfirmError('You need to be signed in to confirm a decision.');
      return;
    }
    setConfirmLoading(true);
    setConfirmError('');
    try {
      await axios.post(`${API}/decisions/${decisionId}/confirm`, { userId: user.id });
      track('decision_confirmed', { lens });
      setIsConfirmed(true);
      setConfirmModalOpen(false);
    } catch (e) {
      setConfirmError(e.response?.data?.error || e.message || 'Could not confirm decision. Please try again.');
    }
    setConfirmLoading(false);
  }

  // Dark guru toolbar — rendered inside each step block, after the
  // ProgressTrack and DecisionMasthead, so it sits below the journey
  // indicator and guru name. Two-zone layout: left group (G logo + How
  // to use + "Your Decision Canvas" label), right group (Add context +
  // Download).
  const renderToolbar = () => {
    const toolbarBtn = {
      fontSize: '0.74rem', padding: '0.4rem 0.85rem', borderRadius: 18,
      border: '1px solid rgba(255,255,255,0.25)', background: 'transparent',
      color: 'white', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
      width: 'auto', whiteSpace: 'nowrap',
    };
    const downloadEnabled = !!result;
    return (
      <div style={{
        background: '#1a1a2e', color: 'white', borderRadius: 10,
        padding: '0.55rem 0.85rem', marginBottom: '0.85rem',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        {/* Left group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flex: 1 }}>
          <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src="/Logo_emblem_only.png" alt="GuruOne" style={{ height: 22, width: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          </div>
          <button onClick={() => setHelpOpen(true)} aria-label="How to use Decision Guru" style={toolbarBtn}>
            How to use
          </button>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
            Your Decision Canvas
          </span>
        </div>
        {/* Right group */}
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button onClick={() => setAddContextOpen(true)} style={toolbarBtn}>
            ⬆ Upload company info
          </button>
          <button
            onClick={handleDownloadDecision}
            disabled={!downloadEnabled}
            title={downloadEnabled ? 'Download your decision summary' : 'Available once a recommendation is generated'}
            style={{
              ...toolbarBtn,
              borderColor: downloadEnabled ? 'rgba(167,227,167,0.55)' : 'rgba(255,255,255,0.15)',
              color: downloadEnabled ? '#A7E3A7' : 'rgba(255,255,255,0.35)',
              cursor: downloadEnabled ? 'pointer' : 'not-allowed',
            }}
          >
            ⬇ Download 1-pager
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="app">
      <Header />

      {/* Start Fresh — discards Decision Guru progress only. Always
          accessible when there is saved progress. Does not touch
          Strategy Canvas or Operating Model Canvas data. */}
      <button
          type="button"
          style={{
            position: 'fixed',
            top: '72px',
            right: '16px',
            zIndex: 1000,
            background: 'white',
            color: '#555',
            padding: '0.35rem 0.75rem',
            borderRadius: 20,
            fontSize: '0.72rem',
            fontWeight: 500,
            cursor: 'pointer',
            border: '1px solid #e0e0e0',
            fontFamily: 'inherit',
            width: 'auto',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            transition: 'all 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.color = '#0F6E56'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.color = '#555'; }}
          onClick={() => clearDecisionGuruState()}
          title="Start fresh (keeps Strategy Canvas and Operating Model Canvas)">
          ↻ Start fresh
        </button>

      <main style={{ maxWidth: 1300, margin: '0 auto', padding: '1.5rem' }}>
        {step === 'challenge' && canvas && (
          <div className="canvas-card">
            {/* Continue previous decision — moved here from step === 'start'.
                Renders only when there is saved progress beyond a fresh start. */}
            {bannerVisible && (
              <div style={{
                background: '#E1F5EE',
                border: '1px solid #1D9E75',
                borderRadius: 12,
                padding: '1rem 1.25rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F6E56', marginBottom: '0.5rem' }}>
                  Continue Previous Decision?
                </div>
                <p style={{ fontSize: '0.8rem', color: '#0F6E56', margin: '0 0 1rem', lineHeight: 1.5 }}>
                  You have a saved decision journey from {new Date(persistedState.lastUpdated).toLocaleDateString()}.
                  {persistedState.challenge && ` Working on: "${persistedState.challenge.substring(0, 60)}${persistedState.challenge.length > 60 ? '...' : ''}"`}
                </p>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      setBannerVisible(false);
                      if (persistedState.result) {
                        setStep('result');
                      } else if (persistedState.answers && Object.keys(persistedState.answers).length > 0) {
                        setStep('questions');
                      } else {
                        setStep('challenge');
                      }
                      window.scrollTo(0, 0);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: 8,
                      background: '#1D9E75',
                      color: 'white',
                      border: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Continue Previous
                  </button>
                  <button
                    onClick={() => clearDecisionGuruState()}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: 8,
                      background: 'none',
                      color: '#0F6E56',
                      border: '1px solid #1D9E75',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            )}
            <ProgressTrack activeIndex={1} />
            <DecisionMasthead tagline="Structure your thinking. Make better decisions." />
            {renderToolbar()}
            {!canvasFromExisting && showCanvasSnapshot && (
              <div style={{
                padding: '1rem 1.5rem 0.75rem',
                borderBottom: '1px solid #f0f0f0',
              }}>
                <p style={{
                  fontSize: '0.88rem', color: '#444', lineHeight: 1.65, margin: 0,
                  fontStyle: 'italic',
                }}>
                  Because I don&apos;t have much from your Direction and Design canvases yet, I&apos;ve put together what I know from your account profile. To make my recommendations more grounded, you can use <strong>⬆ Upload company info</strong> at the top to point me at your website or upload key documents — or just correct anything wrong below and describe your decision.
                </p>
              </div>
            )}

            {canvas && !editingSection && !canvasFromExisting && showCanvasSnapshot && (() => {
              const unclearFields = ['sector','primary_problem_solved','target_customer_type','price_positioning','go_to_market','delivery_model','business_stage','annual_revenue','team_size'].filter(f => {
                const v = getCanvasValue(f);
                return !v || v === 'unclear' || v === '' || v === 'not provided';
              });
              return unclearFields.length > 0 ? (
                <div style={{ background: '#FFF8E1', borderLeft: '3px solid #FFB300', padding: '0.65rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1rem' }}>⚠</span>
                  <span style={{ fontSize: '0.8rem', color: '#5D4037' }}>
                    <strong>Some fields need your attention.</strong> Use the edit buttons below to fill in: {unclearFields.map(f => f.replace(/_/g,' ')).join(', ')}.
                  </span>
                </div>
              ) : null;
            })()}

            {!canvasFromExisting && showCanvasSnapshot && (
            <div className="canvas-grid">
              <div className="canvas-section">
                <div className="canvas-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span className="cs-icon cs-blue">▸</span> Value proposition
                    <ConfidencePill conf={sectionConfidence(canvas._confidence, ['value_proposition', 'sector'])} />
                  </div>
                  {!editingSection && <button className="edit-canvas-btn" onClick={() => setEditingSection('value_proposition')} style={{ fontSize: '0.68rem', padding: '0.2rem 0.6rem' }}>✎ Edit</button>}
                </div>
                <div className="canvas-items-row">
                  <CanvasItem label="Sector" value={getCanvasValue('sector')}
                    editing={editingSection === 'value_proposition'} onChange={v => setCanvasEdits({...canvasEdits, sector: v})} />
                  <CanvasItem label="Problem solved" value={getCanvasValue('primary_problem_solved')}
                    editing={editingSection === 'value_proposition'} onChange={v => setCanvasEdits({...canvasEdits, primary_problem_solved: v})} />
                  <CanvasItem label="Customer type" value={getCanvasValue('target_customer_type')}
                    editing={editingSection === 'value_proposition'} onChange={v => setCanvasEdits({...canvasEdits, target_customer_type: v})} />
                </div>
                {editingSection === 'value_proposition' && (
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '0.6rem 1.5rem 0.75rem' }}>
                    <button className="edit-canvas-btn saving" onClick={() => applyCanvasEdits()}>✓ Save changes</button>
                    <button className="edit-canvas-btn" onClick={() => { setCanvasEdits({}); setEditingSection(null); }}>Cancel</button>
                  </div>
                )}
              </div>

              <div className="canvas-section">
                <div className="canvas-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span className="cs-icon cs-green">▸</span> Market position
                    <ConfidencePill conf={sectionConfidence(canvas._confidence, ['competitors', 'value_proposition'])} />
                  </div>
                  {!editingSection && <button className="edit-canvas-btn" onClick={() => setEditingSection('market_position')} style={{ fontSize: '0.68rem', padding: '0.2rem 0.6rem' }}>✎ Edit</button>}
                </div>
                <div className="canvas-items-row">
                  <CanvasItem label="Differentiators" value={Array.isArray(getCanvasValue('top_3_differentiators'))
                      ? getCanvasValue('top_3_differentiators').join('\n')
                      : getCanvasValue('top_3_differentiators')}
                    editing={editingSection === 'market_position'} onChange={v => setCanvasEdits({...canvasEdits, top_3_differentiators: v})} />
                </div>
                <div className="canvas-items-row" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <CanvasItem label="Price positioning" value={getCanvasValue('price_positioning')}
                    editing={editingSection === 'market_position'} type="select"
                    options={['premium', 'mid-market', 'low-cost']}
                    onChange={v => setCanvasEdits({...canvasEdits, price_positioning: v})} />
                  <CanvasItem label="Geography" value={getCanvasValue('geography')}
                    editing={editingSection === 'market_position'} onChange={v => setCanvasEdits({...canvasEdits, geography: v})} />
                  <CanvasItem label="Currency" value={getCanvasValue('currency')}
                    editing={editingSection === 'market_position'} onChange={v => setCanvasEdits({...canvasEdits, currency: v})} />
                </div>
                {editingSection === 'market_position' && (
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '0.6rem 1.5rem 0.75rem' }}>
                    <button className="edit-canvas-btn saving" onClick={() => applyCanvasEdits()}>✓ Save changes</button>
                    <button className="edit-canvas-btn" onClick={() => { setCanvasEdits({}); setEditingSection(null); }}>Cancel</button>
                  </div>
                )}
              </div>

              <div className="canvas-section">
                <div className="canvas-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span className="cs-icon cs-amber">▸</span> Business model
                    <ConfidencePill conf={sectionConfidence(canvas._confidence, ['revenue_model', 'go_to_market'])} />
                  </div>
                  {!editingSection && <button className="edit-canvas-btn" onClick={() => setEditingSection('business_model')} style={{ fontSize: '0.68rem', padding: '0.2rem 0.6rem' }}>✎ Edit</button>}
                </div>
                <div className="canvas-items-row">
                  <SplitField fieldKey="revenue_model"
                    splitState={splits.revenue_model}
                    onChange={v => setSplits(s => ({...s, revenue_model: v}))}
                    editing={editingSection === 'business_model'} />
                  <SplitField fieldKey="go_to_market"
                    splitState={splits.go_to_market}
                    onChange={v => setSplits(s => ({...s, go_to_market: v}))}
                    editing={editingSection === 'business_model'} />
                  <SplitField fieldKey="delivery_model"
                    splitState={splits.delivery_model}
                    onChange={v => setSplits(s => ({...s, delivery_model: v}))}
                    editing={editingSection === 'business_model'} />
                </div>
                {editingSection === 'business_model' && (
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '0.6rem 1.5rem 0.75rem' }}>
                    <button className="edit-canvas-btn saving" onClick={() => applyCanvasEdits()}>✓ Save changes</button>
                    <button className="edit-canvas-btn" onClick={() => { setCanvasEdits({}); setEditingSection(null); }}>Cancel</button>
                  </div>
                )}
              </div>

              <div className="canvas-section">
                <div className="canvas-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span className="cs-icon cs-purple">▸</span> Business context
                    <ConfidencePill conf={sectionConfidence(canvas._confidence, ['annual_revenue', 'team_size'])} />
                  </div>
                  {!editingSection && <button className="edit-canvas-btn" onClick={() => setEditingSection('business_context')} style={{ fontSize: '0.68rem', padding: '0.2rem 0.6rem' }}>✎ Edit</button>}
                </div>
                <div className="canvas-items-row">
                  <CanvasItem label="Business stage" value={getCanvasValue('business_stage')}
                    editing={editingSection === 'business_context'} type="select"
                    options={['pre-revenue','early-stage','growing','scaling','established']}
                    onChange={v => setCanvasEdits({...canvasEdits, business_stage: v})} />
                  <CanvasItem label="Annual revenue" value={getCanvasValue('annual_revenue')}
                    editing={editingSection === 'business_context'} onChange={v => setCanvasEdits({...canvasEdits, annual_revenue: v})} />
                  <CanvasItem label="Team size" value={getCanvasValue('team_size')}
                    editing={editingSection === 'business_context'} onChange={v => setCanvasEdits({...canvasEdits, team_size: v})} />
                </div>
                {editingSection === 'business_context' && (
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '0.6rem 1.5rem 0.75rem' }}>
                    <button className="edit-canvas-btn saving" onClick={() => applyCanvasEdits()}>✓ Save changes</button>
                    <button className="edit-canvas-btn" onClick={() => { setCanvasEdits({}); setEditingSection(null); }}>Cancel</button>
                  </div>
                )}
              </div>
            </div>
            )}

            <div className="canvas-challenge">
              <h3>What's your biggest challenge or decision right now?</h3>
              <textarea
                placeholder="e.g. I'm the bottleneck in my business and need to figure out who to hire next..."
                value={challenge}
                onChange={e => setChallenge(e.target.value)}
                rows={4}
              />
              {loading && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '1.25rem 0' }}>
                  <div className="loading-spinner" />
                  <p style={{ fontSize: '0.82rem', color: '#1D9E75', margin: 0, fontWeight: 500 }}>Analysing your challenge…</p>
                </div>
              )}
              {!canvasFromExisting && (
                <div style={{
                  margin: '0.75rem 0',
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: '0.75rem',
                }}>
                  <button
                    type="button"
                    onClick={() => setAddContextOpen(true)}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      fontSize: '0.8rem', color: '#1D9E75', fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                    }}
                  >
                    <span>+</span>
                    <span>Add more context — upload documents or materials relevant to this decision</span>
                  </button>
                  {uploadedDocText && (
                    <div style={{
                      marginTop: '0.5rem', fontSize: '0.72rem', color: '#1D9E75',
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                    }}>
                      ✓ Context uploaded — will be included with your challenge
                    </div>
                  )}
                </div>
              )}
              {!loading && (
                <button onClick={handleChallenge} disabled={!challenge}>
                  Let&apos;s work through this →
                </button>
              )}
              {canvasFromExisting && (
                <div style={{ marginTop: '0.75rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => setAddContextOpen(true)}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      fontSize: '0.75rem', color: '#888', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                    }}
                  >
                    + Add more context to improve this recommendation
                  </button>
                  {uploadedDocText && (
                    <div style={{
                      marginTop: '0.5rem', fontSize: '0.72rem', color: '#1D9E75',
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                    }}>
                      ✓ Context uploaded — will be included with your challenge
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {buildingRecommendation && (
          <div className="card">
            <LoadingScreen />
          </div>
        )}

        {!buildingRecommendation && step === 'questions' && (
          <div className="card">
            <ProgressTrack activeIndex={1} />
            <DecisionMasthead tagline="Structure your thinking. Make better decisions." />
            {renderToolbar()}
            <h2>A few questions to advise you well</h2>
            {(() => {
              const gap = evaluateCanvasGaps(lens);
              if (!gap) return null;
              if (gapWarningDismissedFor === lens) return null;
              return <CanvasGapWarning gap={gap} onDismiss={() => setGapWarningDismissedFor(lens)} />;
            })()}
            {/* make_vs_buy — confirm the decision flagged on the canvas */}
            {lens === 'make_vs_buy' && !makeVsBuyConfirmDismissed && canvas?.make_vs_buy_flagged && (
              <div style={{
                background: '#f0faf6', border: '1px solid #b8e4d4',
                borderLeft: '4px solid #48B650',
                borderRadius: 8, padding: '0.85rem 1rem',
                marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.7rem',
              }}>
                <div style={{ fontSize: '1.05rem', marginTop: 1 }}>🤔</div>
                <div style={{ flex: 1, fontSize: '0.85rem', lineHeight: 1.55, color: '#0F6E56' }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>You've already flagged this decision</div>
                  <div style={{ marginBottom: 6 }}>
                    Your Design canvas notes: <em>"{canvas.make_vs_buy_flagged}"</em>
                  </div>
                  Is this what you'd like to work through?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setMakeVsBuyConfirmDismissed(true)}
                    style={{
                      background: '#48B650', color: 'white', border: 'none',
                      borderRadius: 6, padding: '0.32rem 0.7rem', fontSize: '0.75rem',
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                    }}>Yes, continue</button>
                  <button onClick={() => setMakeVsBuyConfirmDismissed(true)}
                    style={{
                      background: 'white', color: '#666', border: '1px solid #ccc',
                      borderRadius: 6, padding: '0.32rem 0.7rem', fontSize: '0.72rem',
                      cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                    }}>No, different decision</button>
                </div>
              </div>
            )}
            {/* people_decisions — relationship-type chip parsed from narrative */}
            {lens === 'people_decisions' && (() => {
              const relOptions = ['employee', 'co-founder', 'contractor', 'potential hire'];
              const current = relationshipType || 'employee';
              return (
                <div style={{
                  background: '#fdfaf0', border: '1px solid #f0e0a8',
                  borderLeft: '4px solid #d9a517',
                  borderRadius: 8, padding: '0.7rem 0.9rem',
                  marginBottom: '1rem',
                }}>
                  <div style={{ fontSize: '0.78rem', color: '#5d4307', fontWeight: 600, marginBottom: 6 }}>
                    Who is this about?
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {relOptions.map(opt => {
                      const isSel = current === opt;
                      return (
                        <button key={opt} type="button" onClick={() => setRelationshipType(opt)}
                          style={{
                            padding: '0.3rem 0.75rem', fontSize: '0.74rem', borderRadius: 16,
                            border: isSel ? '1.5px solid #48B650' : '1.5px solid #e0e0e0',
                            background: isSel ? '#e6f5ec' : 'white',
                            color: isSel ? '#185F2A' : '#666',
                            fontWeight: isSel ? 600 : 500,
                            cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                            textTransform: 'capitalize',
                          }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#7a6428', marginTop: 6, fontStyle: 'italic' }}>
                    Adjust if I've guessed wrong — this shapes the recommendation and any legal callouts.
                  </div>
                </div>
              );
            })()}
            <div style={{
              background: '#f0faf6', border: '1px solid #b8e4d4',
              borderRadius: '8px', padding: '0.875rem 1.1rem', marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, color: '#0F6E56', fontSize: '0.9rem', lineHeight: 1.6 }}>
                <strong style={{ display: 'block', marginBottom: '0.25rem' }}>
                  <span style={{ marginRight: 6 }}>{lensDisplay(lens).icon}</span>
                  Lens: {lensDisplay(lens).label}
                </strong>
                {lensDisplay(lens).blurb}
              </p>
            </div>
            {/* Universal pre-lens setup questions. Both optional. The leader
                can Skip either; skipped questions collapse to a compact
                placeholder with an "Answer this" toggle to restore them. */}
            <div style={{
              background: '#f8f9fc',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '0.85rem 1rem 0.4rem',
              marginBottom: '1.25rem',
            }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#888', marginBottom: '0.55rem' }}>
                Quick context — optional
              </div>
              {PRE_LENS_QUESTIONS.map((q) => {
                const isSkipped = !!preLensSkipped[q.field];
                const setAnswer = (val) => setAnswers({ ...answers, [q.field]: val });
                const skip = () => {
                  setPreLensSkipped({ ...preLensSkipped, [q.field]: true });
                  if (answers[q.field]) {
                    const next = { ...answers };
                    delete next[q.field];
                    setAnswers(next);
                  }
                };
                const unskip = () => {
                  const next = { ...preLensSkipped };
                  delete next[q.field];
                  setPreLensSkipped(next);
                };
                if (isSkipped) {
                  return (
                    <div key={q.field} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '0.4rem 0', borderBottom: '1px dashed #e5e7eb',
                      fontSize: '0.78rem', color: '#888',
                    }}>
                      <span style={{ fontStyle: 'italic' }}>Skipped: {q.question}</span>
                      <button type="button" onClick={unskip}
                        style={{
                          border: 'none', background: 'none', color: '#48B650',
                          fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                          fontSize: '0.75rem', width: 'auto', padding: '0 4px',
                        }}>
                        Answer this →
                      </button>
                    </div>
                  );
                }
                return (
                  <div key={q.field} className="question" style={{ marginBottom: '0.6rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <label style={{ margin: 0 }}>{q.question}</label>
                      <button type="button" onClick={skip}
                        title="Skip this question"
                        style={{
                          border: '1px solid #ddd', background: 'white', color: '#888',
                          borderRadius: 14, padding: '0.2rem 0.6rem', fontSize: '0.7rem',
                          fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                        }}>
                        Skip
                      </button>
                    </div>
                    {q.helper && (
                      <div style={{ fontSize: '0.74rem', color: '#666', marginTop: '0.2rem', marginBottom: '0.4rem', lineHeight: 1.45 }}>
                        {q.helper}
                      </div>
                    )}
                    {q.type === 'choice' ? (
                      <select value={answers[q.field] || ''}
                        onChange={e => setAnswer(e.target.value)}>
                        <option value=''>Select an option...</option>
                        {(q.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : (
                      <textarea
                        placeholder="Your answer..."
                        value={answers[q.field] || ''}
                        onChange={e => setAnswer(e.target.value)}
                        rows={q.rows || 2}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <p style={{ marginBottom: '1.25rem' }}>
              {questions.length} {questions.length === 1 ? 'question' : 'questions'} — answer as honestly as you can. The quality of the recommendation depends on it.
            </p>
            {questions.map((q) => {
              // Wrap setter so the source pill changes to "edited" once the
              // user touches a pre-filled value (rather than disappearing —
              // keeping the context is more useful than hiding it).
              const setAnswer = (val) => {
                setAnswers({ ...answers, [q.field]: val });
                const src = prefillSources[q.field];
                if (src && !src.endsWith('_edited')) {
                  setPrefillSources({ ...prefillSources, [q.field]: src + '_edited' });
                }
              };
              const source = prefillSources[q.field];
              const sourcePillEl = source ? (() => {
                const isCanvas = source.startsWith('canvas');
                const isNarrative = source.startsWith('narrative');
                const edited = source.endsWith('_edited');
                const label = isCanvas
                  ? (edited ? 'From your canvas — edited' : 'From your canvas — edit if not right')
                  : isNarrative
                  ? (edited ? "From what you said — edited" : "From what you said — edit if not right")
                  : null;
                if (!label) return null;
                return (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: '#e6f5ec',
                    border: '1px solid #b8e4d4',
                    color: '#0F6E56',
                    fontSize: '0.7rem', fontWeight: 500,
                    padding: '0.15rem 0.55rem', borderRadius: 12,
                    marginBottom: '0.4rem',
                  }}>
                    <span style={{ fontSize: '0.7rem' }}>✓</span>
                    <span>{label}</span>
                  </div>
                );
              })() : null;
              const helperEl = q.helper ? (
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '-0.15rem', marginBottom: '0.4rem', lineHeight: 1.5 }}>
                  {q.helper}
                </div>
              ) : null;
              let input;
              if (q.type === 'choice' && q.conditional_text === 'always') {
                input = <ChoiceWithConditionalText question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'choice' && q.has_other_input) {
                input = <ChoiceWithOtherInput question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'choice') {
                input = (
                  <select value={answers[q.field] || ''}
                    onChange={e => setAnswer(e.target.value)}>
                    <option value=''>Select an option...</option>
                    {(q.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                );
              } else if (q.type === 'multi_choice') {
                input = <MultiChoice question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'choice_with_input') {
                input = <ChoiceWithInput question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'split') {
                input = (
                  <SplitSelector
                    config={{
                      field: q.field,
                      opts: q.options,
                      allowAbsolute: q.allowAbsolute,
                      valueType: q.valueType,
                      absoluteTotalFrom: q.absoluteTotalFrom,
                      allow_custom: q.allow_custom,
                    }}
                    value={answers[q.field] || {}}
                    onChange={setAnswer}
                  />
                );
              } else if (q.type === 'rated_list') {
                input = <RatedList question={q} value={answers[q.field]} onChange={setAnswer} initialItems={q.initial_items} />;
              } else if (q.type === 'process_matrix') {
                input = <ProcessMatrix value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'ranked') {
                input = <Ranked question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'per_segment_multi') {
                input = <PerSegmentMulti question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else if (q.type === 'multi_with_text') {
                input = <MultiWithText question={q} value={answers[q.field]} onChange={setAnswer} />;
              } else {
                input = (
                  <textarea placeholder="Your answer..." value={answers[q.field] || ''}
                    onChange={e => setAnswer(e.target.value)}
                    rows={q.rows || 3} />
                );
              }
              return (
                <div key={q.field} className="question">
                  <label>{q.question}{q.optional && <span style={{ color: '#999', fontWeight: 400, fontSize: '0.78rem', marginLeft: 6 }}>(optional)</span>}</label>
                  {helperEl}
                  {sourcePillEl}
                  {input}
                </div>
              );
            })}

            <div className="user-options-section">
              <h3>Do you have options in mind?</h3>
              <p>Add any options you're already considering — GuruOne will evaluate these alongside any it suggests.</p>
              {userOptions.map((opt, i) => (
                <input key={i} type="text"
                  placeholder={`Option ${i + 1} — e.g. Hire an operations manager`}
                  value={opt}
                  onChange={e => {
                    const updated = [...userOptions];
                    updated[i] = e.target.value;
                    setUserOptions(updated);
                  }}
                />
              ))}
              {userOptions.length < 5 && (
                <button className="btn-add-option" onClick={() => setUserOptions([...userOptions, ''])}>
                  + Add another option
                </button>
              )}
            </div>

            {/* Criteria weighting */}
            <div className="criteria-section">
              <h3>What matters most in this decision?</h3>
              <p className="criteria-sub">Select 3-5 criteria that matter most. Click once — important. Click twice — critical.</p>
              <div className="criteria-pills">
                {getCriteria().map(c => {
                  const w = criteriaWeights[c.key] || 0;
                  return (
                    <button key={c.key} title={c.tip || ''}
                      className={`criteria-pill ${w === 1 ? 'matters' : w === 2 ? 'critical' : ''}`}
                      onClick={() => cyclePill(c.key)}>
                      {w > 0 && <span className="criteria-state-dot" />}
                      {c.label}{w === 2 ? ' ★' : ''}
                    </button>
                  );
                })}
                {customCriterion && (
                  <button className={`criteria-pill ${(criteriaWeights.custom||0)===1?'matters':(criteriaWeights.custom||0)===2?'critical':''}`}
                    onClick={() => cyclePill('custom')}>
                    {(criteriaWeights.custom||0) > 0 && <span className="criteria-state-dot" />}
                    {customCriterion}{(criteriaWeights.custom||0)===2?' ★':''}
                  </button>
                )}
                {!showCustomInput && (
                  <button className="criteria-add-btn" onClick={() => setShowCustomInput(true)}>+ Add your own</button>
                )}
              </div>
              {showCustomInput && (
                <div className="criteria-custom-input">
                  <input autoFocus type="text" placeholder="e.g. Your bandwidth"
                    value={customCriterion}
                    onChange={e => setCustomCriterion(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && customCriterion.trim()) { setShowCustomInput(false); setCriteriaWeights(w => ({...w, custom: 1})); }}}
                  />
                  <button onClick={() => { if (customCriterion.trim()) { setShowCustomInput(false); setCriteriaWeights(w => ({...w, custom: 1})); }}}>Add</button>
                  <button style={{ background: 'white', color: '#888', border: '1px solid #ddd', borderRadius: 20, padding: '0.35rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer', width: 'auto' }}
                    onClick={() => { setShowCustomInput(false); setCustomCriterion(''); }}>Cancel</button>
                </div>
              )}
              <div className="criteria-legend">
                <span><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ddd', display: 'inline-block', marginRight: 4 }} />Not relevant</span>
                <span><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1D9E75', display: 'inline-block', marginRight: 4 }} />Matters</span>
                <span><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#085041', display: 'inline-block', marginRight: 4 }} />Critical ★</span>
              </div>
            </div>

            <button onClick={handleAnswers} disabled={loading}>
              {buildingRecommendation ? 'Building your recommendation...' : loading ? 'Checking your answers...' : 'Get my recommendation →'}
            </button>
          </div>
        )}

        {/* Clarify step — LLM-driven probe between questions and recommendation.
            Falls back to questions step if state was lost (e.g. mid-clarify reload — see useEffect above).
            Hidden while buildingRecommendation is true so the LoadingScreen renders alone instead of
            sitting underneath the clarify form. */}
        {!buildingRecommendation && step === 'clarifying' && clarifyQuestions.length > 0 && (
          <div className="card">
            <ProgressTrack activeIndex={1} />
            <DecisionMasthead tagline="Structure your thinking. Make better decisions." />
            {renderToolbar()}
            <h2>A few quick checks before I commit</h2>
            <div style={{
              background: '#FFFBEB', border: '1px solid #FDE68A',
              borderRadius: 8, padding: '0.875rem 1.1rem', marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, color: '#7B4A0F', fontSize: '0.88rem', lineHeight: 1.6 }}>
                Before I lock in a recommendation, I want to close a couple of gaps so the answer is grounded in your real situation rather than my guesses. Answer what you can — or skip and I'll do my best with what's already there.
              </p>
            </div>

            {clarifyQuestions.map((q) => (
              <div key={q.id} className="question">
                <label>{q.question}</label>
                {q.type === 'choice' && Array.isArray(q.options) && q.options.length > 0 ? (
                  <select
                    value={clarifyAnswers[q.id] || ''}
                    onChange={e => setClarifyAnswers({ ...clarifyAnswers, [q.id]: e.target.value })}
                  >
                    <option value=''>Select an option...</option>
                    {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <textarea
                    rows={2}
                    value={clarifyAnswers[q.id] || ''}
                    onChange={e => setClarifyAnswers({ ...clarifyAnswers, [q.id]: e.target.value })}
                    placeholder="Your answer..."
                  />
                )}
              </div>
            ))}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
              <button
                onClick={() => submitClarify({ skip: false })}
                disabled={loading || buildingRecommendation}
              >
                {buildingRecommendation ? 'Building your recommendation...' : loading ? 'Checking your answers...' : 'Submit answers →'}
              </button>
              <button
                onClick={() => submitClarify({ skip: true })}
                disabled={loading || buildingRecommendation}
                style={{
                  background: 'none', color: '#1D9E75', border: '1px solid #1D9E75',
                }}
              >
                Just give me your best shot
              </button>
            </div>
          </div>
        )}

        {/* Canvas back-sync toast (fixed-position, auto-dismiss).
            Surfaces when Decision Guru has added new fields to the user's
            canvases. Never shown when no fields were synced. */}
        {syncToast?.visible && syncToast.synced?.length > 0 && (() => {
          const synced = syncToast.synced;
          const canvasNames = Array.from(new Set(synced.map(s => s.canvas)));
          const fieldsByCanvas = canvasNames.map(c => {
            const fields = synced.filter(s => s.canvas === c).map(s => s.label).filter(Boolean);
            return { canvas: c, fields };
          });
          return (
            <div
              role="status"
              aria-live="polite"
              style={{
                position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
                background: 'white',
                border: '1px solid #b8e4d4',
                borderLeft: '4px solid #48B650',
                borderRadius: 10,
                padding: '0.85rem 1rem',
                boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                maxWidth: 340,
                fontSize: '0.82rem', color: '#1a1a2e',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ color: '#0F6E56', fontSize: '1rem', marginTop: 1, lineHeight: 1 }}>✓</div>
                <div style={{ flex: 1, lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>Canvases updated</div>
                  <div style={{ fontSize: '0.76rem', color: '#444', marginBottom: 5 }}>
                    We've added {synced.length} {synced.length === 1 ? 'field' : 'fields'} from your answers to your {canvasNames.join(' and ')} canvas{canvasNames.length === 1 ? '' : 'es'}. Nothing existing was overwritten.
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.72rem', color: '#555' }}>
                    {fieldsByCanvas.flatMap(({ canvas: c, fields }) =>
                      fields.map((f, i) => (
                        <li key={`${c}-${i}`}><strong>{c}:</strong> {f}</li>
                      ))
                    )}
                  </ul>
                </div>
                <button
                  onClick={() => setSyncToast(prev => prev ? { ...prev, visible: false } : null)}
                  aria-label="Dismiss"
                  style={{
                    border: 'none', background: 'none', cursor: 'pointer',
                    color: '#888', padding: '0 2px', lineHeight: 1,
                    fontSize: '0.95rem', width: 'auto',
                  }}
                >✕</button>
              </div>
            </div>
          );
        })()}

        {/* Context-applied toast — fires after "Add context" pulls fields
            into the canvas snapshot. Lives in the same fixed-position
            slot as the canvas back-sync toast so the leader always sees
            confirmation in the same corner. */}
        {contextAppliedToast?.visible && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
              background: 'white',
              border: '1px solid #b8e4d4',
              borderLeft: '4px solid #48B650',
              borderRadius: 10,
              padding: '0.85rem 1rem',
              boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
              maxWidth: 340,
              fontSize: '0.82rem', color: '#1a1a2e',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ color: '#0F6E56', fontSize: '1rem', marginTop: 1, lineHeight: 1 }}>✓</div>
              <div style={{ flex: 1, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, marginBottom: 3 }}>
                  {contextAppliedToast.applied > 0
                    ? `${contextAppliedToast.applied} field${contextAppliedToast.applied === 1 ? '' : 's'} filled from ${contextAppliedToast.source}`
                    : `Couldn't find anything useful in ${contextAppliedToast.source}`}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#444' }}>
                  {contextAppliedToast.applied > 0
                    ? 'Scroll up to see the updated canvas snapshot. Nothing existing was overwritten.'
                    : 'Try a different page or upload a document with more detail.'}
                </div>
                {contextAppliedToast.directionAdded > 0 && (
                  <div style={{ fontSize: '0.76rem', color: '#0F6E56', marginTop: 4, fontWeight: 600 }}>
                    Also added {contextAppliedToast.directionAdded} field{contextAppliedToast.directionAdded === 1 ? '' : 's'} to your Direction canvas.
                  </div>
                )}
              </div>
              <button
                onClick={() => setContextAppliedToast(null)}
                aria-label="Dismiss"
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  color: '#888', padding: '0 2px', lineHeight: 1,
                  fontSize: '0.95rem', width: 'auto',
                }}
              >✕</button>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="result">
            <ProgressTrack activeIndex={3} />
            <DecisionMasthead tagline="Structure your thinking. Make better decisions." />
            {renderToolbar()}

            {/* people_decisions — non-dismissible employment law callout.
                Spec Lens 11: always visible in the recommendation. */}
            {lens === 'people_decisions' && (
              <div role="note" style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderLeft: '4px solid #dc2626', borderRadius: 8,
                padding: '0.85rem 1rem', marginBottom: '1.25rem',
                display: 'flex', alignItems: 'flex-start', gap: '0.7rem',
              }}>
                <div style={{ fontSize: '1.05rem', marginTop: 1 }}>⚠️</div>
                <div style={{ flex: 1, fontSize: '0.82rem', lineHeight: 1.55, color: '#7f1d1d' }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>Employment law caveat — please read</div>
                  Any recommendation involving ending employment, changing terms, or formal performance management must be implemented in line with UK employment law. Treat the suggestions here as a starting point, not a substitute for HR or employment law guidance. GuruOne does not provide legal advice.
                  {answers?.hr_support && typeof answers.hr_support === 'string' && answers.hr_support.startsWith('No') && (
                    <div style={{ marginTop: 6, fontWeight: 600 }}>
                      You've told me you don't have HR or legal support — strongly consider engaging an employment law specialist or HR consultant before acting on this.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Feedback nudge — auto-dismisses after 12s, scroll-to on click */}
            {!feedbackNudgeDismissed && (() => {
              setTimeout(() => setFeedbackNudgeDismissed(true), 12000);
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.75rem', padding: '0.65rem 1rem',
                  background: '#f0fdf8', border: '1px solid #a7f3d0', borderRadius: 8,
                  marginBottom: '1.25rem', fontSize: '0.78rem', color: '#065f46',
                }}>
                  <span>Once you've reviewed your result, we'd love to hear what you think.</span>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      onClick={() => feedbackRef.current?.scrollIntoView({ behavior: 'smooth' })}
                      style={{
                        background: '#1D9E75', color: 'white', border: 'none',
                        borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.75rem',
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                      }}
                    >
                      Jump to feedback ↓
                    </button>
                    <button
                      onClick={() => setFeedbackNudgeDismissed(true)}
                      style={{
                        background: 'none', border: 'none', color: '#6b7280',
                        fontSize: '0.85rem', cursor: 'pointer', padding: '0 0.2rem',
                        lineHeight: 1, width: 'auto',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })()}

            <div className="result-header">
              <h2>{result.decision_question_short || result.decision_question}</h2>
              {result.decision_question_short && (
                <p className="decision-long">{result.decision_question}</p>
              )}
            </div>

            <div className="analysis-sections">
              <ExpandableSection title="Context" summary={result.context_summary}
                content={<p>{result.context}</p>} />
              <ExpandableSection title="Difficulty" summary={result.difficulty_summary}
                content={<p>{result.difficulty}</p>} />
              <ExpandableSection title="Deliberation" summary={result.deliberation_summary}
                content={
                  <div>
                    {result.deliberation.map((d, i) => (
                      <div key={i} className="deliberation-item">
                        <strong>{d.consideration}</strong>
                        <p>{d.detail}</p>
                      </div>
                    ))}
                  </div>
                } />
            </div>

            <div className="options-header">
              <h3>⚖ Your options</h3>
            </div>

            <div className="options" style={{
              gridTemplateColumns: `repeat(${Math.min(result.options.length, 5)}, minmax(0, 1fr))`
            }}>
              {result.options.map((opt, i) => {
                const optLabel = opt.label || String.fromCharCode(65 + i);
                const isRec = isRecommended(opt.title, optLabel);
                const isDiscarded = !!discardedOptions[optLabel];
                const isDiscarding = discardingOption === optLabel;

                return (
                  <div key={i} className={`option ${isRec ? 'recommended' : ''} ${isDiscarded ? 'discarded' : ''}`}>
                    <div className="option-badge-area" style={{ display: 'flex', alignItems: 'center' }}>
                      {isRec && <div className="rec-badge">Recommended</div>}
                      {!isDiscarded && (
                        <button className="discard-btn"
                          onClick={() => { setDiscardingOption(isDiscarding ? null : optLabel); setDiscardReason(''); }}>
                          {isDiscarding ? 'Cancel' : '× Discard'}
                        </button>
                      )}
                    </div>

                    <div className="option-label">Option {optLabel}</div>
                    <h3>{opt.title}</h3>
                    <p className="option-desc">{opt.description}</p>

                    {/* Inline expand toggle for pros/cons */}
                    {!isDiscarding && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.35rem' }}>
                        <button onClick={() => setAllExpanded(e => !e)}
                          style={{ fontSize: '0.68rem', color: '#888', background: 'none', border: '1px solid #e0e0e0', borderRadius: 20, padding: '0.2rem 0.55rem', cursor: 'pointer', width: 'auto' }}>
                          {allExpanded ? '▲ Collapse' : '▼ Pros & cons'}
                        </button>
                      </div>
                    )}

                    <div className={`pros-section ${allExpanded ? '' : 'section-collapsed'}`}>
                      {isDiscarding ? (
                        <div className="discard-prompt">
                          <p>Why are you discarding this?</p>
                          {['Not realistic for us right now', 'Already tried or ruled out', 'Other reason'].map(r => (
                            <button key={r} className={`discard-reason-btn ${discardReason === r ? 'selected' : ''}`}
                              onClick={() => setDiscardReason(r)}>{r}</button>
                          ))}
                          {isRec && (
                            <div style={{ background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 6, padding: '0.6rem 0.75rem', marginTop: '0.5rem' }}>
                              <p style={{ fontSize: '0.75rem', color: '#5D4037', margin: '0 0 0.5rem', lineHeight: 1.5, fontWeight: 500 }}>
                                This is the recommended option. Why isn't it right for you right now?
                              </p>
                              <textarea
                                rows={2}
                                placeholder="e.g. We don't have the budget, already tried this..."
                                value={discardReason === 'Not realistic for us right now' || discardReason === 'Already tried or ruled out' || discardReason === 'Other reason' ? '' : discardReason}
                                onChange={e => setDiscardReason(e.target.value)}
                                style={{ width: '100%', fontSize: '0.75rem', padding: '0.35rem 0.5rem', borderRadius: 5, border: '1px solid #FFD54F', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: 'white' }}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="pros-cons-label pros-label">Pros</div>
                          <div className="pros">
                            {opt.pros.map((p, j) => (
                              <div key={j} className="pro">
                                <span className="pro-icon">✓</span>
                                <span>
                                  <strong>{typeof p === 'object' ? p.headline : p}</strong>
                                  {typeof p === 'object' && p.detail && <span className="pro-detail"> — {p.detail}</span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <div className={`cons-section ${allExpanded ? '' : 'section-collapsed'}`}>
                      {isDiscarding ? (
                        <div className="discard-actions">
                          <button className="discard-confirm" disabled={!discardReason}
                            onClick={() => {
                              setDiscardedOptions(prev => ({ ...prev, [optLabel]: { reason: discardReason, title: opt.title } }));
                              setDiscardingOption(null);
                              setDiscardReason('');
                              if (howOption?.label === optLabel) setHowOption(null);
                            }}>
                            Confirm discard
                          </button>
                          <button className="discard-cancel"
                            onClick={() => { setDiscardingOption(null); setDiscardReason(''); }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="pros-cons-label cons-label">Cons</div>
                          <div className="cons">
                            {opt.cons.map((c, j) => (
                              <div key={j} className="con">
                                <span className="con-icon">✗</span>
                                <span>
                                  <strong>{typeof c === 'object' ? c.headline : c}</strong>
                                  {typeof c === 'object' && c.detail && <span className="con-detail"> — {c.detail}</span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="option-footer">
                      {(() => {
                        const ca = opt.criteria_alignment;
                        const hasCriteria = (Array.isArray(ca) && ca.length > 0) ||
                          (typeof ca === 'string' && ca.length > 10 && !ca.includes('No criteria specified'));
                        if (!hasCriteria) return null;
                        const isOpen = !!criteriaExpanded[optLabel];
                        return (
                          <div className="criteria-fit-block">
                            <div className="criteria-fit-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>How this fits your criteria</span>
                              <button onClick={() => setCriteriaExpanded(prev => ({ ...prev, [optLabel]: !prev[optLabel] }))}
                                style={{ fontSize: '0.65rem', color: '#888', background: 'none', border: '1px solid #e0e0e0', borderRadius: 20, padding: '0.15rem 0.5rem', cursor: 'pointer', width: 'auto' }}>
                                {isOpen ? '▲ Hide' : '▼ Show'}
                              </button>
                            </div>
                            {isOpen && Array.isArray(ca) && ca.map((item, ci) => (
                              <div key={ci} className="criteria-fit-item">
                                <div className="criteria-fit-row">
                                  <span className="criteria-fit-name">{item.criterion}</span>
                                  <Tooltip
                                    content={
                                      item.strength === 'strong' ? 'Strong alignment - this option fits well with this criterion' :
                                      item.strength === 'moderate' ? 'Moderate alignment - partially meets this criterion' :
                                      item.strength === 'weak' ? 'Weak alignment - limited fit with this criterion' :
                                      'Alignment assessment unavailable'
                                    }
                                    show={hoveredTooltip === `criteria-${ci}-${item.criterion}`}
                                  >
                                    <span
                                      className={`criteria-strength ${(item.strength || '').toLowerCase()}`}
                                      onMouseEnter={() => setHoveredTooltip(`criteria-${ci}-${item.criterion}`)}
                                      onMouseLeave={() => setHoveredTooltip('')}
                                      style={{ cursor: 'help' }}
                                    >
                                      {item.strength || '—'}
                                    </span>
                                  </Tooltip>
                                </div>
                                {item.rationale?.length > 0 && (
                                  <ul className="criteria-fit-bullets">
                                    {item.rationale.map((r, ri) => <li key={ri}>{r}</li>)}
                                  </ul>
                                )}
                              </div>
                            ))}
                            {isOpen && typeof ca === 'string' && ca.length > 10 && !ca.includes('No criteria specified') && (
                              <p style={{ fontSize: '0.78rem', color: '#555', lineHeight: 1.5, margin: 0 }}>{ca}</p>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {isDiscarded ? (
                      altInputOption === optLabel ? (
                        <div style={{ marginTop: 'auto' }}>
                          <p style={{ fontSize: '0.75rem', fontWeight: 500, color: '#555', margin: '0 0 0.4rem' }}>
                            What should the alternative focus on?
                          </p>
                          <textarea
                            rows={2}
                            placeholder="e.g. Something lower risk, without upfront investment..."
                            value={altInputText}
                            onChange={e => { setAltInputText(e.target.value); setAltError(null); }}
                            style={{ width: '100%', fontSize: '0.75rem', padding: '0.4rem 0.5rem', borderRadius: 5, border: '1px solid #ddd', resize: 'none', fontFamily: 'inherit', marginBottom: '0.4rem', boxSizing: 'border-box' }}
                          />
                          {altError && (
                            <p style={{ fontSize: '0.72rem', color: '#A32D2D', margin: '0 0 0.4rem', lineHeight: 1.4 }}>
                              {altError}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button
                              style={{ flex: 1, padding: '0.4rem', fontSize: '0.75rem', background: generatingAlt === optLabel ? '#ccc' : '#1D9E75', color: 'white', border: 'none', borderRadius: 5, cursor: generatingAlt === optLabel ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
                              disabled={generatingAlt === optLabel}
                              onClick={() => handleGenerateAlternative(optLabel)}>
                              {generatingAlt === optLabel ? 'Generating...' : 'Generate →'}
                            </button>
                            <button
                              style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', background: 'white', color: '#888', border: '1px solid #ddd', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}
                              onClick={() => { setAltInputOption(null); setAltInputText(''); setAltError(null); }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button className="generate-alt-btn" onClick={() => { setAltInputOption(optLabel); setAltError(null); }}>
                          + Generate an alternative
                        </button>
                      )
                    ) : !isDiscarding && (
                      <button
                        className={`how-btn ${howOption?.label === optLabel ? 'active' : ''}`}
                        onClick={() => setHowOption(prev => prev?.label === optLabel ? null : opt)}>
                        {howOption?.label === optLabel ? '▲ Close' : 'How would this work? →'}
                      </button>
                    )}

                  </div>
                );
              })}
            </div>

            {howOption && (
              <HowPanel
                key={howOption.label}
                option={howOption}
                canvas={canvasWithSplits()}
                lens={lens}
                cachedContent={howCache[howOption.label] || null}
                onCacheContent={content => setHowCache(prev => ({ ...prev, [howOption.label]: content }))}
                onClose={() => setHowOption(null)}
              />
            )}

            <div className="recommendation-box">
              <div className="rec-header">★ Recommendation</div>
              <p className="rec-text">{result.recommendation}</p>

              <button className="why-btn" onClick={handleWhyToggle}>
                {whyOpen ? 'Hide reasoning ▲' : 'Why this recommendation? →'}
              </button>
              {whyOpen && (
                <WhyPanel
                  loading={whyData.loading}
                  error={whyData.error}
                  content={whyData.content}
                  onClose={() => setWhyOpen(false)}
                />
              )}

              {result.confidence && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', margin: '1rem 0', background: 'rgba(255,255,255,0.5)', borderRadius: 8, padding: '0.6rem 0.85rem' }}>
                  <Tooltip 
                    content="Confidence level in this recommendation based on data quality and business context"
                    show={hoveredTooltip === 'confidence-pill'}
                  >
                    <span 
                      style={{ 
                        fontSize: '0.65rem', 
                        fontWeight: 700, 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.06em', 
                        padding: '2px 8px', 
                        borderRadius: 20, 
                        flexShrink: 0, 
                        marginTop: 1, 
                        background: result.confidence.level === 'high' ? '#1D9E75' : result.confidence.level === 'medium' ? '#BA7517' : '#A32D2D', 
                        color: 'white',
                        cursor: 'help',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={() => setHoveredTooltip('confidence-pill')}
                      onMouseLeave={() => setHoveredTooltip('')}
                    >
                      {result.confidence.level}
                    </span>
                  </Tooltip>
                  <div>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#0F6E56', marginBottom: '2px' }}>
                      Why this confidence level:
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#0F6E56', lineHeight: 1.5 }}>{result.confidence.reason}</span>
                  </div>
                </div>
              )}

            </div>

            {revisedResult && <RevisedResult text={revisedResult} />}

            <div className="result-actions">
              <button className="btn-secondary" onClick={() => {
                setStep('challenge'); setResult(null); setAnswers({});
                setUserOptions(['', '']); setRevisedResult(null);
                setDiscardedOptions({}); setDiscardingOption(null);
                setAltInputOption(null); setAltInputText(''); setAltError(null);
                setWhyOpen(false); setWhyData({ loading: false, content: null, error: null });
                setCriteriaWeights({}); setCustomCriterion(''); setShowCustomInput(false);
                setHowCache({}); setAllExpanded(false); setCriteriaExpanded({});
                window.scrollTo(0, 0);
              }}>Ask another question</button>
              <button className="btn-secondary" onClick={() => {
                setChatOpen(true);
                setChatMessages([{ role: 'assistant', content: "What assumption have I got wrong, or what context am I missing? Tell me and I'll revise the recommendation." }]);
              }}>Improve context →</button>
              <button className="btn-consultant" onClick={() => setConsultantOpen(true)}>
                Book a 15min call →
              </button>
              {isConfirmed ? (
                <button
                  className="btn-secondary"
                  disabled
                  style={{
                    background: '#E1F5EE',
                    color: '#0F6E56',
                    border: '1px solid #1D9E75',
                    cursor: 'default',
                    opacity: 1,
                  }}
                  title="This decision has been committed to memory">
                  ✓ Decision confirmed
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={() => { setConfirmError(''); setConfirmModalOpen(true); }}
                  disabled={!decisionId}
                  style={{ background: '#0F6E56' }}
                  title={decisionId ? 'Commit this decision to memory' : 'Decision not yet saved'}>
                  ✓ Confirm decision
                </button>
              )}
              <button className="btn-primary" onClick={() => { track('decision_actioned'); navigate('/contextual-chat', {
                state: {
                  recommendation: result.recommendation,
                  canvas,
                  challenge,
                  lens: result.lens || lens,
                  result: result
                }
              }); }}>Help me action this →</button>
            </div>

            {chatOpen && (
              <div className="chat-panel">
                <div className="chat-header">
                  <span>{chatMessages.length > 0 && chatMessages[0]?.content.includes('assumption') ? 'Improve context' : 'Continue the conversation'}</span>
                  <button className="chat-close" onClick={() => setChatOpen(false)}>✕</button>
                </div>
                <div className="chat-messages" ref={chatEndRef}>
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`chat-msg ${msg.role === 'user' ? 'user' : 'assistant'}`}>
                      <p>{msg.content}</p>
                    </div>
                  ))}
                  {chatLoading && <div className="chat-msg assistant"><p>Thinking...</p></div>}
                </div>
                <div className="chat-input-row">
                  <textarea
                    placeholder="e.g. Actually, I've already committed to the manufacturing premises..."
                    value={chatInput} onChange={e => setChatInput(e.target.value)} rows={2} />
                  <button className="btn-primary" onClick={handleChat} disabled={chatLoading || !chatInput}>Send</button>
                </div>
              </div>
            )}

            <div ref={feedbackRef}>
              <FeedbackPanel
                guru="Decision"
                promptSent={challenge}
                responseReceived={result.recommendation}
                contextData={{
                  lens: result.lens || lens,
                  businessContext: canvas,
                  criteriaWeights: criteriaWeights,
                }}
                onComplete={() => setFeedbackNudgeDismissed(true)}
              />
            </div>
          </div>
        )}

        {error && <div className="error">{friendlyError(error)}</div>}
      </main>

      {helpOpen && <HowToUseDecisionPanel onClose={() => setHelpOpen(false)} />}

      {/* Add context slide-in — wraps UploadContextPanel; closes after upload completes */}
      {addContextOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
          <div style={{ width: '100%', maxWidth: 380, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Context</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>Add more context</div>
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.35rem', lineHeight: 1.5 }}>
                  Upload your business plan, financial documents, marketing materials or anything else relevant to this decision.
                </div>
              </div>
              <button onClick={() => setAddContextOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
              <UploadContextPanel
                collapsedByDefault={false}
                onTextExtracted={(text) => { setUploadedDocText(text); }}
                onSectionsExtracted={async (sections, meta) => {
                  const applied = applyUploadedSections(sections);
                  // Close the slide-in immediately so it feels like the
                  // upload "did something" rather than burying success
                  // under a tiny green status line.
                  setAddContextOpen(false);
                  setContextAppliedToast({
                    visible: true,
                    applied,
                    source: meta?.sourceLabel || 'your upload',
                    directionAdded: null, // will be filled after spillover resolves
                  });
                  track('decision_add_context_applied', { applied, source: meta?.sourceLabel || 'upload' });
                  // Spillover: same upload also enriches the Direction
                  // canvas server-side (additive — never overwrites). Runs
                  // in the background so the user isn't blocked by it.
                  const directionAdded = await mergeUploadedIntoDirectionCanvas(sections);
                  if (directionAdded > 0) {
                    setContextAppliedToast(prev => prev && prev.visible
                      ? { ...prev, directionAdded }
                      : prev);
                    track('decision_add_context_direction_spillover', { directionAdded });
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Consultant modal — shared component, also mounted in Header
          so it surfaces from the header pill. Local copy here drives
          the in-result "Book a 15min call →" button on the
          recommendation screen. */}
      <ConsultantModal open={consultantOpen} onClose={() => setConsultantOpen(false)} />

      {/* Confirm Decision modal — commits the decision to memory. */}
      {confirmModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem',
          }}
          onClick={() => !confirmLoading && setConfirmModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: '1.75rem 1.85rem',
              maxWidth: 440,
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1.15rem' }}>✓</span>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                Confirm decision?
              </h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#444', lineHeight: 1.65, margin: '0 0 1.25rem' }}>
              Are you sure? This decision will be committed to your business memory and will inform how GuruOne reasons about future decisions.
            </p>
            {confirmError && (
              <div style={{
                background: '#FEE2E2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                padding: '0.6rem 0.85rem',
                fontSize: '0.78rem',
                color: '#991B1B',
                marginBottom: '1rem',
              }}>
                {confirmError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setConfirmModalOpen(false)}
                disabled={confirmLoading}
                style={{
                  padding: '0.55rem 1.1rem',
                  borderRadius: 8,
                  background: 'white',
                  color: '#555',
                  border: '1px solid #e0e0e0',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: confirmLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  width: 'auto',
                  opacity: confirmLoading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDecision}
                disabled={confirmLoading}
                style={{
                  padding: '0.55rem 1.2rem',
                  borderRadius: 8,
                  background: '#0F6E56',
                  color: 'white',
                  border: 'none',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: confirmLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  width: 'auto',
                  opacity: confirmLoading ? 0.7 : 1,
                }}
              >
                {confirmLoading ? 'Confirming…' : 'Yes, confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
